import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { HauffeLoader } from "../components/BrandLogo";
import { FieldHelp } from "../components/FieldHelp";
import { Layout } from "../components/Layout";
import { Panel, Stat, StatusBadge } from "../components/Panel";
import {
  approveRuntimeCustomerValueClusters,
  confirmRuntimeCustomerValueCalculation,
  confirmRuntimeCustomerValueConfiguration,
  createRuntimeCustomerValueCategory,
  createRuntimeCustomerValueCriterion,
  deleteRuntimeCustomerValueCategory,
  deleteRuntimeCustomerValueCluster,
  deleteRuntimeCustomerValueCriterion,
  downloadRuntimeCustomerValueExport,
  duplicateRuntimeCustomerValueCriterion,
  fetchRuntimeWorkspace,
  fetchRuntimeCustomerValueCompanyEvaluationResult,
  fetchRuntimeCustomerValueCompanyEvaluationResults,
  finalizeRuntimeCustomerValueCompanyEvaluation,
  getRuntimeCustomerValueSegmentDefinition,
  getRuntimeCustomerValueConfiguration,
  requestRuntimeCustomerValueCriterionDescriptionSuggestion,
  setRuntimeCustomerValueCriterionActive,
  updateRuntimeCustomerValueCategory,
  updateRuntimeCustomerValueConfiguration,
  previewRuntimeCustomerValueConfiguration,
  generateRuntimeCustomerValueClusters,
  acknowledgeRuntimeMissingPerspectives,
  createRuntimeCustomerValueSegmentDraft,
  updateRuntimeCustomerValueSegmentDraft,
  confirmRuntimeCustomerValueSegmentDraft,
  reopenRuntimeValueRound,
  saveRuntimeCustomerValueWeights,
  sendRuntimeDepartmentWeightingReminder,
  updateRuntimeCustomerValueCluster,
  type RuntimeCustomerValueCluster,
  type RuntimeCustomerValueConfigurationPreview,
  type RuntimeCustomerValueConfigurationResponse,
  type RuntimeCustomerValueCriterionConfiguration,
  type RuntimeCustomerValueFinalResult,
  type RuntimeCustomerValueSegmentDefinition,
  type RuntimeCustomerValueSegmentDefinitionResponse,
  type RuntimeWorkspaceOverview
} from "../lib/api/runtimeWorkspaceClient";
import {
  canPrepareClusters,
  canWeightClusters,
  analyzePerspectiveCoverage,
  canInviteExecutivePerspective,
  customerValueProcessLabel,
  dedupeExecutivePerspectives,
  getExecutivePerspective,
  getPerspectiveDisplayName,
  getPerspectiveTypeLabel,
  MIN_SEGMENT_COUNT,
  MAX_SEGMENT_COUNT,
  validateWeightDistribution
} from "../lib/valueWorkflow";
import type { CustomerValueCriterionKind, CustomerValueScoringType } from "../lib/valueWorkflow";
import { useWorkspaceLanguage, withLanguagePrefix } from "../lib/i18n";
import { readBilingualText } from "../lib/i18n/bilingualContent";
import {
  hasResolvedClusterLanguage,
  hasResolvedCalculationLanguage,
  resolveCalculationForLanguage,
  resolveRuntimeClusterForLanguage
} from "../lib/i18n/languageResolvers";
import type { CriterionType, PerspectiveType } from "../types";
import type {
  CustomerValueCriterionCategory,
  CustomerValueCriterionScoringBand,
  CustomerValueCriterionScoringError,
  CustomerValueModelType
} from "../lib/customerValue/criterionScoring";
import {
  validateCriterionScoringBandSet,
  validateDraftScoringBandsForPersistence,
  validateActiveCriteriaWeightTotal
} from "../lib/customerValue/criterionScoring";
import type { CustomerValueCriterionDescriptionSuggestion } from "../lib/customerValue/criterionFinalization";
import { normalizeCustomerValueCriterionName } from "../lib/customerValue/criterionFinalization";
import {
  CUSTOMER_VALUE_CRM_TRANSPARENCY_FIELDS,
  CUSTOMER_VALUE_HELP_TEXTS,
  CRITERION_TOOLTIPS,
  CRITERION_TYPE_LABELS,
  type CustomerValueHelpKey,
  SCORING_DIRECTION_LABELS,
  SCORING_STATUS_LABELS,
  SOURCE_STRATEGY_LABELS,
  UNIT_TYPE_LABELS,
  VALUE_TYPE_LABELS,
  canSelectCriterionTypeForNewCriterion,
  createTenPointBandSuggestion,
  criterionLabel,
  shouldShowQuantitativeFields
} from "../lib/customerValue/criterionManagement";
import {
  hasCompleteConfirmedCriterionScoring,
  hasConfiguredCustomerValueModelType,
  type CustomerValueModelReadinessStatus
} from "../lib/customerValue/modelReadiness";
import {
  buildCustomerValueScoringVersionHistory,
  buildCustomerValueScoringVersionSummaries
} from "../lib/customerValue/scoringHistory";
import { deriveCustomerValueSegmentOrder } from "../lib/customerValue/segmentDefinitions";

type ReplacementFlowStep = "explain" | "criteria" | "weights" | "review";

interface CriterionAddDraft {
  name: string;
  description: string;
  criterionType: Exclude<RuntimeCustomerValueCriterionConfiguration["criterionType"], "legacy">;
  valueType: RuntimeCustomerValueCriterionConfiguration["valueType"];
  unitType: RuntimeCustomerValueCriterionConfiguration["unitType"];
  scoringDirection: RuntimeCustomerValueCriterionConfiguration["scoringDirection"];
  weight: number;
  required: boolean;
  suggestion?: CustomerValueCriterionDescriptionSuggestion;
}

interface LegacyReplacementDraft {
  sourceCriterionId: string;
  step: ReplacementFlowStep;
  splitConfirmed: boolean;
  criteria: Array<{
    localId: string;
    name: string;
    description: string;
    valueType: "currency" | "percentage" | "decimal" | "integer" | "count";
    unitType: "EUR" | "percentage" | "count" | "none";
    scoringDirection: "higher_is_better" | "lower_is_better";
    weight: number;
  }>;
}

export function isCustomerValueScoringDraftSaveAvailable(
  criteria: readonly Pick<RuntimeCustomerValueCriterionConfiguration, "id" | "weight" | "active">[],
  isWorking: boolean
): boolean {
  return !isWorking && validateActiveCriteriaWeightTotal(criteria).ok;
}

const CUSTOMER_VALUE_UX_COPY = {
  en: {
    processTitle: "Customer Value workflow",
    processBody: "Follow the current step. The productive model stays unchanged until you deliberately activate saved changes.",
    compactSummaryTitle: "Customer Value model is active",
    compactSummaryReady: "Fully configured",
    compactSummaryCriteria: "Criteria",
    compactSummaryWeighting: "weighting",
    compactSummarySegments: "Segments",
    compactSummaryScoring: "Scoring logic",
    processReadyTitle: "Customer Value model is active and fully configured",
    processReadyBody: "HAUFFE OS uses the saved criteria, weighting, scoring logic and segments for the Customer Value assessment.",
    processDraftBody: "Saved draft changes are not active yet. Review them and activate the updated model deliberately.",
    processConfirmedBadge: "Active",
    processDraftBadge: "Draft changes",
    processNeedsAttentionBadge: "Action required",
    stepDone: "",
    stepCurrent: "Current",
    stepOpen: "Open",
    stepCriteria: "Criteria",
    stepWeighting: "Weighting",
    stepScoring: "Scoring logic",
    stepSegments: "Segments",
    stepFinish: "Active",
    editCriteria: "Edit criteria",
    primaryActionTitle: "Next step",
    primaryActionDone: "No further setup action is required right now.",
    editModeTitle: "Edit mode",
    editModeBody: "The active model stays unchanged until you deliberately activate saved changes.",
    editModel: "Edit model",
    stopEditing: "Leave edit mode",
    testModelCollapsed: "Test model",
    hideTestModel: "Hide model test",
    criteriaSectionTitle: "Active criteria",
    criteriaSectionBody: "These criteria currently shape the productive Customer Value model. Open a card to adjust wording, weighting or scoring logic.",
    criteriaCompactBody: "Open a criterion only when you want to adjust its wording, weighting or scoring logic.",
    historicalCriteriaTitle: (count: number) => count === 1 ? "Historical criterion (1)" : `Historical criteria (${count})`,
    historicalCriteriaBody: "These criteria are no longer active in the current model and do not block readiness, weighting or scoring.",
    showMore: "Show more",
    showLess: "Show less",
    activeStatusReady: "Configured",
    activeStatusDraft: "Saved changes not active yet",
    activeStatusTodo: "Scoring logic still missing",
    activeStatusLegacy: "Configured",
    activeStatusHistorical: "Historical",
    criterionInfluenceHigher: "Higher values increase Customer Value",
    criterionInfluenceLower: "Lower values increase Customer Value",
    criterionInfluenceManual: "Business assessment with explicit score levels",
    criterionInfluenceUnknown: "Scoring logic is configured in this criterion",
    criterionEdit: "Edit",
    criterionBusinessSummary: "Business summary",
    criterionValidationTitle: "What still needs attention",
    criterionValidationGap: "There is a gap between at least two score levels.",
    criterionValidationOverlap: "At least two score levels overlap.",
    criterionValidationAmbiguousBoundary: "A boundary is ambiguous because two adjacent ranges include the same value.",
    criterionValidationBounds: "At least one score level has invalid start or end values.",
    criterionValidationPoints: "Each active score level needs a unique point value from 1 to 10.",
    criterionValidationEmpty: "Add a complete scoring logic before activating this criterion.",
    criterionValidationOption: "Each selectable option needs its own unique score level.",
    criterionValidationBoolean: "Both yes and no need an assigned score.",
    criterionValidationFallback: "Complete and validate the scoring logic in this criterion.",
    criterionFixAction: "Fix criterion",
    saveChanges: "Save changes",
    savedChangesInactive: "Saved changes are not active yet",
    activateModel: "Activate model",
    reviewModel: "Review model",
    testModel: "Test model",
    noUnsavedChanges: "No unsaved scoring changes",
    confirmAlreadyActive: "The current model version is already active.",
    segmentsCompactTitle: "Segment overview",
    segmentsCompactBody: "These score ranges currently structure the productive Customer Value interpretation.",
    editSegments: "Edit segments",
    optionalNoticeTitle: "Additional hints",
    optionalPerspectives: (count: number) => count === 1 ? "1 optional perspective is still missing." : `${count} optional perspectives are still missing.`,
    optionalTranslations: "English translation is still incomplete.",
    optionalNoticeBody: "These points do not block the active model, but they can further improve the workspace setup.",
    advancedDetails: "More details",
    advancedSettings: "Advanced settings",
    historicalState: "Not active in current model"
  },
  de: {
    processTitle: "Customer-Value-Workflow",
    processBody: "Folgen Sie dem aktuellen Schritt. Das aktive Modell bleibt unverändert, bis Sie gespeicherte Änderungen bewusst aktivieren.",
    compactSummaryTitle: "Customer-Value-Modell ist aktiv",
    compactSummaryReady: "Vollständig eingerichtet",
    compactSummaryCriteria: "Kriterien",
    compactSummaryWeighting: "Gewichtung",
    compactSummarySegments: "Segmente",
    compactSummaryScoring: "Bewertungslogik",
    processReadyTitle: "Customer-Value-Modell ist vollständig eingerichtet und aktiv",
    processReadyBody: "HAUFFE OS verwendet die gespeicherten Kriterien, Gewichtungen, Bewertungslogiken und Segmente für die Customer-Value-Bewertung.",
    processDraftBody: "Gespeicherte Änderungen sind noch nicht aktiv. Prüfen Sie diese Änderungen und aktivieren Sie das aktualisierte Modell bewusst.",
    processConfirmedBadge: "Aktiv",
    processDraftBadge: "Änderungen bereit",
    processNeedsAttentionBadge: "Aktion erforderlich",
    stepDone: "",
    stepCurrent: "Aktuell",
    stepOpen: "Offen",
    stepCriteria: "Kriterien",
    stepWeighting: "Gewichtung",
    stepScoring: "Bewertungslogik",
    stepSegments: "Segmente",
    stepFinish: "Aktiv",
    editCriteria: "Kriterien bearbeiten",
    primaryActionTitle: "Nächster Schritt",
    primaryActionDone: "Aktuell ist keine weitere Einrichtungsaktion erforderlich.",
    editModeTitle: "Bearbeitungsmodus",
    editModeBody: "Die aktive Modellversion bleibt unverändert, bis Sie gespeicherte Änderungen bewusst aktivieren.",
    editModel: "Modell bearbeiten",
    stopEditing: "Bearbeitung beenden",
    testModelCollapsed: "Modell testen",
    hideTestModel: "Modelltest ausblenden",
    criteriaSectionTitle: "Aktive Kriterien",
    criteriaSectionBody: "Diese Kriterien steuern aktuell das produktive Customer-Value-Modell. Öffnen Sie eine Karte, um Formulierung, Gewichtung oder Bewertungslogik anzupassen.",
    criteriaCompactBody: "Öffnen Sie ein Kriterium nur dann, wenn Sie Formulierung, Gewichtung oder Bewertungslogik anpassen möchten.",
    historicalCriteriaTitle: (count: number) => count === 1 ? "Historisches Kriterium (1)" : `Historische Kriterien (${count})`,
    historicalCriteriaBody: "Diese Kriterien sind im aktuellen Modell nicht mehr aktiv und blockieren weder Readiness noch Gewichtung oder Scoring.",
    showMore: "Mehr anzeigen",
    showLess: "Weniger anzeigen",
    activeStatusReady: "Eingerichtet",
    activeStatusDraft: "Änderungen noch nicht aktiviert",
    activeStatusTodo: "Bewertungslogik noch nicht vollständig",
    activeStatusLegacy: "Eingerichtet",
    activeStatusHistorical: "Historisch",
    criterionInfluenceHigher: "Höhere Werte erhöhen den Customer Value",
    criterionInfluenceLower: "Niedrigere Werte erhöhen den Customer Value",
    criterionInfluenceManual: "Fachliche Einschätzung mit festen Bewertungsstufen",
    criterionInfluenceUnknown: "Die Bewertungslogik wird in diesem Kriterium gepflegt",
    criterionEdit: "Bearbeiten",
    criterionBusinessSummary: "Fachliche Zusammenfassung",
    criterionValidationTitle: "Was hier noch fehlt",
    criterionValidationGap: "Zwischen mindestens zwei Bewertungsstufen besteht eine Lücke.",
    criterionValidationOverlap: "Mindestens zwei Bewertungsstufen überschneiden sich.",
    criterionValidationAmbiguousBoundary: "Eine Grenze ist mehrdeutig, weil zwei benachbarte Bereiche denselben Wert einschließen.",
    criterionValidationBounds: "Mindestens eine Bewertungsstufe hat ungültige Von-/Bis-Werte.",
    criterionValidationPoints: "Jede aktive Bewertungsstufe braucht einen eindeutigen Punktwert von 1 bis 10.",
    criterionValidationEmpty: "Hinterlegen Sie zuerst eine vollständige Bewertungslogik, bevor dieses Kriterium aktiviert wird.",
    criterionValidationOption: "Jede auswählbare Option braucht eine eigene eindeutige Bewertungsstufe.",
    criterionValidationBoolean: "Sowohl Ja als auch Nein brauchen einen zugeordneten Punktwert.",
    criterionValidationFallback: "Vervollständigen und prüfen Sie die Bewertungslogik in diesem Kriterium.",
    criterionFixAction: "Kriterium korrigieren",
    saveChanges: "Änderungen speichern",
    savedChangesInactive: "Gespeicherte Änderungen sind noch nicht aktiv",
    activateModel: "Modell aktivieren",
    reviewModel: "Modell prüfen",
    testModel: "Modell testen",
    noUnsavedChanges: "Keine ungespeicherten Änderungen",
    confirmAlreadyActive: "Die aktuelle Modellversion ist bereits aktiv.",
    segmentsCompactTitle: "Segmentübersicht",
    segmentsCompactBody: "Diese Score-Bereiche strukturieren aktuell die produktive Customer-Value-Einordnung.",
    editSegments: "Segmente bearbeiten",
    optionalNoticeTitle: "Weitere Hinweise",
    optionalPerspectives: (count: number) => count === 1 ? "1 optionale Perspektive fehlt noch." : `${count} optionale Perspektiven fehlen noch.`,
    optionalTranslations: "Die englische Übersetzung ist noch unvollständig.",
    optionalNoticeBody: "Diese Punkte blockieren das aktive Modell nicht, können den Workspace aber weiter verbessern.",
    advancedDetails: "Weitere Details",
    advancedSettings: "Erweiterte Einstellungen",
    historicalState: "Im aktuellen Modell nicht aktiv"
  }
} as const;

type CustomerValueUxLanguage = keyof typeof CUSTOMER_VALUE_UX_COPY;
type CustomerValueWorkflowStepState = "done" | "current" | "todo";

interface CustomerValueWorkflowStep {
  key: "criteria" | "weighting" | "scoring" | "segments" | "finish";
  state: CustomerValueWorkflowStepState;
}

export function splitCustomerValueCriteriaForDisplay<T extends Pick<RuntimeCustomerValueCriterionConfiguration, "id" | "active" | "rank" | "scoringConfigurationStatus">>(criteria: readonly T[]) {
  const sorted = criteria.slice().sort((left, right) => left.rank - right.rank);
  return {
    active: sorted.filter((criterion) => criterion.active && criterion.scoringConfigurationStatus !== "superseded"),
    historical: sorted.filter((criterion) => !criterion.active || criterion.scoringConfigurationStatus === "superseded")
  };
}

function customerValueValidationMessage(error: CustomerValueCriterionScoringError, language: CustomerValueUxLanguage): string {
  const copy = CUSTOMER_VALUE_UX_COPY[language];
  switch (error.code) {
    case "scoring_band_gap":
      return copy.criterionValidationGap;
    case "scoring_band_overlap":
      return copy.criterionValidationOverlap;
    case "scoring_band_ambiguous_boundary":
      return copy.criterionValidationAmbiguousBoundary;
    case "scoring_band_invalid_bounds":
      return copy.criterionValidationBounds;
    case "scoring_band_missing_points":
    case "scoring_band_points_out_of_range":
    case "scoring_band_duplicate_points":
      return copy.criterionValidationPoints;
    case "scoring_band_empty":
      return copy.criterionValidationEmpty;
    case "qualitative_option_empty":
    case "qualitative_option_duplicate":
    case "qualitative_option_minimum_not_met":
      return copy.criterionValidationOption;
    case "boolean_band_missing_true":
    case "boolean_band_missing_false":
    case "boolean_band_duplicate_value":
      return copy.criterionValidationBoolean;
    default:
      return copy.criterionValidationFallback;
  }
}

export function describeCustomerValueScoringIssues(
  criterion: RuntimeCustomerValueCriterionConfiguration,
  bands: readonly CustomerValueCriterionScoringBand[],
  language: CustomerValueUxLanguage
): string[] {
  const validation = validateCriterionScoringBandSet(criterion, bands);
  const fallbackValidation = validateDraftScoringBandsForPersistence(criterion, bands);
  const sourceErrors = validation.errors.length > 0 ? validation.errors : fallbackValidation.errors;
  return Array.from(new Set(sourceErrors.map((error) => customerValueValidationMessage(error, language))));
}

function buildCustomerValueWorkflowSteps(input: {
  modelReadiness?: RuntimeCustomerValueConfigurationResponse["readiness"];
  hasActiveCriteria: boolean;
  hasConfirmedModel: boolean;
}): CustomerValueWorkflowStep[] {
  const readiness = input.modelReadiness;
  const criteriaDone = Boolean(readiness?.modelTypeConfigured && input.hasActiveCriteria);
  const weightingDone = Boolean(readiness?.weightsComplete);
  const scoringDone = Boolean(readiness?.scoringComplete);
  const segmentsDone = Boolean(readiness?.segmentsConfirmed);
  const finishDone = input.hasConfirmedModel;
  return [
    { key: "criteria", state: criteriaDone ? "done" : "current" },
    { key: "weighting", state: criteriaDone && !weightingDone ? "current" : weightingDone ? "done" : "todo" },
    { key: "scoring", state: criteriaDone && weightingDone && !scoringDone ? "current" : scoringDone ? "done" : "todo" },
    { key: "segments", state: criteriaDone && weightingDone && scoringDone && !segmentsDone ? "current" : segmentsDone ? "done" : "todo" },
    { key: "finish", state: finishDone ? "done" : readiness?.confirmationReady ? "current" : "todo" }
  ];
}

function buildCustomerValueScoringDraftSignature(input: {
  modelType: string;
  criteria: readonly RuntimeCustomerValueCriterionConfiguration[];
  bandsByCriterionId: Record<string, readonly CustomerValueCriterionScoringBand[]>;
}) {
  return JSON.stringify({
    modelType: input.modelType,
    criteria: input.criteria
      .slice()
      .sort((left, right) => left.id.localeCompare(right.id))
      .map((criterion) => ({
        id: criterion.id,
        active: criterion.active,
        categoryId: criterion.categoryId ?? null,
        criterionType: criterion.criterionType,
        customUnit: criterion.customUnit ?? null,
        description: criterion.description ?? "",
        modelType: criterion.modelType,
        name: criterion.name,
        rank: criterion.rank,
        required: criterion.required,
        scoringConfigurationStatus: criterion.scoringConfigurationStatus,
        scoringDirection: criterion.scoringDirection,
        sourceStrategy: criterion.sourceStrategy,
        unitType: criterion.unitType,
        valueType: criterion.valueType,
        weight: criterion.weight
      })),
    bandsByCriterionId: Object.fromEntries(
      Object.entries(input.bandsByCriterionId)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([criterionId, bands]) => [criterionId, bands.slice().sort((left, right) => left.id.localeCompare(right.id))])
    )
  });
}

export function CustomerValueModel() {
  const language = useWorkspaceLanguage();
  const copy = language === "en" ? {
    eyebrow: "Customer Value Takeaway",
    title: "Customer Value Calculation",
    description: "The Customer Value Calculation is prepared step by step from department perspectives, value clustering and reviewed weighting.",
    loadError: "Customer Value Calculation could not be loaded.",
    exportError: "Export could not be created.",
    loadingTitle: "Customer Value Calculation is loading",
    loadingLabel: "Customer Value Calculation is being prepared",
    loadErrorTitle: "The working state could not be loaded",
    saved: "Saved",
    noWorkspaceTitle: "No workspace available yet.",
    noWorkspaceBody: "There is currently no workspace stored for your access.",
    workingState: "Digital working state",
    workingBody: "This process leads from submitted department perspectives to a reviewable Customer Value Calculation. The Company Owner finalizes criteria, weightings and segment logic in the workspace.",
    workspace: "Workspace",
    process: "Process",
    valueRound: "Value Round",
    calculation: "Calculation",
    confirmed: "Confirmed",
    readyForReview: "Ready for review",
    open: "Open",
    firstEvaluationTitle: "Request the HAUFFE OS Evaluation first.",
    firstEvaluationBody: "The second Value Round opens automatically once the HAUFFE OS Evaluation has been saved.",
    goEvaluation: "Go to HAUFFE OS Evaluation",
    valueRoundRunning: "Value Round running",
    valueRoundBody: "The invited departments are currently answering what makes a customer valuable from their perspective. Afterwards, you can cluster and weight criteria.",
    clusterTitle: "Cluster Customer Value Criteria",
    clusterBody: "Once all value perspectives have been submitted, the workspace can derive up to seven reviewable Customer Value Criteria from them.",
    clustersPrepared: "Customer Value Criteria were prepared.",
    clustersRegenerated: "Customer Value Criteria were regenerated in the current language.",
    clustersError: "Customer Value Criteria could not be prepared.",
    criteriaLanguageTitle: "Translation incomplete",
    criteriaLanguageBody: "This Customer Value Calculation is not fully available in the current language yet. Run repair or regenerate the criteria in this language.",
    regenerateCriteria: "Regenerate criteria in current language",
    localGeneratingHelp: "This usually takes up to 3 minutes. Please keep this window open.",
    processingData: "HAUFFE OS is processing the data ...",
    processingStay: "This may take a moment. Please stay on this page.",
    openRegeneratedCriteria: "Open regenerated criteria",
    prepareCriteria: "Prepare Customer Value Criteria",
    reviewTitle: "Review and confirm criteria",
    reviewNote: "Review the suggested criteria as a working state. Remove or refine criteria before finalizing the weighting.",
    criterion: "Criterion",
    criterionType: "Type",
    descriptionLabel: "Description",
    category: "Category",
    scoringType: "Scoring type",
    unit: "Unit",
    unitPlaceholder: "€, %, number, days, hours...",
    positive: "positive",
    negative: "negative",
    neutral: "neutral",
    perspective: "Department perspective",
    systemMetric: "central numeric value / system data",
    prospectMetric: "prospect / offer value",
    manual: "1-10 assessment",
    higherBetter: "Numeric value, higher is better",
    lowerBetter: "Numeric value, lower is better",
    effort: "negative effort signal",
    signals: "Signals",
    derivedSignals: "derived from department perspectives",
    departments: "Departments",
    multiplePerspectives: "multiple perspectives",
    criterionSaved: "Criterion saved.",
    criterionSaveError: "Criterion could not be saved.",
    saveCriterion: "Save criterion",
    criterionRemoved: "Criterion removed.",
    criterionRemoveError: "Criterion could not be removed.",
    remove: "Remove",
    criteriaApproved: "Criteria are released for weighting.",
    criteriaApproveError: "Criteria could not be confirmed.",
    confirmCriteria: "Confirm criteria and open weighting",
    editCriteria: "Edit criteria and open weighting",
    weightingTitle: "Finalize weighting",
    weightingBody: "Once all participating perspectives have submitted their weighting, the average becomes the suggested baseline. The Company Owner then reviews, adjusts if needed and prepares the Customer Value Calculation.",
    departmentWeightingTitle: "Department weighting results",
    departmentWeightingBody: "Each participating perspective weights the confirmed criteria. The average becomes the suggested baseline for the final weighting.",
    department: "Perspective",
    suggestedAverage: "Suggested average",
    ownerFinal: "Owner final",
    missingWeightings: "Missing weightings",
    allWeightingsSubmitted: "All participating perspectives have submitted their weighting.",
    continueWithMissing: "Final weighting is only available once all required perspectives have submitted their weighting.",
    allWeightingsReady: "All department weightings are available. You can now review the final weighting.",
    remindDepartments: "Remind missing perspectives",
    reminderSent: "Reminder sent.",
    weightingTask: "Open weighting task",
    segmentLogic: "How many customer segments do you want to create?",
    finalSegmentLogic: "Customer Value dimensions and weighting",
    dimensionLogicBody: "The final Customer Value Score is calculated separately from weighted dimensions. Each dimension is internally assessed from 0-100 and then contributes to the overall score with its defined weighting.",
    dimensionScore: "Dimension score",
    dimensionScoreRange: "0-100 internal partial score",
    dimensionWeight: "Weighting in overall score",
    dimensionInfluence: "Influence",
    positiveInfluence: "positive value driver",
    negativeInfluence: "effort / risk reduces the final score",
    neutralInfluence: "context factor",
    segmentMeaning: "Meaning",
    segmentCharacteristics: "Typical characteristics",
    segmentRisks: "Risks",
    segmentRecommendation: "Recommended action",
    segmentDepartments: "Relevant perspectives",
    segmentDataHints: "CRM / operational data",
    segmentHelp: "Choose how granularly HAUFFE OS should distinguish customers by calculated Customer Value. Segment logic is derived from your criteria, weightings and customer data.",
    segmentOption: (count: number) => `${count} customer-defined segments`,
    distributed: "Assigned",
    calculationPrepared: "Customer Value Calculation was prepared.",
    preparingCalculation: "Customer Value Calculation is being prepared ...",
    weightingError: "Weighting could not be saved.",
    prepareCalculation: "Prepare Customer Value Calculation",
    status: "Status",
    methodologyVersion: "Methodology version",
    notStored: "Not stored",
    calculationConfirmed: "Customer Value Calculation was confirmed.",
    confirmError: "Customer Value Calculation could not be confirmed.",
    confirmCalculation: "Confirm Customer Value Calculation",
    finalEvaluationTitle: "Final customer evaluation",
    finalEvaluationBody: "The final result is calculated on the server from submitted customer responses and stored as an immutable snapshot.",
    finalEvaluationCompany: "Company",
    finalEvaluationScore: "Final score",
    finalEvaluationSegment: "Final segment",
    finalEvaluationVersion: "Engine version",
    finalEvaluationReady: "Ready for finalization",
    finalEvaluationFinalized: "Finalized",
    finalEvaluationUnavailable: "No company evaluation is ready for finalization yet.",
    finalEvaluationNotReady: "Finalization becomes available once a company evaluation is marked ready on the server.",
    finalizeEvaluation: "Finalize customer evaluation",
    finalizingEvaluation: "Finalization is running ...",
    finalEvaluationSaved: "Final customer evaluation was stored immutably.",
    finalEvaluationError: "Final customer evaluation could not be stored.",
    finalEvaluationDialogTitle: "Finalize this customer evaluation?",
    finalEvaluationDialogBody: "The result is recalculated on the server and then stored immutably. Later model changes will not change this result. A future reassessment creates a new result state.",
    finalEvaluationDialogCancel: "Cancel",
    finalEvaluationDialogConfirm: "Finalize evaluation",
    finalEvaluationReadbackError: "The final result could not be loaded from the server.",
    finalEvaluationRecovered: "The server already contains the final result. The stored result was loaded.",
    finalEvaluationHistoryTitle: "Result history",
    finalEvaluationHistoryEmpty: "No final result has been stored for this evaluation yet.",
    latestFinalResult: "Latest result",
    historicalFinalResult: "Historical result",
    criterionBreakdown: "Criterion breakdown",
    calculationDetails: "Calculation details",
    scoreScale: "Scale",
    rawScore: "Raw score",
    roundedScore: "Rounded score",
    weightingVersion: "Weighting version",
    weightingVersionMissing: "No separate weighting version is stored yet.",
    snapshotReference: "Snapshot reference",
    finalizedAt: "Finalized at",
    calculatedAt: "Calculated at",
    criterionRawValue: "Raw value",
    criterionPoints: "Points",
    criterionWeightedContribution: "Weighted contribution",
    scoringBand: "Scoring band",
    immutableResult: "Immutable result",
    finalCriterionStatusLabel: (status: string) => ({
      computed: "Calculated",
      blocked: "Blocked",
      ignored_inactive: "Inactive"
    })[status] ?? "Not available",
    finalEvaluationFailureLabel: (code: string) => ({
      customer_value_score_not_ready: "The scoring setup is not ready for finalization.",
      scoring_not_ready: "The scoring setup is not ready for finalization.",
      missing_confirmed_weights: "Confirmed weighting is missing.",
      missing_confirmed_scoring_logic: "Confirmed scoring logic is missing.",
      invalid_segment_configuration: "The segment configuration is not valid.",
      calculation_failed: "The server could not calculate the result.",
      company_evaluation_not_ready: "This company evaluation is not ready for finalization.",
      evaluation_not_ready: "This company evaluation is not ready for finalization.",
      company_evaluation_already_finalized: "This company evaluation has already been finalized.",
      evaluation_already_finalized: "This company evaluation has already been finalized.",
      forbidden: "You do not have permission to finalize this evaluation.",
      unauthenticated: "Please sign in again.",
      assessment_persistence_failed: "The final snapshot could not be stored.",
      final_result_snapshot_exists: "A final result already exists."
    })[code] ?? "The finalization could not be completed safely.",
    finalHelp: {
      score: {
        title: "Customer Value Score",
        explanation: "The stored score shows the server-calculated customer value on the 0-100 scale.",
        impact: "It is used to assign the customer to the configured segment.",
        example: "Example: 84.2 can place a customer in a high-value segment if the saved segment ranges match."
      },
      segment: {
        title: "Segment",
        explanation: "The segment is resolved from the stored score and the confirmed segment definition.",
        impact: "It helps translate the score into a practical customer group.",
        example: "Example: a top segment can indicate stronger account focus."
      },
      points: {
        title: "Points",
        explanation: "Points are the score assigned by the confirmed scoring band for this criterion.",
        impact: "They determine how strongly the raw criterion value contributes before weighting.",
        example: "Example: a value can receive 8 of 10 points in its configured range."
      },
      weight: {
        title: "Weighting",
        explanation: "The weighting defines how much this criterion contributes to the total score.",
        impact: "Higher weighting makes the criterion more influential.",
        example: "Example: 20% weighting means the criterion contributes one fifth of the total score."
      },
      contribution: {
        title: "Weighted contribution",
        explanation: "This is the stored contribution from points multiplied by weighting.",
        impact: "The contributions add up to the raw score.",
        example: "Example: 8 points with 20% weighting contribute 16 points."
      },
      engine: {
        title: "Engine version",
        explanation: "The engine version identifies the server-side calculation contract used for this result.",
        impact: "It makes historic results traceable when the model evolves.",
        example: "Example: customer_value_scoring_v1 marks the first calculation engine."
      },
      immutable: {
        title: "Immutable evaluation",
        explanation: "A finalized result is stored as a snapshot and is not overwritten.",
        impact: "Later model changes do not alter this historic result.",
        example: "Example: a future reassessment creates another result instead of changing this one."
      }
    },
    viewCalculation: "View Customer Value Calculation",
    download: "Download Customer Value Calculation",
    downloading: "Download is being prepared ...",
    criteriaWeights: "Criteria and weightings",
    weighting: "Weighting",
    scoreLogic: "Score logic",
    scoreLogicBody: "The final Customer Value Score runs from 0-100 and is assigned to the customer-defined score segments below. These segments are separate from the individual dimension scores.",
    segmentDefinitionTitle: "Customer Value segmentation",
    segmentDefinitionBody: "Define how customers are assigned to customer segments based on their calculated Customer Value total score. The total score ranges from 0 to 100 points. Confirmed segment definitions are immutable; changes are prepared as a new draft version and become active only after confirmation.",
    segmentAssignmentExample: (name: string, min: number, max: number) => `Customers with a Customer Value total score from ${min} to ${max} points are assigned to ${name}.`,
    segmentPosition: (position: number, total: number) => `Segment ${position} of ${total}`,
    segmentVersion: "Segment version",
    createSegmentDraft: "Prepare new segment version",
    saveSegmentDraft: "Save segment draft",
    confirmSegmentDraft: "Confirm segment version",
    segmentDraftPrepared: "Segment draft was prepared.",
    segmentDraftSaved: "Segment draft was saved.",
    segmentDraftConfirmed: "Segment version was confirmed.",
    segmentDraftError: "Segment definition could not be saved.",
    segmentName: "Segment name",
    segmentDescriptionField: "Description",
    segmentRank: "Segment order",
    segmentMinScore: "Customer Value total score from",
    segmentMaxScore: "Customer Value total score to",
    segmentBest: "Highest segment",
    segmentWorst: "Lowest segment",
    segmentInvalid: "Please resolve the segment validation issues before confirmation.",
    configurationTitle: "Customer Value scoring setup",
    configurationBody: "Prepare model type, criteria, weighting and scoring bands as a draft. Confirmation is disabled in this step until the full model-version guard is released.",
    completionSummaryTitle: "Customer Value completion flow",
    completionStepsRemaining: (count: number) => count === 1 ? "1 step to completion" : `${count} steps to completion`,
    completionReadyTitle: "The Customer Value model is fully configured.",
    completionReadyBody: "You can now confirm the model deliberately.",
    completionFlowTitle: "Configuration flow",
    completionStepCriteria: "Define criteria",
    completionStepWeighting: "Finalize weighting",
    completionStepScoring: "Define scoring logic",
    completionStepSegments: "Define customer segments",
    completionStepPreview: "Review preview",
    completionStepConfirm: "Confirm model",
    completionActionCriteria: "Edit criteria",
    completionActionWeighting: "Edit weighting",
    completionActionScoring: "Edit scoring logic",
    completionActionSegments: "Edit segmentation",
    completionActionPreview: "Review preview",
    completionActionConfirm: "Go to confirmation",
    confirmationBlockedTitle: "You cannot confirm the model yet.",
    confirmationUnavailableReason: "Complete the remaining configuration steps first.",
    modelTypeMissingReason: "Select whether this model evaluates existing or new customer value.",
    weightingIncompleteReason: "The weighting is not complete yet.",
    scoringIncompleteReason: (count: number) => count === 1 ? "Scoring logic is missing for 1 criterion." : `Scoring logic is missing for ${count} criteria.`,
    segmentIncompleteReason: "The segmentation is not complete yet.",
    previewIncompleteReason: "Review the preview before confirming the model.",
    configurationLoading: "Scoring setup is loading.",
    modelType: "Model type",
    unclassifiedModel: "Model type not selected yet",
    unsavedModelTypeDraft: "selected locally, not saved yet",
    existingCustomerValue: "Customer Value for existing customers",
    newCustomerValue: "New Customer Value for prospects and new customers",
    modelReadinessStatus: "Model readiness",
    modelReadinessLabel: (status: CustomerValueModelReadinessStatus) => ({
      not_configured: "Not configured",
      in_progress: "In progress",
      scoring_incomplete: "Scoring logic incomplete",
      ready_for_review: "Ready for review",
      preview_ready: "Preview-ready",
      confirmed: "Confirmed"
    })[status],
    modelReadinessHelp: "Readiness combines model type, weighting, confirmed segments and confirmed scoring logic for active criteria.",
    criteriaConfigurationTitle: "Criteria overview",
    categoriesTitle: "Criterion categories",
    uncategorized: "Uncategorized",
    categoryName: "Category name",
    addCategory: "Add category",
    addCategoryInline: "+ New category",
    saveCategory: "Save category",
    manageCategories: "Manage categories",
    advancedOrganization: "Advanced organization",
    advancedOrganizationBody: "Categories are optional. They help structure the model, but do not affect scoring, weighting or Deal Win usage.",
    deactivateCategory: "Deactivate category",
    reactivateCategory: "Reactivate category",
    deleteCategory: "Delete draft category",
    categoryDescription: "Description",
    createCategoryAndSelect: "Create and select",
    cancel: "Cancel",
    addCriterion: "Add criterion",
    addCriterionFlowTitle: "Create atomic criterion",
    addCriterionFlowBody: "Define one independently measurable dimension. Create separate criteria when revenue, margin, effort or strategic fit should be assessed separately.",
    addCriterionDraft: "Create criterion draft",
    descriptionSuggestion: "Description suggestion",
    requestDescriptionSuggestion: "Suggest description",
    applyDescriptionSuggestion: "Use suggestion",
    suggestionSourceSummary: "Source",
    suggestionConfidence: "Confidence",
    confidenceStrongContext: "strong workshop context",
    confidencePartialContext: "partial workshop context",
    confidenceGenericFallback: "general suggestion",
    existingDescriptionKept: "Existing descriptions are never overwritten automatically.",
    duplicateCriterion: "Duplicate",
    deactivateCriterion: "Deactivate",
    reactivateCriterion: "Reactivate",
    deleteCriterion: "Delete draft",
    openCriterion: "Open configuration",
    closeCriterion: "Close configuration",
    replaceLegacyCriterion: "Replace with individual criteria",
    replacementFlowTitle: "Guided replacement draft",
    replacementIntro: "Split this combined criterion into clear individual criteria. The confirmed criterion stays unchanged until you save the scoring draft.",
    replacementStepExplain: "Understand",
    replacementStepCriteria: "Criteria",
    replacementStepWeights: "Weights",
    replacementStepReview: "Review",
    replacementCategorySuggestion: "Replacement category",
    replacementCriteriaSuggestion: "Suggested individual criteria",
    replacementApplyWeights: "Apply suggested split",
    replacementSplitConfirmed: "I have reviewed the replacement and weighting split.",
    replacementDraftApplied: "Replacement draft was prepared locally.",
    replacementDraftApply: "Prepare replacement draft",
    replacementDraftCancel: "Cancel replacement",
    replacementDraftBlocked: "Review and confirm the weight split before preparing the replacement draft.",
    replacementWeightSummary: (total: number, expected: number) => `Replacement weighting: ${total}% of ${expected}%`,
    replacementRevenuePotential: "Revenue potential",
    replacementMarginQuality: "Margin quality",
    replacementDescriptionRevenue: "Expected recurring and one-time revenue potential for this customer.",
    replacementDescriptionMargin: "Expected margin quality or contribution quality for this customer.",
    advancedSettings: "Advanced settings",
    fromValue: "From",
    toValue: "To",
    crmTransparencyTitle: "CRM transparency contract",
    crmTransparencyBody: "Later CRM write-back should expose raw value, points, weighting and weighted contribution per criterion. Categories remain organizational and do not receive their own score.",
    criterionCreated: "Criterion draft was created.",
    criterionCreatedNamed: (name: string) => `Criterion "${name}" was created as a draft and is visible in the list.`,
    criterionCreating: "Creating criterion draft...",
    criterionDuplicateError: "A criterion with this name already exists in this model. Please use a distinct name or edit the existing draft.",
    criterionWeightError: "Enter a criterion weighting between 0 and 100%. The overall model weighting is checked only when the scoring draft is saved.",
    criterionBandsError: "Please complete the scoring bands for this criterion before saving it.",
    criterionCreateError: "Criterion draft could not be created.",
    criterionDeleted: "Criterion draft was deleted.",
    categorySaved: "Category was saved.",
    categoryError: "Category could not be saved.",
    replaceLegacyHint: "Creates a draft suggestion only. No activation, confirmation, points or weighting are changed automatically.",
    quantitativeSuggestion: "Suggest 10 ranges",
    directRatingSuggestion: "Configure as direct 1-10 assessment",
    directRatingHelp: "Use this for business ratings where the user deliberately assigns 1 to 10 points. The saved scoring bands map each point to the same input value.",
    quantitativeFieldsOnly: "Numeric fields are shown only for numeric criteria.",
    activeWeightDifference: (total: number) => total === 100
      ? "Weighting is exactly 100%."
      : total > 100
        ? `${Math.abs(100 - total)} percentage points too much.`
        : `${Math.abs(100 - total)} percentage points missing.`,
    activeWeightSummary: "Active weighting",
    activeWeightAllowed: "Allowed: 100%",
    activeWeightTooHigh: (difference: number) => `Reduce the weighting of active criteria by ${difference}% before saving the scoring draft.`,
    activeWeightTooLow: (difference: number) => `Add ${difference}% to active criteria before saving the scoring draft.`,
    activeWeightComplete: "Active criteria add up to exactly 100%. The scoring draft can be saved.",
    activeWeightDraftIncomplete: "Individual criteria can already be saved as drafts. The scoring draft can only be completed at exactly 100% active weighting.",
    saveBlockedWeight: "Saving is blocked until active criteria add up to exactly 100%.",
    noAutomaticRedistribution: "Weights are never redistributed automatically.",
    scoringConfigurationTitle: "Scoring bands",
    editNewScoringDraft: "Edit new version",
    previewTitle: "Preview",
    versionsTitle: "Versions",
    versionSummary: (active: number, total: number) => `${active} active · ${total} total`,
    criterionConfiguration: "Criterion configuration",
    configurationStatus: "Configuration status",
    activeWeightTotal: (total: number) => `Active weighting: ${total}%`,
    configurationInvalid: "Please resolve the configuration issues before saving the draft.",
    saveConfigurationDraft: "Save scoring draft",
    configurationDraftSaved: "Scoring draft was saved.",
    configurationDraftError: "Scoring draft could not be saved.",
    runConfigurationPreview: "Run preview",
    configurationPreviewError: "Preview could not be calculated.",
    confirmConfiguration: "Confirm scoring configuration",
    confirmConfigurationDisabled: "Complete scoring logic before confirmation",
    configurationConfirmed: "Scoring configuration is confirmed.",
    configurationConfirmError: "Scoring configuration could not be confirmed.",
    scoringIncompleteExplanation: "The model exists, but active criteria still need confirmed scoring logic before final scoring can run.",
    criteriaRequiringScoring: "Criteria requiring scoring logic",
    scoringStatusTodo: "Still to configure",
    scoringStatusDraft: "Draft",
    scoringStatusReady: "Ready",
    configureCriterionEvaluation: "Configure evaluation",
    weightingRequiredExplanation: "All active criteria must add up to 100% weighting.",
    currentWeighting: "Current",
    requiredWeighting: "Required",
    evaluationType: "Evaluation type",
    valueType: "Value type",
    scoringDirection: "Scoring direction",
    sourceStrategy: "Source strategy",
    requiredField: "Required",
    activeField: "Active",
    rank: "Order",
    points: "Points",
    minValue: "Min",
    maxValue: "Max",
    openLower: "Open lower bound",
    openUpper: "Open upper bound",
    minInclusive: "Lower included",
    maxInclusive: "Upper included",
    bandPreview: "Readable preview",
    optionValue: "Option value",
    optionLabel: "Option label",
    optionLabelEn: "English label optional",
    booleanTrue: "True",
    booleanFalse: "False",
    addOption: "Add option",
    previewInput: "Test value",
    previewDirectRatingInput: "Direct 1-10 assessment",
    previewSelectOption: "Select option",
    previewBooleanInput: "Yes/No test value",
    previewConfigureFirst: "Configure scoring logic first",
    previewScore: "Preview score",
    previewSegment: "Preview segment",
    previewMissing: "Missing input",
    previewMissingScoringLogic: "Missing scoring logic",
    previewNotComputable: "Criteria not computable",
    previewAffectedCriteria: "Affected criteria",
    previewCounterHelp: "Missing input means no test value. Missing scoring logic means no confirmed scoring setup. Not computable means input and scoring exist, but no matching result could be resolved.",
    previewAllInputsPresentConfigurationMissing: "All test values are present, but the preview cannot use the saved scoring setup yet.",
    previewErrors: "Preview issues",
    previewIssueLabel: (code: string) => ({
      missing_input: "At least one test value is missing.",
      scoring_configuration_missing: "Scoring logic could not be loaded for at least one criterion.",
      weighted_score_missing_criterion_score: "At least one criterion could not be calculated.",
      weighted_score_no_active_criteria: "No active criteria are available for the preview.",
      weighted_score_invalid_weight_total: "Active criteria must add up to 100% weighting.",
      invalid_weight: "At least one active criterion has an invalid weighting.",
      criterion_value_unmatched: "At least one test value does not match a scoring range."
    })[code] ?? "Preview issue",
    calculatedUnavailable: "Calculated criteria are shown for transparency but cannot be configured in this step.",
    legacyNotice: "No structured scoring logic is stored for this legacy criterion yet. Configure scoring logic here or prepare separate individual criteria.",
    configureScoringLogic: "Configure scoring logic",
    draftOnly: "Draft only. Confirming a new productive scoring version is intentionally unavailable.",
    atomicSaveNote: "Save criterion stores base data, category, weighting and scoring logic together as one draft.",
    incompleteBands: "Please complete the scoring bands before saving.",
    missingBandConfiguration: "Scoring logic is incomplete.",
    notAvailableYet: "This function is not available yet.",
    bandUntil: (value: string) => `up to ${value}`,
    bandFrom: (value: string) => `from ${value}`,
    bandBetween: (min: string, max: string) => `${min} to ${max}`,
    bandAny: "any value",
    reviewPerspectives: "Review Missing Perspectives",
    reviewPerspectivesBody: "Check whether all relevant functional perspectives have been included. Margin, contribution margin, payment quality and strategic relevance often require an executive, finance or commercial perspective.",
    submittedPerspectives: "Submitted perspectives",
    openPerspectives: "Open perspectives",
    recommendedPerspectives: "Optional recommended perspectives",
    executiveMissing: "Executive Management perspective is still missing.",
    financeMissing: "Finance or Commercial perspective is still missing.",
    addPerspective: "Add Perspective",
    inviteExecutive: "Invite Executive Management Perspective",
    answerExecutive: "Answer Executive Management Perspective",
    executiveExists: "Executive Management perspective already exists for this workspace.",
    inviteFinance: "Invite Finance Perspective",
    requestAdditional: "Request Additional Perspective",
    reopenValueRound: "Reopen Value Round",
    updateEvaluation: "Update Evaluation",
    updateEvaluationNote: "Existing results are preserved. Updating the evaluation is a conscious next action and does not happen silently.",
    organizationalOnly: "Organizational responsibility only",
    functionalPerspective: "Functional Perspective",
    votingPerspective: "Voting Perspective",
    continueWithoutExecutive: "Continue without Executive Management perspective",
    continueWithoutFinance: "Continue without Finance perspective",
    missingAcknowledged: "Missing perspectives were acknowledged.",
    acknowledgeError: "Missing perspectives could not be acknowledged.",
    valueRoundReopened: "Value Round was reopened for additional perspectives.",
    valueRoundReopenError: "Value Round could not be reopened.",
    perspectiveLabels: {
      department: "Department",
      executive: "Executive Management",
      finance: "Finance / Controlling",
      commercial: "Commercial / Revenue",
      operations: "Operations / Delivery",
      other: "Additional Perspective"
    },
    fromTo: (min: number, max: number) => `${min.toFixed(2)} to ${max.toFixed(2)}`
  } : {
    eyebrow: "Customer Value Takeaway",
    title: "Customer Value Calculation",
    description: "Die Customer Value Calculation wird Schritt für Schritt aus Abteilungsperspektiven, Value-Clustering und geprüfter Gewichtung vorbereitet.",
    loadError: "Customer Value Calculation konnte nicht geladen werden.",
    exportError: "Export konnte nicht erstellt werden.",
    loadingTitle: "Customer Value Calculation wird geladen",
    loadingLabel: "Customer Value Calculation wird vorbereitet",
    loadErrorTitle: "Der Arbeitsstand konnte nicht geladen werden",
    saved: "Gespeichert",
    noWorkspaceTitle: "Noch kein Workspace vorhanden.",
    noWorkspaceBody: "Für Ihren Zugang ist aktuell noch kein Workspace hinterlegt.",
    workingState: "Digitaler Arbeitsstand",
    workingBody: "Dieser Prozess führt von den eingereichten Abteilungsperspektiven zu einer prüfbaren Customer Value Calculation. Der Company Owner finalisiert Kriterien, Gewichtungen und Segmentlogik im Workspace.",
    workspace: "Workspace",
    process: "Prozess",
    valueRound: "Value-Runde",
    calculation: "Calculation",
    confirmed: "Bestätigt",
    readyForReview: "Bereit zur Prüfung",
    open: "Offen",
    firstEvaluationTitle: "Rufen Sie zuerst die HAUFFE OS Auswertung ab.",
    firstEvaluationBody: "Die zweite Value-Runde öffnet automatisch, sobald die HAUFFE OS Auswertung gespeichert wurde.",
    goEvaluation: "Zur HAUFFE OS Auswertung",
    valueRoundRunning: "Value-Runde läuft",
    valueRoundBody: "Die eingeladenen Abteilungen beantworten aktuell, was aus ihrer Sicht einen wertvollen Kunden ausmacht. Danach können Sie Kriterien clustern und gewichten.",
    clusterTitle: "Customer-Value-Kriterien clustern",
    clusterBody: "Sobald alle Value-Perspektiven eingereicht wurden, kann der Workspace daraus maximal sieben prüfbare Customer-Value-Kriterien ableiten.",
    clustersPrepared: "Customer-Value-Kriterien wurden vorbereitet.",
    clustersRegenerated: "Customer-Value-Kriterien wurden in der aktuellen Sprache neu erstellt.",
    clustersError: "Customer-Value-Kriterien konnten nicht vorbereitet werden.",
    criteriaLanguageTitle: "Übersetzung unvollständig",
    criteriaLanguageBody: "Diese Customer-Value-Inhalte sind in der aktuellen Sprache noch nicht vollständig. Bitte reparieren oder erstellen Sie die Inhalte neu, bevor Sie sie bearbeiten, gewichten oder exportieren.",
    regenerateCriteria: "Kriterien in aktueller Sprache neu erstellen",
    localGeneratingHelp: "Das dauert normalerweise bis zu 3 Minuten. Bitte lassen Sie dieses Fenster geöffnet.",
    processingData: "HAUFFE OS verarbeitet die Daten ...",
    processingStay: "Das kann einen Moment dauern. Bitte bleiben Sie auf dieser Seite.",
    openRegeneratedCriteria: "Neu erstellte Kriterien öffnen",
    prepareCriteria: "Customer-Value-Kriterien vorbereiten",
    reviewTitle: "Kriterien prüfen und bestätigen",
    reviewNote: "Prüfen Sie die vorgeschlagenen Kriterien als Arbeitsstand. Entfernen oder präzisieren Sie Kriterien, bevor Sie die Gewichtung finalisieren.",
    criterion: "Kriterium",
    criterionType: "Typ",
    descriptionLabel: "Beschreibung",
    category: "Kategorie",
    scoringType: "Bewertungstyp",
    unit: "Einheit",
    unitPlaceholder: "€, %, Anzahl, Tage, Stunden...",
    positive: "positiv",
    negative: "negativ",
    neutral: "neutral",
    perspective: "Abteilungsperspektive",
    systemMetric: "zentraler Zahlenwert / Systemdaten",
    prospectMetric: "Prospect-/Angebotswert",
    manual: "Einschätzung 1-10",
    higherBetter: "Zahlenwert, höher ist besser",
    lowerBetter: "Zahlenwert, niedriger ist besser",
    effort: "negatives Aufwandssignal",
    signals: "Signale",
    derivedSignals: "aus den Abteilungsperspektiven abgeleitet",
    departments: "Abteilungen",
    multiplePerspectives: "mehrere Perspektiven",
    criterionSaved: "Kriterium gespeichert.",
    criterionSaveError: "Kriterium konnte nicht gespeichert werden.",
    saveCriterion: "Kriterium speichern",
    criterionRemoved: "Kriterium entfernt.",
    criterionRemoveError: "Kriterium konnte nicht entfernt werden.",
    remove: "Entfernen",
    criteriaApproved: "Kriterien sind zur Gewichtung freigegeben.",
    criteriaApproveError: "Kriterien konnten nicht bestätigt werden.",
    confirmCriteria: "Kriterien bestätigen und Gewichtung öffnen",
    editCriteria: "Kriterien bearbeiten und Gewichtung öffnen",
    weightingTitle: "Gewichtung finalisieren",
    weightingBody: "Sobald alle beteiligten Perspektiven ihre Gewichtung eingereicht haben, wird der Durchschnitt zum vorgeschlagenen Basiswert. Der Company Owner prüft ihn, passt bei Bedarf an und bereitet danach die Customer Value Calculation vor.",
    departmentWeightingTitle: "Ergebnisse der Abteilungsgewichtung",
    departmentWeightingBody: "Jede beteiligte Perspektive gewichtet die bestätigten Kriterien. Der Durchschnitt wird zum vorgeschlagenen Basiswert für die finale Gewichtung.",
    department: "Perspektive",
    suggestedAverage: "Vorgeschlagener Durchschnitt",
    ownerFinal: "Owner final",
    missingWeightings: "Fehlende Gewichtungen",
    allWeightingsSubmitted: "Alle beteiligten Perspektiven haben ihre Gewichtung eingereicht.",
    continueWithMissing: "Die finale Gewichtung ist erst möglich, wenn alle erforderlichen Perspektiven ihre Gewichtung eingereicht haben.",
    allWeightingsReady: "Alle Abteilungsgewichtungen liegen vor. Sie können nun die finale Gewichtung prüfen.",
    remindDepartments: "Fehlende Perspektiven erinnern",
    reminderSent: "Erinnerung gesendet.",
    weightingTask: "Gewichtungsaufgabe öffnen",
    segmentLogic: "Wie viele Kundensegmente wollen Sie bilden?",
    finalSegmentLogic: "Customer-Value-Dimensionen und Gewichtung",
    dimensionLogicBody: "Der finale Customer-Value-Score entsteht separat aus gewichteten Dimensionen. Jede Dimension wird intern von 0 bis 100 bewertet und fließt anschließend mit ihrer definierten Gewichtung in den Gesamtscore ein.",
    dimensionScore: "Dimensionsscore",
    dimensionScoreRange: "0-100 interner Teilscore",
    dimensionWeight: "Gewichtung im Gesamtscore",
    dimensionInfluence: "Einfluss",
    positiveInfluence: "positiver Werttreiber",
    negativeInfluence: "Aufwand / Risiko reduziert den finalen Score",
    neutralInfluence: "Kontextfaktor",
    segmentMeaning: "Bedeutung",
    segmentCharacteristics: "Typische Merkmale",
    segmentRisks: "Risiken",
    segmentRecommendation: "Handlungsempfehlung",
    segmentDepartments: "Relevante Perspektiven",
    segmentDataHints: "CRM-/Operationalisierungslogik",
    segmentHelp: "Wählen Sie, wie fein HAUFFE OS Ihre Kunden nach dem berechneten Customer Value unterscheiden soll. Die Segmente werden aus Ihren Kriterien, Gewichtungen und Kundendaten gebildet.",
    segmentOption: (count: number) => `${count} kundendefinierte Segmente`,
    distributed: "Verteilt",
    calculationPrepared: "Customer Value Calculation wurde vorbereitet.",
    preparingCalculation: "Customer Value Calculation wird vorbereitet ...",
    weightingError: "Gewichtung konnte nicht gespeichert werden.",
    prepareCalculation: "Customer Value Calculation vorbereiten",
    status: "Status",
    methodologyVersion: "Methodik-Version",
    notStored: "Nicht hinterlegt",
    calculationConfirmed: "Customer Value Calculation wurde bestätigt.",
    confirmError: "Customer Value Calculation konnte nicht bestätigt werden.",
    confirmCalculation: "Customer Value Calculation bestätigen",
    finalEvaluationTitle: "Finale Kundenbewertung",
    finalEvaluationBody: "Das finale Ergebnis wird serverseitig aus eingereichten Kundenantworten berechnet und als unveränderlicher Snapshot gespeichert.",
    finalEvaluationCompany: "Unternehmen",
    finalEvaluationScore: "Finaler Score",
    finalEvaluationSegment: "Finales Segment",
    finalEvaluationVersion: "Engine-Version",
    finalEvaluationReady: "Bereit zur Finalisierung",
    finalEvaluationFinalized: "Finalisiert",
    finalEvaluationUnavailable: "Noch ist keine Unternehmensbewertung zur Finalisierung bereit.",
    finalEvaluationNotReady: "Die Finalisierung wird aktiv, sobald eine Unternehmensbewertung serverseitig bereitsteht.",
    finalizeEvaluation: "Kundenbewertung finalisieren",
    finalizingEvaluation: "Finalisierung läuft ...",
    finalEvaluationSaved: "Finale Kundenbewertung wurde unveränderlich gespeichert.",
    finalEvaluationError: "Finale Kundenbewertung konnte nicht gespeichert werden.",
    finalEvaluationDialogTitle: "Diese Kundenbewertung finalisieren?",
    finalEvaluationDialogBody: "Das Ergebnis wird serverseitig neu berechnet und anschließend unveränderlich gespeichert. Spätere Modelländerungen verändern dieses Ergebnis nicht. Eine spätere Neubewertung erzeugt einen neuen Ergebnisstand.",
    finalEvaluationDialogCancel: "Abbrechen",
    finalEvaluationDialogConfirm: "Bewertung finalisieren",
    finalEvaluationReadbackError: "Das finale Ergebnis konnte nicht vom Server geladen werden.",
    finalEvaluationRecovered: "Der Server enthält bereits ein finales Ergebnis. Der gespeicherte Stand wurde geladen.",
    finalEvaluationHistoryTitle: "Bewertungshistorie",
    finalEvaluationHistoryEmpty: "Für diese Bewertung wurde noch kein finales Ergebnis gespeichert.",
    latestFinalResult: "Aktuellster Stand",
    historicalFinalResult: "Historischer Stand",
    criterionBreakdown: "Kriterienaufschlüsselung",
    calculationDetails: "Berechnungsdetails",
    scoreScale: "Skala",
    rawScore: "Rohscore",
    roundedScore: "Gerundeter Score",
    weightingVersion: "Gewichtungsversion",
    weightingVersionMissing: "Für die Gewichtung ist noch keine eigene Version gespeichert.",
    snapshotReference: "Snapshot-Referenz",
    finalizedAt: "Finalisiert am",
    calculatedAt: "Berechnet am",
    criterionRawValue: "Rohwert",
    criterionPoints: "Punkte",
    criterionWeightedContribution: "Gewichteter Beitrag",
    scoringBand: "Bewertungsband",
    immutableResult: "Unveränderliche Bewertung",
    finalCriterionStatusLabel: (status: string) => ({
      computed: "Berechnet",
      blocked: "Blockiert",
      ignored_inactive: "Inaktiv"
    })[status] ?? "Nicht verfügbar",
    finalEvaluationFailureLabel: (code: string) => ({
      customer_value_score_not_ready: "Die Bewertungslogik ist noch nicht bereit für die Finalisierung.",
      scoring_not_ready: "Die Bewertungslogik ist noch nicht bereit für die Finalisierung.",
      missing_confirmed_weights: "Eine bestätigte Gewichtung fehlt.",
      missing_confirmed_scoring_logic: "Bestätigte Bewertungslogik fehlt.",
      invalid_segment_configuration: "Die Segmentlogik ist nicht gültig.",
      calculation_failed: "Der Server konnte das Ergebnis nicht berechnen.",
      company_evaluation_not_ready: "Diese Unternehmensbewertung ist noch nicht bereit für die Finalisierung.",
      evaluation_not_ready: "Diese Unternehmensbewertung ist noch nicht bereit für die Finalisierung.",
      company_evaluation_already_finalized: "Diese Unternehmensbewertung wurde bereits finalisiert.",
      evaluation_already_finalized: "Diese Unternehmensbewertung wurde bereits finalisiert.",
      forbidden: "Für die Finalisierung fehlt die Berechtigung.",
      unauthenticated: "Bitte melden Sie sich erneut an.",
      assessment_persistence_failed: "Der finale Snapshot konnte nicht gespeichert werden.",
      final_result_snapshot_exists: "Ein finales Ergebnis ist bereits gespeichert."
    })[code] ?? "Die Finalisierung konnte nicht sicher abgeschlossen werden.",
    finalHelp: {
      score: {
        title: "Customer-Value-Score",
        explanation: "Der gespeicherte Score zeigt den serverseitig berechneten Kundenwert auf der Skala von 0 bis 100.",
        impact: "Er ordnet den Kunden dem bestätigten Segment zu.",
        example: "Beispiel: 84,2 kann einen Kunden einem hohen Wertsegment zuordnen, wenn die gespeicherten Segmentgrenzen dazu passen."
      },
      segment: {
        title: "Segment",
        explanation: "Das Segment wird aus dem gespeicherten Score und der bestätigten Segmentlogik ermittelt.",
        impact: "Es übersetzt den Score in eine nutzbare Kundengruppe.",
        example: "Beispiel: Ein Top-Segment kann stärkeren Account-Fokus begründen."
      },
      points: {
        title: "Punkte",
        explanation: "Punkte entstehen aus dem bestätigten Bewertungsband dieses Kriteriums.",
        impact: "Sie bestimmen den Beitrag des Rohwerts, bevor die Gewichtung greift.",
        example: "Beispiel: Ein Wert kann in seinem Bereich 8 von 10 Punkten erhalten."
      },
      weight: {
        title: "Gewichtung",
        explanation: "Die Gewichtung legt fest, wie stark dieses Kriterium in den Gesamtscore eingeht.",
        impact: "Je höher die Gewichtung, desto stärker beeinflusst das Kriterium das Ergebnis.",
        example: "Beispiel: 20% Gewichtung bedeutet ein Fünftel Einfluss auf den Gesamtscore."
      },
      contribution: {
        title: "Gewichteter Beitrag",
        explanation: "Der gespeicherte Beitrag entsteht aus Punkten und Gewichtung.",
        impact: "Die Beiträge ergeben zusammen den Rohscore.",
        example: "Beispiel: 8 Punkte mit 20% Gewichtung ergeben 16 Scorepunkte Beitrag."
      },
      engine: {
        title: "Engine-Version",
        explanation: "Die Engine-Version benennt den serverseitigen Berechnungsvertrag dieses Ergebnisses.",
        impact: "So bleiben historische Ergebnisse nachvollziehbar, wenn sich Modelle weiterentwickeln.",
        example: "Beispiel: customer_value_scoring_v1 markiert die erste Berechnungslogik."
      },
      immutable: {
        title: "Unveränderliche Bewertung",
        explanation: "Ein finalisiertes Ergebnis wird als Snapshot gespeichert und nicht überschrieben.",
        impact: "Spätere Modelländerungen verändern diesen historischen Stand nicht.",
        example: "Beispiel: Eine spätere Neubewertung erzeugt einen weiteren Ergebnisstand."
      }
    },
    viewCalculation: "Customer Value Calculation ansehen",
    download: "Customer Value Calculation herunterladen",
    downloading: "Download wird vorbereitet ...",
    criteriaWeights: "Kriterien und Gewichtungen",
    weighting: "Gewichtung",
    scoreLogic: "Scorelogik",
    scoreLogicBody: "Der finale Customer-Value-Score reicht von 0 bis 100 und wird den untenstehenden kundendefinierten Scoresegmenten zugeordnet. Diese Segmente sind getrennt von den einzelnen Dimensionsscores.",
    segmentDefinitionTitle: "Customer-Value-Segmentierung",
    segmentDefinitionBody: "Definiere, wie Kunden anhand ihres berechneten Customer-Value-Gesamtscores den einzelnen Kundensegmenten zugeordnet werden. Der Gesamtscore reicht von 0 bis 100 Punkten. Bestätigte Segmentdefinitionen sind unveränderlich; Änderungen werden als neue Draft-Version vorbereitet und erst nach bewusster Bestätigung aktiv.",
    segmentAssignmentExample: (name: string, min: number, max: number) => `Kunden mit einem Customer-Value-Gesamtscore von ${min} bis ${max} Punkten werden automatisch ${name} zugeordnet.`,
    segmentPosition: (position: number, total: number) => `Segment ${position} von ${total}`,
    segmentVersion: "Segmentversion",
    createSegmentDraft: "Neue Segmentversion vorbereiten",
    saveSegmentDraft: "Segment-Draft speichern",
    confirmSegmentDraft: "Segmentversion bestätigen",
    segmentDraftPrepared: "Segment-Draft wurde vorbereitet.",
    segmentDraftSaved: "Segment-Draft wurde gespeichert.",
    segmentDraftConfirmed: "Segmentversion wurde bestätigt.",
    segmentDraftError: "Segmentdefinition konnte nicht gespeichert werden.",
    segmentName: "Segmentname",
    segmentDescriptionField: "Beschreibung",
    segmentRank: "Segmentreihenfolge",
    segmentMinScore: "Customer-Value-Gesamtscore von",
    segmentMaxScore: "Customer-Value-Gesamtscore bis",
    segmentBest: "Höchstes Segment",
    segmentWorst: "Niedrigstes Segment",
    segmentInvalid: "Bitte lösen Sie die Segmentfehler, bevor Sie die Version bestätigen.",
    configurationTitle: "Customer-Value-Scoring einrichten",
    configurationBody: "Bereiten Sie Modelltyp, Kriterien, Gewichtung und Bewertungslogik als Draft vor. Die Bestätigung bleibt in diesem Schritt gesperrt, bis die vollständige Modellversionsprüfung freigegeben ist.",
    completionSummaryTitle: "Customer-Value-Fertigstellung",
    completionStepsRemaining: (count: number) => count === 1 ? "1 Schritt bis zur Fertigstellung" : `${count} Schritte bis zur Fertigstellung`,
    completionReadyTitle: "Das Customer-Value-Modell ist vollständig konfiguriert.",
    completionReadyBody: "Sie können das Modell jetzt bewusst bestätigen.",
    completionFlowTitle: "Konfigurationsablauf",
    completionStepCriteria: "Kriterien festlegen",
    completionStepWeighting: "Gewichtung abschließen",
    completionStepScoring: "Bewertungslogik festlegen",
    completionStepSegments: "Kundensegmente definieren",
    completionStepPreview: "Vorschau prüfen",
    completionStepConfirm: "Modell bestätigen",
    completionActionCriteria: "Kriterien bearbeiten",
    completionActionWeighting: "Gewichtung bearbeiten",
    completionActionScoring: "Bewertungslogik bearbeiten",
    completionActionSegments: "Segmentierung bearbeiten",
    completionActionPreview: "Vorschau prüfen",
    completionActionConfirm: "Zur Bestätigung",
    confirmationBlockedTitle: "Du kannst das Modell noch nicht bestätigen.",
    confirmationUnavailableReason: "Schließen Sie zuerst die fehlenden Konfigurationsschritte ab.",
    modelTypeMissingReason: "Wählen Sie aus, ob dieses Modell bestehenden oder neuen Kundenwert bewertet.",
    weightingIncompleteReason: "Die Gewichtung ist noch nicht vollständig.",
    scoringIncompleteReason: (count: number) => count === 1 ? "Für 1 Kriterium fehlt noch die Bewertungslogik." : `Für ${count} Kriterien fehlt noch die Bewertungslogik.`,
    segmentIncompleteReason: "Die Segmentierung ist noch nicht vollständig.",
    previewIncompleteReason: "Prüfen Sie die Vorschau, bevor Sie das Modell bestätigen.",
    configurationLoading: "Scoring-Einrichtung wird geladen.",
    modelType: "Modelltyp",
    unclassifiedModel: "Modelltyp noch nicht ausgewählt",
    unsavedModelTypeDraft: "lokal ausgewählt, noch nicht gespeichert",
    existingCustomerValue: "Customer Value für Bestandskunden",
    newCustomerValue: "New Customer Value für neue Kunden und Prospects",
    modelReadinessStatus: "Modellbereitschaft",
    modelReadinessLabel: (status: CustomerValueModelReadinessStatus) => ({
      not_configured: "Nicht konfiguriert",
      in_progress: "In Bearbeitung",
      scoring_incomplete: "Bewertungslogik unvollständig",
      ready_for_review: "Bereit zur Prüfung",
      preview_ready: "Preview-bereit",
      confirmed: "Bestätigt"
    })[status],
    modelReadinessHelp: "Die Bereitschaft verbindet Modelltyp, Gewichtung, bestätigte Segmente und bestätigte Bewertungslogik der aktiven Kriterien.",
    criteriaConfigurationTitle: "Kriterienübersicht",
    categoriesTitle: "Kategorien für Kriterien",
    uncategorized: "Ohne Kategorie",
    categoryName: "Kategoriename",
    addCategory: "Kategorie hinzufügen",
    addCategoryInline: "+ Neue Kategorie",
    saveCategory: "Kategorie speichern",
    manageCategories: "Kategorien verwalten",
    advancedOrganization: "Erweiterte Organisation",
    advancedOrganizationBody: "Kategorien sind optional. Sie strukturieren das Modell, beeinflussen aber keine Punkte, keine Gewichtung und keine Nutzung in der Deal Win Engine.",
    deactivateCategory: "Kategorie deaktivieren",
    reactivateCategory: "Kategorie reaktivieren",
    deleteCategory: "Draft-Kategorie löschen",
    categoryDescription: "Beschreibung",
    createCategoryAndSelect: "Anlegen und auswählen",
    cancel: "Abbrechen",
    addCriterion: "Kriterium hinzufügen",
    addCriterionFlowTitle: "Atomares Kriterium anlegen",
    addCriterionFlowBody: "Definieren Sie genau eine eigenständig mess- und bewertbare Dimension. Umsatz, Marge, Aufwand und strategische Passung werden als eigene Kriterien geführt, wenn sie getrennt bewertet werden sollen.",
    addCriterionDraft: "Kriterien-Draft anlegen",
    descriptionSuggestion: "Beschreibungsvorschlag",
    requestDescriptionSuggestion: "Beschreibung vorschlagen",
    applyDescriptionSuggestion: "Vorschlag übernehmen",
    suggestionSourceSummary: "Quelle",
    suggestionConfidence: "Einordnung",
    confidenceStrongContext: "starker Workshop-Kontext",
    confidencePartialContext: "teilweiser Workshop-Kontext",
    confidenceGenericFallback: "allgemeiner Vorschlag",
    existingDescriptionKept: "Bestehende Beschreibungen werden nie automatisch überschrieben.",
    duplicateCriterion: "Duplizieren",
    deactivateCriterion: "Deaktivieren",
    reactivateCriterion: "Reaktivieren",
    deleteCriterion: "Draft löschen",
    openCriterion: "Konfiguration öffnen",
    closeCriterion: "Konfiguration schließen",
    replaceLegacyCriterion: "Durch einzelne Kriterien ersetzen",
    replacementFlowTitle: "Geführter Ersatz-Draft",
    replacementIntro: "Zerlegen Sie dieses Sammelkriterium in klare Einzelkriterien. Das bestätigte Kriterium bleibt unverändert, bis Sie den Scoring-Draft speichern.",
    replacementStepExplain: "Verstehen",
    replacementStepCriteria: "Kriterien",
    replacementStepWeights: "Gewichtung",
    replacementStepReview: "Prüfen",
    replacementCategorySuggestion: "Ersatzkategorie",
    replacementCriteriaSuggestion: "Vorgeschlagene Einzelkriterien",
    replacementApplyWeights: "Vorgeschlagene Aufteilung übernehmen",
    replacementSplitConfirmed: "Ich habe Ersatz und Gewichtungsaufteilung geprüft.",
    replacementDraftApplied: "Ersatz-Draft wurde lokal vorbereitet.",
    replacementDraftApply: "Ersatz-Draft vorbereiten",
    replacementDraftCancel: "Ersetzung abbrechen",
    replacementDraftBlocked: "Bitte prüfen und bestätigen Sie die Gewichtungsaufteilung, bevor der Ersatz-Draft vorbereitet wird.",
    replacementWeightSummary: (total: number, expected: number) => `Ersatzgewichtung: ${total}% von ${expected}%`,
    replacementRevenuePotential: "Umsatzpotenzial",
    replacementMarginQuality: "Margenqualität",
    replacementDescriptionRevenue: "Erwartetes wiederkehrendes und einmaliges Umsatzpotenzial dieses Kunden.",
    replacementDescriptionMargin: "Erwartete Marge oder Deckungsbeitragsqualität dieses Kunden.",
    advancedSettings: "Erweiterte Einstellungen",
    fromValue: "Von",
    toValue: "Bis",
    crmTransparencyTitle: "CRM-Transparenzvertrag",
    crmTransparencyBody: "Ein späterer CRM-Rückschrieb soll Rohwert, Punkte, Gewichtung und gewichteten Beitrag je Kriterium zeigen. Kategorien bleiben organisatorisch und erhalten keinen eigenen Score.",
    criterionCreated: "Kriterien-Draft wurde angelegt.",
    criterionCreatedNamed: (name: string) => `Kriterium „${name}“ wurde als Draft angelegt und ist in der Liste sichtbar.`,
    criterionCreating: "Kriterien-Draft wird angelegt...",
    criterionDuplicateError: "Ein Kriterium mit diesem Namen besteht bereits. Bitte nutzen Sie einen eindeutigen Namen oder bearbeiten Sie den bestehenden Draft.",
    criterionWeightError: "Geben Sie eine Gewichtung zwischen 0 und 100 % ein. Die Gesamtgewichtung des Modells wird erst beim Speichern des Scoring-Drafts geprüft.",
    criterionBandsError: "Bitte vervollständigen Sie die Bewertungslogik für dieses Kriterium, bevor Sie es speichern.",
    criterionCreateError: "Kriterien-Draft konnte nicht angelegt werden.",
    criterionDeleted: "Kriterien-Draft wurde gelöscht.",
    categorySaved: "Kategorie wurde gespeichert.",
    categoryError: "Kategorie konnte nicht gespeichert werden.",
    replaceLegacyHint: "Erstellt nur einen Draft-Vorschlag. Es werden keine Aktivierung, Bestätigung, Punkte oder Gewichtungen automatisch verändert.",
    quantitativeSuggestion: "10 Wertebereiche vorschlagen",
    directRatingSuggestion: "Als direkte 1-10-Einschätzung konfigurieren",
    directRatingHelp: "Nutzen Sie dies für fachliche Einschätzungen, bei denen der Nutzer bewusst 1 bis 10 Punkte vergibt. Die gespeicherten Bewertungsbänder ordnen jeden Punkt demselben Eingabewert zu.",
    quantitativeFieldsOnly: "Zahlenfelder erscheinen nur bei Kriterien mit Zahlenwert.",
    activeWeightDifference: (total: number) => total === 100
      ? "Die Gewichtung ergibt exakt 100 %."
      : total > 100
        ? `${Math.abs(100 - total)} Prozentpunkte zu viel.`
        : `${Math.abs(100 - total)} Prozentpunkte fehlen.`,
    activeWeightSummary: "Aktive Gewichtung",
    activeWeightAllowed: "Erlaubt: 100 %",
    activeWeightTooHigh: (difference: number) => `Reduzieren Sie die Gewichtungen aktiver Kriterien um ${difference} %, bevor der Scoring-Draft gespeichert werden kann.`,
    activeWeightTooLow: (difference: number) => `Ergänzen Sie ${difference} % bei aktiven Kriterien, bevor der Scoring-Draft gespeichert werden kann.`,
    activeWeightComplete: "Die aktiven Kriterien ergeben exakt 100 %. Der Scoring-Draft kann gespeichert werden.",
    activeWeightDraftIncomplete: "Einzelne Kriterien können bereits als Draft gespeichert werden. Der Scoring-Draft kann erst bei exakt 100 % abgeschlossen werden.",
    saveBlockedWeight: "Speichern ist blockiert, bis die aktiven Kriterien exakt 100 % ergeben.",
    noAutomaticRedistribution: "Gewichtungen werden nie automatisch umverteilt.",
    scoringConfigurationTitle: "Bewertungslogik",
    editNewScoringDraft: "Neue Version bearbeiten",
    previewTitle: "Preview",
    versionsTitle: "Versionen",
    versionSummary: (active: number, total: number) => `${active} aktiv · ${total} gesamt`,
    criterionConfiguration: "Kriterium konfigurieren",
    configurationStatus: "Konfigurationsstatus",
    activeWeightTotal: (total: number) => `Aktive Gewichtung: ${total}%`,
    configurationInvalid: "Bitte lösen Sie die Konfigurationsfehler, bevor Sie den Draft speichern.",
    saveConfigurationDraft: "Scoring-Draft speichern",
    configurationDraftSaved: "Scoring-Draft wurde gespeichert.",
    configurationDraftError: "Scoring-Draft konnte nicht gespeichert werden.",
    runConfigurationPreview: "Preview berechnen",
    configurationPreviewError: "Preview konnte nicht berechnet werden.",
    confirmConfiguration: "Bewertungslogik bestätigen",
    confirmConfigurationDisabled: "Bewertungslogik vor der Bestätigung vervollständigen",
    configurationConfirmed: "Bewertungslogik ist bestätigt.",
    configurationConfirmError: "Bewertungslogik konnte nicht bestätigt werden.",
    scoringIncompleteExplanation: "Das Modell ist vorhanden, aber aktive Kriterien benötigen noch bestätigte Bewertungslogik, bevor finale Scores berechnet werden können.",
    criteriaRequiringScoring: "Kriterien mit fehlender Bewertungslogik",
    scoringStatusTodo: "Noch zu konfigurieren",
    scoringStatusDraft: "Entwurf",
    scoringStatusReady: "Bereit",
    configureCriterionEvaluation: "Bewertung konfigurieren",
    weightingRequiredExplanation: "Die Gewichtung aller aktiven Kriterien muss zusammen 100 % ergeben.",
    currentWeighting: "Aktuell",
    requiredWeighting: "Erforderlich",
    evaluationType: "Bewertungstyp",
    valueType: "Wertetyp",
    scoringDirection: "Bewertungsrichtung",
    sourceStrategy: "Datenquelle",
    requiredField: "Pflichtfeld",
    activeField: "Aktiv",
    rank: "Reihenfolge",
    points: "Punkte",
    minValue: "Minimum",
    maxValue: "Maximum",
    openLower: "Nach unten offen",
    openUpper: "Nach oben offen",
    minInclusive: "Untergrenze inklusive",
    maxInclusive: "Obergrenze inklusive",
    bandPreview: "Lesbare Vorschau",
    optionValue: "Optionswert",
    optionLabel: "Optionslabel",
    optionLabelEn: "Englisches Label optional",
    booleanTrue: "Ja",
    booleanFalse: "Nein",
    addOption: "Option hinzufügen",
    previewInput: "Testwert",
    previewDirectRatingInput: "Direkte 1-10-Einschätzung",
    previewSelectOption: "Option auswählen",
    previewBooleanInput: "Ja/Nein-Testwert",
    previewConfigureFirst: "Bewertungslogik zuerst konfigurieren",
    previewScore: "Preview-Score",
    previewSegment: "Preview-Segment",
    previewMissing: "Fehlende Eingaben",
    previewMissingScoringLogic: "Fehlende Bewertungslogik",
    previewNotComputable: "Nicht berechenbare Kriterien",
    previewAffectedCriteria: "Betroffene Kriterien",
    previewCounterHelp: "Fehlende Eingaben bedeutet: Es fehlt ein Testwert. Fehlende Bewertungslogik bedeutet: Es gibt noch keine bestätigte Scoring-Konfiguration. Nicht berechenbar bedeutet: Eingabe und Bewertungslogik sind vorhanden, aber es konnte kein passendes Ergebnis ermittelt werden.",
    previewAllInputsPresentConfigurationMissing: "Alle Testwerte sind vorhanden, aber die Preview kann die gespeicherte Bewertungslogik noch nicht verwenden.",
    previewErrors: "Preview-Hinweise",
    previewIssueLabel: (code: string) => ({
      missing_input: "Mindestens ein Testwert fehlt.",
      scoring_configuration_missing: "Für mindestens ein Kriterium konnte keine Bewertungslogik geladen werden.",
      weighted_score_missing_criterion_score: "Mindestens ein Kriterium konnte nicht berechnet werden.",
      weighted_score_no_active_criteria: "Für die Preview ist kein aktives Kriterium verfügbar.",
      weighted_score_invalid_weight_total: "Die aktiven Kriterien müssen exakt 100 % Gewichtung ergeben.",
      invalid_weight: "Mindestens ein aktives Kriterium hat eine ungültige Gewichtung.",
      criterion_value_unmatched: "Mindestens ein Testwert passt zu keinem Wertebereich."
    })[code] ?? "Preview-Hinweis",
    calculatedUnavailable: "Berechnete Kriterien werden transparent angezeigt, können in diesem Schritt aber noch nicht konfiguriert werden.",
    legacyNotice: "Für dieses Legacy-Kriterium ist noch keine strukturierte Bewertungslogik hinterlegt. Konfigurieren Sie die Bewertungslogik hier oder bereiten Sie separate Einzelkriterien vor.",
    configureScoringLogic: "Bewertungslogik konfigurieren",
    draftOnly: "Nur Draft. Eine neue produktive Scoring-Version kann hier bewusst noch nicht bestätigt werden.",
    atomicSaveNote: "Kriterium speichern schreibt Grunddaten, Kategorie, Gewichtung und Bewertungslogik gemeinsam als Draft.",
    incompleteBands: "Bitte vervollständigen Sie die Bewertungslogik, bevor Sie speichern.",
    missingBandConfiguration: "Die Bewertungslogik ist noch unvollständig.",
    notAvailableYet: "Diese Funktion ist noch nicht verfügbar.",
    bandUntil: (value: string) => `bis ${value}`,
    bandFrom: (value: string) => `ab ${value}`,
    bandBetween: (min: string, max: string) => `${min} bis ${max}`,
    bandAny: "jeder Wert",
    reviewPerspectives: "Fehlende Perspektiven prüfen",
    reviewPerspectivesBody: "Prüfen Sie, ob alle relevanten fachlichen Perspektiven eingebunden wurden. Für Marge, Deckungsbeitrag, Zahlungsqualität und strategische Relevanz ist häufig eine Geschäftsleitungs-, Finance- oder Commercial-Perspektive erforderlich.",
    submittedPerspectives: "Eingereichte Perspektiven",
    openPerspectives: "Offene Perspektiven",
    recommendedPerspectives: "Optional empfohlene Perspektiven",
    executiveMissing: "Geschäftsleitungsperspektive fehlt noch.",
    financeMissing: "Finance- oder Commercial-Perspektive fehlt noch.",
    addPerspective: "Perspektive hinzufügen",
    inviteExecutive: "Geschäftsleitungsperspektive einladen",
    answerExecutive: "Geschäftsleitungsperspektive beantworten",
    executiveExists: "Geschäftsleitungsperspektive ist für diesen Workspace bereits vorhanden.",
    inviteFinance: "Finance-Perspektive einladen",
    requestAdditional: "Weitere Perspektive einholen",
    reopenValueRound: "Value-Runde erneut öffnen",
    updateEvaluation: "Auswertung aktualisieren",
    updateEvaluationNote: "Bestehende Ergebnisse bleiben erhalten. Eine Aktualisierung der Auswertung ist eine bewusste nächste Aktion und passiert nicht still.",
    organizationalOnly: "Nur organisatorische Verantwortung",
    functionalPerspective: "Fachliche Perspektive",
    votingPerspective: "Stimmberechtigte Perspektive",
    continueWithoutExecutive: "Ohne Geschäftsleitungsperspektive fortfahren",
    continueWithoutFinance: "Ohne Finance-Perspektive fortfahren",
    missingAcknowledged: "Fehlende Perspektiven wurden bestätigt.",
    acknowledgeError: "Fehlende Perspektiven konnten nicht bestätigt werden.",
    valueRoundReopened: "Value-Runde wurde für weitere Perspektiven erneut geöffnet.",
    valueRoundReopenError: "Value-Runde konnte nicht erneut geöffnet werden.",
    perspectiveLabels: {
      department: "Abteilung",
      executive: "Geschäftsleitung",
      finance: "Finance / Controlling",
      commercial: "Commercial / Revenue",
      operations: "Operations / Delivery",
      other: "Weitere Perspektive"
    },
    fromTo: (min: number, max: number) => `${min.toFixed(2)} bis ${max.toFixed(2)}`
  };
  const [overview, setOverview] = useState<RuntimeWorkspaceOverview>({ departments: [] });
  const [draftClusters, setDraftClusters] = useState<Record<string, RuntimeCustomerValueCluster>>({});
  const [weights, setWeights] = useState<Record<string, number>>({});
  const [segmentCount, setSegmentCount] = useState<number>(4);
  const [segmentDefinition, setSegmentDefinition] = useState<RuntimeCustomerValueSegmentDefinitionResponse | undefined>();
  const [segmentDraft, setSegmentDraft] = useState<RuntimeCustomerValueSegmentDefinition[]>([]);
  const [scoringConfiguration, setScoringConfiguration] = useState<RuntimeCustomerValueConfigurationResponse | undefined>();
  const [categoryDraft, setCategoryDraft] = useState<CustomerValueCriterionCategory[]>([]);
  const [criterionDraft, setCriterionDraft] = useState<RuntimeCustomerValueCriterionConfiguration[]>([]);
  const [bandDraft, setBandDraft] = useState<Record<string, CustomerValueCriterionScoringBand[]>>({});
  const [expandedCriterionIds, setExpandedCriterionIds] = useState<Set<string>>(new Set());
  const [inlineCategoryForCriterionId, setInlineCategoryForCriterionId] = useState<string | undefined>();
  const [inlineCategoryDraft, setInlineCategoryDraft] = useState({ name: "", description: "", rank: 1 });
  const [criterionAddDraft, setCriterionAddDraft] = useState<CriterionAddDraft | undefined>();
  const [criterionAddError, setCriterionAddError] = useState<string | undefined>();
  const [isCreatingCriterionDraft, setIsCreatingCriterionDraft] = useState(false);
  const [highlightedCriterionId, setHighlightedCriterionId] = useState<string | undefined>();
  const [legacyReplacementDraft, setLegacyReplacementDraft] = useState<LegacyReplacementDraft | undefined>();
  const [selectedModelTypeDraft, setSelectedModelTypeDraft] = useState<CustomerValueModelType | "">("");
  const [previewValues, setPreviewValues] = useState<Record<string, string>>({});
  const [configurationPreview, setConfigurationPreview] = useState<RuntimeCustomerValueConfigurationPreview | undefined>();
  const [isConfigurationLoading, setIsConfigurationLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isPreviewPanelOpen, setIsPreviewPanelOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isWorking, setIsWorking] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isFinalizingEvaluation, setIsFinalizingEvaluation] = useState(false);
  const [finalEvaluationDialogOpen, setFinalEvaluationDialogOpen] = useState(false);
  const [finalEvaluationResult, setFinalEvaluationResult] = useState<RuntimeCustomerValueFinalResult | undefined>();
  const [finalEvaluationHistory, setFinalEvaluationHistory] = useState<RuntimeCustomerValueFinalResult[]>([]);
  const [selectedFinalResultId, setSelectedFinalResultId] = useState<string | undefined>();
  const [finalEvaluationReadbackLoading, setFinalEvaluationReadbackLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [notice, setNotice] = useState<string | undefined>();
  const [criteriaRegenerated, setCriteriaRegenerated] = useState(false);
  const finalEvaluationConfirmButtonRef = useRef<HTMLButtonElement | null>(null);
  function safeTextList(value: unknown): string[] {
    if (Array.isArray(value)) {
      return value.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
    }

    if (typeof value === "string" && value.trim().length > 0) {
      try {
        const parsed = JSON.parse(value) as unknown;
        if (Array.isArray(parsed)) {
          return parsed.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
        }
      } catch {
        // Keep non-JSON legacy strings as a single display value.
      }

      return [value];
    }

    return [];
  }
  const loadWorkspace = useCallback(async () => {
    setIsLoading(true);
    setError(undefined);
    try {
      const nextOverview = await fetchRuntimeWorkspace();
      setOverview(nextOverview);
      const clusterDrafts = Object.fromEntries((nextOverview.clusters ?? []).map((cluster) => [cluster.id, cluster]));
      setDraftClusters(clusterDrafts);
      const nextWeights = nextOverview.weighting?.weights;
      if (nextWeights) {
        setWeights(nextWeights);
      } else if (nextOverview.departmentWeighting?.submittedDepartments) {
        setWeights(nextOverview.departmentWeighting.averages);
      } else {
        setWeights(Object.fromEntries((nextOverview.clusters ?? []).map((cluster) => [cluster.id, cluster.suggestedWeight])));
      }
      setSegmentCount(nextOverview.calculation?.segmentCount ?? 4);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : copy.loadError);
    } finally {
      setIsLoading(false);
    }
  }, [copy.loadError]);

  const loadFinalEvaluationReadback = useCallback(async (
    companyEvaluationId: string,
    options: { quietMissing?: boolean } = {}
  ): Promise<RuntimeCustomerValueFinalResult | undefined> => {
    setFinalEvaluationReadbackLoading(true);
    try {
      const [result, history] = await Promise.all([
        fetchRuntimeCustomerValueCompanyEvaluationResult(companyEvaluationId).catch((caught) => {
          const message = caught instanceof Error ? caught.message : "";
          if (options.quietMissing && message === "final_result_snapshot_not_found") return undefined;
          throw caught;
        }),
        fetchRuntimeCustomerValueCompanyEvaluationResults(companyEvaluationId)
      ]);
      setFinalEvaluationResult(result);
      setFinalEvaluationHistory(history);
      setSelectedFinalResultId((current) => {
        if (current && history.some((entry) => entry.id === current)) return current;
        return result?.id ?? history[0]?.id;
      });
      return result;
    } catch {
      setFinalEvaluationResult(undefined);
      setFinalEvaluationHistory([]);
      setSelectedFinalResultId(undefined);
      return undefined;
    } finally {
      setFinalEvaluationReadbackLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadWorkspace();
  }, [loadWorkspace]);

  useEffect(() => {
    const companyEvaluationId = overview.calculation?.finalEvaluation?.id;
    if (!companyEvaluationId) {
      setFinalEvaluationResult(undefined);
      setFinalEvaluationHistory([]);
      setSelectedFinalResultId(undefined);
      return;
    }
    void loadFinalEvaluationReadback(companyEvaluationId, { quietMissing: true });
  }, [loadFinalEvaluationReadback, overview.calculation?.finalEvaluation?.id]);

  useEffect(() => {
    if (!finalEvaluationDialogOpen) return;
    finalEvaluationConfirmButtonRef.current?.focus();
  }, [finalEvaluationDialogOpen]);

  useEffect(() => {
    const modelId = overview.calculation?.id;
    if (!modelId) {
      setSegmentDefinition(undefined);
      setSegmentDraft([]);
      return;
    }
    let isMounted = true;
    getRuntimeCustomerValueSegmentDefinition(modelId)
      .then((definition) => {
        if (!isMounted) return;
        setSegmentDefinition(definition);
        setSegmentDraft(definition.segments);
      })
      .catch(() => {
        if (!isMounted) return;
        setSegmentDefinition(undefined);
        setSegmentDraft([]);
      });
    return () => {
      isMounted = false;
    };
  }, [overview.calculation?.id]);

  useEffect(() => {
    const modelId = overview.calculation?.id;
    if (!modelId) {
      setScoringConfiguration(undefined);
      setCategoryDraft([]);
      setCriterionDraft([]);
      setBandDraft({});
      setExpandedCriterionIds(new Set());
      setLegacyReplacementDraft(undefined);
      setPreviewValues({});
      setConfigurationPreview(undefined);
      return;
    }
    let isMounted = true;
    setIsConfigurationLoading(true);
    getRuntimeCustomerValueConfiguration(modelId)
      .then((configuration) => {
        if (!isMounted) return;
        setScoringConfiguration(configuration);
        setCategoryDraft(configuration.categories ?? []);
        setCriterionDraft(configuration.criteria);
        setBandDraft(configuration.bandsByCriterionId);
        setSelectedModelTypeDraft(hasConfiguredCustomerValueModelType(configuration.model.modelType) ? configuration.model.modelType : "");
        setExpandedCriterionIds(new Set(configuration.criteria.slice(0, 1).map((criterion) => criterion.id)));
        setLegacyReplacementDraft(undefined);
        setConfigurationPreview(undefined);
      })
      .catch(() => {
        if (!isMounted) return;
        setScoringConfiguration(undefined);
        setCategoryDraft([]);
        setCriterionDraft([]);
        setBandDraft({});
        setSelectedModelTypeDraft("");
      })
      .finally(() => {
        if (!isMounted) return;
        setIsConfigurationLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [overview.calculation?.id]);

  const clusters = overview.clusters ?? [];
  const displayClusters = clusters.map((cluster) => resolveRuntimeClusterForLanguage(cluster, language));
  const displayCalculation = resolveCalculationForLanguage(overview.calculation, language);
  const calculationLanguageMismatch = Boolean(
    overview.calculation && !hasResolvedCalculationLanguage(overview.calculation, language)
  );
  const clusterLanguageMismatch = Boolean(clusters.length > 0 && !hasResolvedClusterLanguage(clusters, language));
  const translationIncomplete = clusterLanguageMismatch || calculationLanguageMismatch;
  const approvedClusters = clusters.filter((cluster) => cluster.ownerApproved);
  const weightableClusters = approvedClusters.length ? approvedClusters : clusters;
  const displayWeightableClusters = weightableClusters.map((cluster) => resolveRuntimeClusterForLanguage(cluster, language));
  const visibleDepartments = useMemo(() => dedupeExecutivePerspectives(overview.departments), [overview.departments]);
  const executivePerspective = useMemo(() => getExecutivePerspective(overview.departments), [overview.departments]);
  const ownerExecutivePerspective = overview.ownerPerspective?.perspectiveType === "executive" ? overview.ownerPerspective : undefined;
  const submittedValueInputs = visibleDepartments.filter((department) => department.valueInputStatus === "submitted").length;
  const canInviteExecutive = canInviteExecutivePerspective(overview.departments, overview.ownerPerspective);
  const perspectiveCoverage = useMemo(() => analyzePerspectiveCoverage(visibleDepartments), [visibleDepartments]);
  const recommendedPerspectiveTypes = perspectiveCoverage.recommendedPerspectiveTypes ?? [];
  const missingWeightingDepartments = overview.departmentWeighting?.missingDepartments ?? [];
  const allDepartmentWeightingsSubmitted = Boolean(
    overview.departmentWeighting &&
    overview.departmentWeighting.totalDepartments > 0 &&
    overview.departmentWeighting.submittedDepartments === overview.departmentWeighting.totalDepartments &&
    missingWeightingDepartments.length === 0
  );
  const criteriaAlreadyConfirmed = canWeightClusters(overview.process?.status) || clusters.some((cluster) => cluster.ownerApproved);
  const weightValidation = useMemo(() => {
    const relevantWeights = Object.fromEntries(weightableClusters.map((cluster) => [cluster.id, weights[cluster.id] ?? 0]));
    return validateWeightDistribution(relevantWeights, language);
  }, [language, weightableClusters, weights]);

  async function runAction(action: () => Promise<void>, fallbackMessage: string, options: { reloadWorkspace?: boolean } = {}) {
    setIsWorking(true);
    setError(undefined);
    setNotice(undefined);
    try {
      await action();
      if (options.reloadWorkspace !== false) {
        await loadWorkspace();
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : fallbackMessage);
    } finally {
      setIsWorking(false);
    }
  }

  function updateClusterDraft(clusterId: string, patch: Partial<RuntimeCustomerValueCluster>) {
    setDraftClusters((current) => ({
      ...current,
      [clusterId]: {
        ...current[clusterId],
        ...patch
      }
    }));
  }

  function updateSegmentDraft(segmentId: string, patch: Partial<RuntimeCustomerValueSegmentDefinition>) {
    setSegmentDraft((current) => current.map((segment) => segment.id === segmentId ? { ...segment, ...patch } : segment));
  }

  function activeWeightTotal(criteria = criterionDraft) {
    return criteria.filter((criterion) => criterion.active).reduce((sum, criterion) => sum + Number(criterion.weight || 0), 0);
  }

  function updateCriterionDraft(criterionId: string, patch: Partial<RuntimeCustomerValueCriterionConfiguration>) {
    setCriterionDraft((current) => current.map((criterion) => criterion.id === criterionId ? { ...criterion, ...patch } : criterion));
  }

  function updateCategoryDraft(categoryId: string, patch: Partial<CustomerValueCriterionCategory>) {
    setCategoryDraft((current) => current.map((category) => category.id === categoryId ? { ...category, ...patch } : category));
  }

  function toggleCriterionDetails(criterionId: string) {
    setExpandedCriterionIds((current) => {
      const next = new Set(current);
      if (next.has(criterionId)) next.delete(criterionId);
      else next.add(criterionId);
      return next;
    });
  }

  function formatCriterionValue(criterion: RuntimeCustomerValueCriterionConfiguration, value: number): string {
    if (criterion.unitType === "percentage" || criterion.valueType === "percentage") {
      return `${value.toLocaleString(language === "en" ? "en-US" : "de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %`;
    }
    if (criterion.unitType === "EUR" || criterion.valueType === "currency") {
      return value.toLocaleString(language === "en" ? "en-US" : "de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
    }
    const formatted = value.toLocaleString(language === "en" ? "en-US" : "de-DE", { maximumFractionDigits: 2 });
    if (criterion.unitType === "custom" && criterion.customUnit) return `${formatted} ${criterion.customUnit}`;
    return formatted;
  }

  function bandPreview(criterion: RuntimeCustomerValueCriterionConfiguration, band: CustomerValueCriterionScoringBand) {
    const hasMin = band.minValue !== null && band.minValue !== undefined;
    const hasMax = band.maxValue !== null && band.maxValue !== undefined;
    if (criterion.criterionType === "boolean") return band.booleanValue ? copy.booleanTrue : copy.booleanFalse;
    if (criterion.criterionType === "qualitative") return band.qualitativeLabelDe || band.qualitativeLabelEn || band.qualitativeOptionId || band.qualitativeValue || copy.notStored;
    if (!hasMin && !hasMax) return copy.bandAny;
    if (!hasMin && hasMax) return copy.bandUntil(formatCriterionValue(criterion, Number(band.maxValue)));
    if (hasMin && !hasMax) return copy.bandFrom(formatCriterionValue(criterion, Number(band.minValue)));
    return copy.bandBetween(formatCriterionValue(criterion, Number(band.minValue)), formatCriterionValue(criterion, Number(band.maxValue)));
  }

  function criterionScoringHasCompleteDraftBands(criterion: RuntimeCustomerValueCriterionConfiguration) {
    if (!criterion.active) return false;
    return validateCriterionScoringBandSet(criterion, bandsForCriterion(criterion)).confirmationAllowed;
  }

  function criterionScoringHasCompleteConfirmedBands(criterion: RuntimeCustomerValueCriterionConfiguration) {
    return hasCompleteConfirmedCriterionScoring(criterion, bandsForCriterion(criterion), { modelStatus: scoringConfiguration?.model.status });
  }

  function criterionScoringCanBeSaved(criterion: RuntimeCustomerValueCriterionConfiguration) {
    if (!criterion.active || criterion.scoringConfigurationStatus === "unconfigured" || criterion.scoringConfigurationStatus === "superseded") return true;
    if (criterion.scoringConfigurationStatus === "draft") {
      return validateDraftScoringBandsForPersistence(criterion, bandsForCriterion(criterion)).ok;
    }
    return criterionScoringHasCompleteDraftBands(criterion);
  }

  function criterionScoringIsConfirmed(criterion: RuntimeCustomerValueCriterionConfiguration) {
    return criterionScoringHasCompleteConfirmedBands(criterion);
  }

  function localizedCriterionTypeLabel(value: RuntimeCustomerValueCriterionConfiguration["criterionType"]) {
    return criterionLabel(CRITERION_TYPE_LABELS, value, language);
  }

  function helpContent(key: CustomerValueHelpKey) {
    const content = CUSTOMER_VALUE_HELP_TEXTS[key];
    return {
      title: content.title[language],
      explanation: content.explanation[language],
      impact: content.impact[language],
      example: content.example[language],
      warning: content.warning?.[language]
    };
  }

  function startLegacyReplacementDraft(criterion: RuntimeCustomerValueCriterionConfiguration) {
    const sourceWeight = Number(criterion.weight || 0);
    const firstWeight = Number((sourceWeight / 2).toFixed(2));
    const secondWeight = Number((sourceWeight - firstWeight).toFixed(2));
    setLegacyReplacementDraft({
      sourceCriterionId: criterion.id,
      step: "explain",
      splitConfirmed: false,
      criteria: [
        {
          localId: `replacement-${crypto.randomUUID()}`,
          name: copy.replacementRevenuePotential,
          description: copy.replacementDescriptionRevenue,
          valueType: "currency",
          unitType: "EUR",
          scoringDirection: "higher_is_better",
          weight: firstWeight
        },
        {
          localId: `replacement-${crypto.randomUUID()}`,
          name: copy.replacementMarginQuality,
          description: copy.replacementDescriptionMargin,
          valueType: "percentage",
          unitType: "percentage",
          scoringDirection: "higher_is_better",
          weight: secondWeight
        }
      ]
    });
  }

  function updateLegacyReplacementDraft(patch: Partial<LegacyReplacementDraft>) {
    setLegacyReplacementDraft((current) => current ? { ...current, ...patch } : current);
  }

  function updateLegacyReplacementCriterion(localId: string, patch: Partial<LegacyReplacementDraft["criteria"][number]>) {
    setLegacyReplacementDraft((current) => current ? {
      ...current,
      splitConfirmed: patch.weight === undefined ? current.splitConfirmed : false,
      criteria: current.criteria.map((criterion) => criterion.localId === localId ? { ...criterion, ...patch } : criterion)
    } : current);
  }

  function applySuggestedReplacementSplit() {
    setLegacyReplacementDraft((current) => {
      if (!current) return current;
      const source = criterionDraft.find((criterion) => criterion.id === current.sourceCriterionId);
      const sourceWeight = Number(source?.weight || 0);
      const firstWeight = Number((sourceWeight / current.criteria.length).toFixed(2));
      const weights = current.criteria.map((_, index) => index === current.criteria.length - 1
        ? Number((sourceWeight - firstWeight * (current.criteria.length - 1)).toFixed(2))
        : firstWeight);
      return {
        ...current,
        splitConfirmed: false,
        criteria: current.criteria.map((criterion, index) => ({ ...criterion, weight: weights[index] ?? 0 }))
      };
    });
  }

  function applyLegacyReplacementDraft() {
    if (!legacyReplacementDraft || !scoringConfiguration) return;
    const source = criterionDraft.find((criterion) => criterion.id === legacyReplacementDraft.sourceCriterionId);
    if (!source) return;
    const sourceWeight = Number(source.weight || 0);
    const replacementWeight = legacyReplacementDraft.criteria.reduce((sum, criterion) => sum + Number(criterion.weight || 0), 0);
    if (!legacyReplacementDraft.splitConfirmed || Number(replacementWeight.toFixed(2)) !== Number(sourceWeight.toFixed(2))) {
      setError(copy.replacementDraftBlocked);
      return;
    }
    const modelType = scoringConfiguration.model.modelType === "new_customer_value" ? "new_customer_value" : "existing_customer_value";
    const baseRank = Math.max(0, ...criterionDraft.map((criterion) => Number(criterion.rank || 0)));
    const replacementCriteria = legacyReplacementDraft.criteria.map((criterion, index): RuntimeCustomerValueCriterionConfiguration => ({
      id: criterion.localId,
      modelId: scoringConfiguration.model.id,
      modelType,
      categoryId: null,
      name: criterion.name,
      description: criterion.description,
      criterionType: "quantitative",
      valueType: criterion.valueType,
      unitType: criterion.unitType,
      weight: criterion.weight,
      scoringDirection: criterion.scoringDirection,
      required: true,
      active: true,
      rank: baseRank + index + 1,
      sourceStrategy: "manual",
      scoringConfigurationStatus: "draft"
    }));
    setCriterionDraft((current) => current
      .map((criterion) => criterion.id === source.id ? { ...criterion, active: false, scoringConfigurationStatus: "draft" as const } : criterion)
      .concat(replacementCriteria));
    setBandDraft((current) => ({
      ...current,
      ...Object.fromEntries(replacementCriteria.map((criterion) => [criterion.id, createTenPointBandSuggestion(criterion.id, criterion.scoringDirection)]))
    }));
    setExpandedCriterionIds((current) => {
      const next = new Set(current);
      replacementCriteria.forEach((criterion) => next.add(criterion.id));
      return next;
    });
    setLegacyReplacementDraft(undefined);
    setNotice(copy.replacementDraftApplied);
  }

  async function refreshConfigurationFromResponse(response: RuntimeCustomerValueConfigurationResponse) {
    setScoringConfiguration(response);
    setCategoryDraft(response.categories ?? []);
    setCriterionDraft(response.criteria);
    setBandDraft(response.bandsByCriterionId);
  }

  async function addCategoryDraft() {
    const modelId = overview.calculation?.id;
    if (!modelId) return;
    await runAction(async () => {
      const response = await createRuntimeCustomerValueCategory(modelId, {
        name: language === "en" ? "New category" : "Neue Kategorie",
        rank: categoryDraft.length + 1,
        active: true
      });
      await refreshConfigurationFromResponse(response);
      setNotice(copy.categorySaved);
    }, copy.categoryError, { reloadWorkspace: false });
  }

  async function saveCategoryDraft(category: CustomerValueCriterionCategory) {
    const modelId = overview.calculation?.id;
    if (!modelId) return;
    await runAction(async () => {
      const response = await updateRuntimeCustomerValueCategory(modelId, category);
      await refreshConfigurationFromResponse(response);
      setNotice(copy.categorySaved);
    }, copy.categoryError, { reloadWorkspace: false });
  }

  async function deactivateCategoryDraft(category: CustomerValueCriterionCategory) {
    const modelId = overview.calculation?.id;
    if (!modelId) return;
    await runAction(async () => {
      const response = await updateRuntimeCustomerValueCategory(modelId, { ...category, active: false });
      await refreshConfigurationFromResponse(response);
      setNotice(copy.categorySaved);
    }, copy.categoryError, { reloadWorkspace: false });
  }

  async function deleteCategoryDraft(categoryId: string) {
    const modelId = overview.calculation?.id;
    if (!modelId) return;
    await runAction(async () => {
      const response = await deleteRuntimeCustomerValueCategory(modelId, categoryId);
      await refreshConfigurationFromResponse(response);
      setNotice(copy.categorySaved);
    }, copy.categoryError, { reloadWorkspace: false });
  }

  async function createInlineCategoryForCriterion(criterionId: string) {
    const modelId = overview.calculation?.id;
    const name = inlineCategoryDraft.name.trim();
    if (!modelId || !name) return;
    await runAction(async () => {
      const response = await createRuntimeCustomerValueCategory(modelId, {
        name,
        description: inlineCategoryDraft.description,
        rank: Math.max(1, Number(inlineCategoryDraft.rank || categoryDraft.length + 1)),
        active: true
      });
      const createdCategory = response.categories.find((category) => category.name === name);
      await refreshConfigurationFromResponse(response);
      if (createdCategory) {
        setCriterionDraft((current) => current.map((criterion) => criterion.id === criterionId ? { ...criterion, categoryId: createdCategory.id } : criterion));
      }
      setInlineCategoryForCriterionId(undefined);
      setInlineCategoryDraft({ name: "", description: "", rank: response.categories.length + 1 });
      setNotice(copy.categorySaved);
    }, copy.categoryError, { reloadWorkspace: false });
  }

  function startCriterionAddFlow() {
    setCriterionAddError(undefined);
    setHighlightedCriterionId(undefined);
    setCriterionAddDraft({
      name: "",
      description: "",
      criterionType: "quantitative",
      valueType: "currency",
      unitType: "EUR",
      scoringDirection: "higher_is_better",
      weight: 0,
      required: true
    });
  }

  function updateCriterionAddDraft(patch: Partial<CriterionAddDraft>) {
    setCriterionAddError(undefined);
    setCriterionAddDraft((current) => current ? { ...current, ...patch } : current);
  }

  function criterionDraftMutationMessage(caught: unknown): string {
    const message = caught instanceof Error ? caught.message : "";
    if (/CRITERION_NAME_DUPLICATE|criterion_name_duplicate/i.test(message)) return copy.criterionDuplicateError;
    if (/INVALID_CRITERION_WEIGHT|criterion_weight_invalid|invalid_weight/i.test(message)) return copy.criterionWeightError;
    if (/INVALID_SCORING_BANDS|criterion_scoring_bands_invalid|scoring_band_/i.test(message)) return copy.criterionBandsError;
    return copy.criterionCreateError;
  }

  async function suggestCriterionDescription() {
    const modelId = overview.calculation?.id;
    if (!modelId || !criterionAddDraft || !criterionAddDraft.name.trim() || !scoringConfiguration) return;
    await runAction(async () => {
      const suggestion = await requestRuntimeCustomerValueCriterionDescriptionSuggestion(modelId, {
        criterionName: criterionAddDraft.name,
        modelType: scoringConfiguration.model.modelType === "new_customer_value" ? "new_customer_value" : "existing_customer_value",
        evaluationType: criterionAddDraft.criterionType,
        valueType: criterionAddDraft.valueType,
        unitType: criterionAddDraft.unitType,
        scoringDirection: criterionAddDraft.scoringDirection,
        existingDescription: criterionAddDraft.description,
        language
      });
      setCriterionAddDraft((current) => current ? { ...current, suggestion } : current);
    }, copy.configurationDraftError, { reloadWorkspace: false });
  }

  async function saveCriterionAddDraft() {
    const modelId = overview.calculation?.id;
    if (!modelId || !criterionAddDraft || !criterionAddDraft.name.trim()) return;
    const draft = criterionAddDraft;
    const normalizedName = normalizeCustomerValueCriterionName(draft.name);
    const duplicate = criterionDraft.some((criterion) => criterion.active && normalizeCustomerValueCriterionName(criterion.name) === normalizedName);
    if (duplicate) {
      setCriterionAddError(copy.criterionDuplicateError);
      return;
    }
    if (!Number.isFinite(draft.weight) || draft.weight < 0 || draft.weight > 100) {
      setCriterionAddError(copy.criterionWeightError);
      return;
    }
    setIsCreatingCriterionDraft(true);
    setIsWorking(true);
    setError(undefined);
    setNotice(undefined);
    setCriterionAddError(undefined);
    try {
      const scoringBands = draft.criterionType === "quantitative"
        ? createTenPointBandSuggestion("__new_criterion__", draft.scoringDirection)
        : [];
      const response = await createRuntimeCustomerValueCriterion(modelId, {
        name: draft.name.trim(),
        description: draft.description,
        criterionType: draft.criterionType,
        valueType: draft.valueType,
        unitType: draft.unitType,
        weight: draft.weight,
        scoringDirection: draft.scoringDirection,
        rank: criterionDraft.length + 1,
        active: true,
        required: draft.required,
        sourceStrategy: "manual",
        scoringConfigurationStatus: "draft",
        scoringBands
      });
      await refreshConfigurationFromResponse(response);
      const createdCriterion = response.criteria.find((criterion) => normalizeCustomerValueCriterionName(criterion.name) === normalizedName && criterion.scoringConfigurationStatus === "draft")
        ?? response.criteria.slice(-1)[0];
      setExpandedCriterionIds(new Set(createdCriterion ? [createdCriterion.id] : []));
      setHighlightedCriterionId(createdCriterion?.id);
      setCriterionAddDraft(undefined);
      setNotice(copy.criterionCreatedNamed(draft.name.trim()));
    } catch (caught) {
      setCriterionAddError(criterionDraftMutationMessage(caught));
    } finally {
      setIsCreatingCriterionDraft(false);
      setIsWorking(false);
    }
  }

  async function duplicateCriterionDraft(criterion: RuntimeCustomerValueCriterionConfiguration) {
    const modelId = overview.calculation?.id;
    if (!modelId) return;
    await runAction(async () => {
      const response = await duplicateRuntimeCustomerValueCriterion(modelId, criterion.id, {
        name: `${criterion.name} Draft`
      });
      await refreshConfigurationFromResponse(response);
      setNotice(copy.criterionCreated);
    }, copy.configurationDraftError, { reloadWorkspace: false });
  }

  async function setCriterionActiveDraft(criterionId: string, active: boolean) {
    const modelId = overview.calculation?.id;
    if (!modelId) return;
    await runAction(async () => {
      const response = await setRuntimeCustomerValueCriterionActive(modelId, criterionId, active);
      await refreshConfigurationFromResponse(response);
      setNotice(copy.configurationDraftSaved);
    }, copy.configurationDraftError, { reloadWorkspace: false });
  }

  async function deleteCriterionDraft(criterionId: string) {
    const modelId = overview.calculation?.id;
    if (!modelId) return;
    await runAction(async () => {
      const response = await deleteRuntimeCustomerValueCriterion(modelId, criterionId);
      await refreshConfigurationFromResponse(response);
      setNotice(copy.criterionDeleted);
    }, copy.configurationDraftError);
  }

  function updateConfigurationModelType(modelType: CustomerValueModelType) {
    setSelectedModelTypeDraft(modelType);
    setScoringConfiguration((current) => current ? {
      ...current,
      model: {
        ...current.model,
        modelType
      }
    } : current);
    setCriterionDraft((current) => current.map((criterion) => ({ ...criterion, modelType })));
  }

  function defaultQuantitativeBands(criterionId: string): CustomerValueCriterionScoringBand[] {
    return Array.from({ length: 10 }, (_, index) => {
      const point = index + 1;
      return {
        id: `${criterionId}-draft-point-${point}`,
        criterionId,
        version: 1,
        points: point,
        minValue: point === 1 ? null : (point - 1) * 10,
        maxValue: point === 10 ? null : point * 10,
        minInclusive: true,
        maxInclusive: point === 10,
        rank: point,
        status: "draft",
        active: true
      };
    });
  }

  function defaultDirectRatingBands(criterionId: string): CustomerValueCriterionScoringBand[] {
    return Array.from({ length: 10 }, (_, index) => {
      const point = index + 1;
      return {
        id: `${criterionId}-direct-rating-${point}`,
        criterionId,
        version: 1,
        points: point,
        minValue: point,
        maxValue: point,
        minInclusive: true,
        maxInclusive: true,
        rank: point,
        status: "draft",
        active: true
      };
    });
  }

  function criterionUsesNumericBands(criterion: RuntimeCustomerValueCriterionConfiguration) {
    const bands = bandsForCriterion(criterion);
    return criterion.criterionType === "quantitative" || (criterion.criterionType === "legacy" && bands.some((band) => band.minValue !== null || band.maxValue !== null));
  }

  function criterionUsesQualitativeBands(criterion: RuntimeCustomerValueCriterionConfiguration) {
    const bands = bandsForCriterion(criterion);
    return criterion.criterionType === "qualitative" || (criterion.criterionType === "legacy" && bands.some((band) => band.qualitativeOptionId ?? band.qualitativeValue));
  }

  function criterionUsesBooleanBands(criterion: RuntimeCustomerValueCriterionConfiguration) {
    const bands = bandsForCriterion(criterion);
    return criterion.criterionType === "boolean" || (criterion.criterionType === "legacy" && bands.some((band) => band.booleanValue !== null && band.booleanValue !== undefined));
  }

  function criterionPreviewInputKind(criterion: RuntimeCustomerValueCriterionConfiguration): "number" | "choice" | "boolean" | "unconfigured" {
    if (criterionUsesBooleanBands(criterion)) return "boolean";
    if (criterionUsesQualitativeBands(criterion)) return "choice";
    if (criterionUsesNumericBands(criterion)) return "number";
    return "unconfigured";
  }

  function previewInputLabel(criterion: RuntimeCustomerValueCriterionConfiguration): string {
    const kind = criterionPreviewInputKind(criterion);
    if (kind === "boolean") return copy.previewBooleanInput;
    if (kind === "choice") return copy.previewSelectOption;
    if (criterion.criterionType === "legacy" && kind === "number") return copy.previewDirectRatingInput;
    return copy.previewInput;
  }

  function configureLegacyDirectRatingDraft(criterion: RuntimeCustomerValueCriterionConfiguration) {
    setBandDraft((current) => ({ ...current, [criterion.id]: defaultDirectRatingBands(criterion.id) }));
    updateCriterionDraft(criterion.id, {
      valueType: "integer",
      unitType: "none",
      scoringDirection: "manual_bands",
      scoringConfigurationStatus: "draft"
    });
    setExpandedCriterionIds((current) => {
      const next = new Set(current);
      next.add(criterion.id);
      return next;
    });
  }

  function bandsForCriterion(criterion: RuntimeCustomerValueCriterionConfiguration): CustomerValueCriterionScoringBand[] {
    const bands = bandDraft[criterion.id] ?? [];
    if (criterion.criterionType === "quantitative") {
      if (bands.length > 0) {
        const draftBands = bands.filter((band) => band.active && band.status === "draft");
        const editableBands = draftBands.length > 0 ? draftBands : bands.filter((band) => band.active && band.status === "confirmed");
        return (editableBands.length > 0 ? editableBands : bands).sort((left, right) => left.rank - right.rank);
      }
      return defaultQuantitativeBands(criterion.id);
    }
    if (criterion.criterionType === "boolean") {
      if (bands.length > 0) return bands;
      return [
        {
          id: `${criterion.id}-draft-false`,
          criterionId: criterion.id,
          version: 1,
          points: 1,
          booleanValue: false,
          minInclusive: true,
          maxInclusive: true,
          rank: 1,
          status: "draft",
          active: true
        },
        {
          id: `${criterion.id}-draft-true`,
          criterionId: criterion.id,
          version: 1,
          points: 10,
          booleanValue: true,
          minInclusive: true,
          maxInclusive: true,
          rank: 2,
          status: "draft",
          active: true
        }
      ];
    }
    return bands;
  }

  function parseOptionalBandNumber(value: string): number | null {
    if (value === "") return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  function startNewScoringDraftVersion(criterion: RuntimeCustomerValueCriterionConfiguration) {
    const currentBands = bandDraft[criterion.id] ?? [];
    const sourceBands = currentBands.filter((band) => band.active && band.status === "confirmed");
    const nextVersion = Math.max(1, ...currentBands.map((band) => Number(band.version || 1))) + 1;
    const draftBands = (sourceBands.length > 0 ? sourceBands : bandsForCriterion(criterion)).map((band, index) => ({
      ...band,
      id: `${criterion.id}-draft-v${nextVersion}-${index + 1}`,
      version: nextVersion,
      status: "draft" as const
    }));
    setBandDraft((current) => ({ ...current, [criterion.id]: [...currentBands.filter((band) => band.status !== "draft"), ...draftBands] }));
    updateCriterionDraft(criterion.id, { scoringConfigurationStatus: "draft" });
  }

  function updateBandDraft(criterionId: string, bandId: string, patch: Partial<CustomerValueCriterionScoringBand>) {
    setBandDraft((current) => {
      const criterion = criterionDraft.find((entry) => entry.id === criterionId);
      const bands = criterion ? bandsForCriterion(criterion) : current[criterionId] ?? [];
      return {
        ...current,
        [criterionId]: bands.map((band) => band.id === bandId ? { ...band, ...patch } : band)
      };
    });
  }

  function addQualitativeOption(criterion: RuntimeCustomerValueCriterionConfiguration) {
    setBandDraft((current) => {
      const bands = current[criterion.id] ?? [];
      const nextRank = bands.length + 1;
      const nextBand: CustomerValueCriterionScoringBand = {
        id: `${criterion.id}-draft-option-${crypto.randomUUID()}`,
        criterionId: criterion.id,
        version: 1,
        points: Math.min(10, Math.max(1, nextRank)),
        qualitativeValue: `option_${nextRank}`,
        qualitativeOptionId: `option_${nextRank}`,
        qualitativeLabelDe: "",
        qualitativeLabelEn: "",
        minInclusive: true,
        maxInclusive: true,
        rank: nextRank,
        status: "draft",
        active: true
      };
      return { ...current, [criterion.id]: [...bands, nextBand] };
    });
  }

  async function prepareSegmentDraft() {
    const modelId = overview.calculation?.id;
    if (!modelId) return;
    await runAction(async () => {
      const definition = await createRuntimeCustomerValueSegmentDraft(modelId, crypto.randomUUID());
      setSegmentDefinition(definition);
      setSegmentDraft(definition.segments);
      setNotice(copy.segmentDraftPrepared);
    }, copy.segmentDraftError);
  }

  async function saveSegmentDraft() {
    const modelId = overview.calculation?.id;
    if (!modelId || !segmentDefinition) return;
    await runAction(async () => {
      const definition = await updateRuntimeCustomerValueSegmentDraft(modelId, {
        expectedSegmentVersion: segmentDefinition.segmentVersion,
        segments: deriveCustomerValueSegmentOrder(segmentDraft)
      });
      setSegmentDefinition(definition);
      setSegmentDraft(definition.segments);
      setNotice(copy.segmentDraftSaved);
    }, copy.segmentDraftError);
  }

  async function confirmSegmentDraft() {
    const modelId = overview.calculation?.id;
    if (!modelId || !segmentDefinition) return;
    await runAction(async () => {
      const definition = await confirmRuntimeCustomerValueSegmentDraft(modelId, segmentDefinition.segmentVersion);
      setSegmentDefinition(definition);
      setSegmentDraft(definition.segments);
      setNotice(copy.segmentDraftConfirmed);
    }, copy.segmentDraftError);
  }

  async function saveScoringConfigurationDraft() {
    const modelId = overview.calculation?.id;
    if (!modelId || !scoringConfiguration) return;
    const modelType = selectedModelTypeDraft || (hasConfiguredCustomerValueModelType(scoringConfiguration.model.modelType) ? scoringConfiguration.model.modelType : undefined);
    if (!modelType) {
      setError(copy.unclassifiedModel);
      return;
    }
    const weightValidationResult = validateActiveCriteriaWeightTotal(criterionDraft);
    if (!weightValidationResult.ok) {
      setError(`${copy.saveBlockedWeight} ${copy.activeWeightTotal(weightValidationResult.totalWeight)}`);
      return;
    }
    const incompleteCriteria = criterionDraft.filter((criterion) => !criterionScoringCanBeSaved(criterion));
    if (incompleteCriteria.length > 0) {
      setError(`${copy.incompleteBands} ${incompleteCriteria.map((criterion) => criterion.name).join(", ")}`);
      return;
    }
    await runAction(async () => {
      const response = await updateRuntimeCustomerValueConfiguration(modelId, {
        modelType,
        criteria: criterionDraft.map((criterion) => ({
          id: criterion.id,
          modelType,
          categoryId: criterion.categoryId ?? null,
          name: criterion.name,
          description: criterion.description,
          criterionType: criterion.criterionType,
          valueType: criterion.valueType,
          unitType: criterion.unitType,
          customUnit: criterion.customUnit,
          weight: Number(criterion.weight),
          scoringDirection: criterion.scoringDirection,
          required: criterion.required,
          active: criterion.active,
          rank: Number(criterion.rank),
          sourceStrategy: criterion.sourceStrategy,
          scoringConfigurationStatus: criterion.scoringConfigurationStatus
        })),
        bandsByCriterionId: Object.fromEntries(
          criterionDraft.map((criterion) => [criterion.id, bandsForCriterion(criterion)])
        )
      });
      setScoringConfiguration(response);
      setCategoryDraft(response.categories ?? []);
      setCriterionDraft(response.criteria);
      setBandDraft(response.bandsByCriterionId);
      setNotice(copy.configurationDraftSaved);
    }, copy.configurationDraftError);
  }

  async function runScoringPreview() {
    const modelId = overview.calculation?.id;
    if (!modelId) return;
    await runAction(async () => {
      const normalizedValues = Object.fromEntries(criterionDraft.map((criterion) => {
        const value = previewValues[criterion.id];
        const kind = criterionPreviewInputKind(criterion);
        if (kind === "boolean") {
          return [criterion.id, value === "true" ? true : value === "false" ? false : ""];
        }
        if (kind === "number") {
          return [criterion.id, value === undefined || value === "" ? "" : Number(value)];
        }
        return [criterion.id, value ?? ""];
      }));
      const preview = await previewRuntimeCustomerValueConfiguration(modelId, normalizedValues);
      setConfigurationPreview(preview);
    }, copy.configurationPreviewError);
  }

  async function confirmScoringConfiguration() {
    const modelId = overview.calculation?.id;
    if (!modelId || !scoringConfiguration?.confirmAvailable) return;
    await runAction(async () => {
      const response = await confirmRuntimeCustomerValueConfiguration(modelId);
      await refreshConfigurationFromResponse(response);
      setNotice(copy.configurationConfirmed);
    }, copy.configurationConfirmError, { reloadWorkspace: false });
  }

  async function confirmFinalizeCustomerValueEvaluation() {
    const companyEvaluationId = overview.calculation?.finalEvaluation?.id;
    if (!companyEvaluationId || overview.calculation?.finalEvaluation?.status !== "ready_for_finalization" || isFinalizingEvaluation) return;
    setIsFinalizingEvaluation(true);
    setError(undefined);
    setNotice(undefined);
    try {
      await finalizeRuntimeCustomerValueCompanyEvaluation(companyEvaluationId);
      const result = await loadFinalEvaluationReadback(companyEvaluationId);
      if (!result) {
        setError(copy.finalEvaluationReadbackError);
        return;
      }
      setFinalEvaluationDialogOpen(false);
      setNotice(copy.finalEvaluationSaved);
      await loadWorkspace();
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : "";
      const existingResult = await loadFinalEvaluationReadback(companyEvaluationId, { quietMissing: true });
      if (existingResult && ["company_evaluation_already_finalized", "final_result_snapshot_exists", "company_evaluation_state_changed"].includes(message)) {
        setFinalEvaluationDialogOpen(false);
        setNotice(copy.finalEvaluationRecovered);
        await loadWorkspace();
      } else {
        setError(message ? copy.finalEvaluationFailureLabel(message) : copy.finalEvaluationError);
      }
    } finally {
      setIsFinalizingEvaluation(false);
    }
  }

  async function downloadExport() {
    setError(undefined);
    setIsExporting(true);
    try {
      const blob = await downloadRuntimeCustomerValueExport(language);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `hauffe-os-customer-value-calculation-${overview.workspace?.name ?? "workspace"}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : copy.exportError);
    } finally {
      setIsExporting(false);
    }
  }

  function influenceLabel(criterionType: CriterionType | undefined) {
    if (criterionType === "negative") return copy.negativeInfluence;
    if (criterionType === "neutral") return copy.neutralInfluence;
    return copy.positiveInfluence;
  }

  function perspectiveList(items: Array<{ name: string; perspectiveType?: PerspectiveType }>) {
    if (items.length === 0) return language === "en" ? "None yet" : "Noch keine";
    return items.map((department) => {
      const type = department.perspectiveType ?? "department";
      const bilingualName = (department as { nameI18n?: Parameters<typeof readBilingualText>[0] }).nameI18n;
      const name = getPerspectiveDisplayName({ ...department, name: readBilingualText(bilingualName ?? department.name, language) }, language);
      const typeLabel = getPerspectiveTypeLabel(type, language);
      return name === typeLabel ? name : `${name} (${typeLabel})`;
    }).join(", ");
  }

  function perspectiveLink(type: string) {
    return withLanguagePrefix(`/workspace/departments/new?perspective=${encodeURIComponent(type)}`, language);
  }

  function scrollToCustomerValueSection(sectionId: string) {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function focusCriterionScoring(criterionId: string) {
    setExpandedCriterionIds((current) => {
      const next = new Set(current);
      next.add(criterionId);
      return next;
    });
    setHighlightedCriterionId(criterionId);
    window.setTimeout(() => setHighlightedCriterionId((current) => current === criterionId ? undefined : current), 2500);
    window.setTimeout(() => scrollToCustomerValueSection(`customer-value-criterion-${criterionId}`), 0);
  }

  function runCompletionAction(target: string) {
    if (target.startsWith("customer-value-criterion-")) {
      focusCriterionScoring(target.replace("customer-value-criterion-", ""));
      return;
    }
    scrollToCustomerValueSection(target);
  }

  function openModelEditor(target = "customer-value-criteria-section") {
    setIsPreviewPanelOpen(false);
    setIsEditMode(true);
    window.setTimeout(() => runCompletionAction(target), 0);
  }

  function openPreviewPanel() {
    setIsPreviewPanelOpen(true);
    setIsEditMode(true);
    window.setTimeout(() => scrollToCustomerValueSection("customer-value-preview-section"), 0);
  }

  function closeModelEditor() {
    setIsEditMode(false);
    setIsPreviewPanelOpen(false);
  }

  const criterionTypeOptions = ["legacy", "quantitative", "qualitative", "boolean", "calculated"] as const;
  const valueTypeOptions = ["unknown", "currency", "percentage", "integer", "decimal", "days", "months", "years", "count", "text_choice", "boolean"] as const;
  const unitTypeOptions = ["EUR", "percentage", "count", "days", "months", "years", "custom", "none"] as const;
  const scoringDirectionOptions = ["higher_is_better", "lower_is_better", "manual_bands"] as const;
  const sourceStrategyOptions = ["legacy_assessment", "manual", "crm_mapped", "customer_value_process", "deal_win_engine", "calculated_formula"] as const;
  const configurationStatusOptions = ["unconfigured", "draft", "confirmed", "superseded"] as const;
  const criterionDisplay = splitCustomerValueCriteriaForDisplay(criterionDraft);
  const editableScoringCriteria = criterionDisplay.active;
  const historicalCriteria = criterionDisplay.historical;
  const criterionDraftWeightTotal = activeWeightTotal();
  const criterionDraftWeightDifference = Number(Math.abs(100 - criterionDraftWeightTotal).toFixed(2));
  const criterionWeightStatusTone = criterionDraftWeightTotal === 100 ? "success" : criterionDraftWeightTotal > 100 ? "danger" : "warning";
  const modelReadiness = scoringConfiguration?.readiness;
  const scoringVersionSummaries = scoringConfiguration
    ? criterionDraft.flatMap((criterion) => buildCustomerValueScoringVersionSummaries(
      buildCustomerValueScoringVersionHistory(bandDraft[criterion.id] ?? [])
    ))
    : [];
  const segmentDraftForDisplay = deriveCustomerValueSegmentOrder(segmentDraft);
  const modelTypeSummary = scoringConfiguration
    ? hasConfiguredCustomerValueModelType(scoringConfiguration.model.modelType)
      ? scoringConfiguration.model.modelType
      : selectedModelTypeDraft || ""
    : "";
  const modelTypeSummaryIsDraft = Boolean(
    scoringConfiguration &&
    !hasConfiguredCustomerValueModelType(scoringConfiguration.model.modelType) &&
    selectedModelTypeDraft
  );
  const storedModelType = scoringConfiguration && hasConfiguredCustomerValueModelType(scoringConfiguration.model.modelType)
    ? scoringConfiguration.model.modelType
    : "";
  const modelTypeSelectValue = selectedModelTypeDraft || storedModelType;
  const previewIssueNames = (ids: readonly unknown[] | undefined) => Array.from(new Set((ids ?? [])
    .filter((id): id is string => typeof id === "string" && id.length > 0)
    .map((id) => criterionDraft.find((criterion) => criterion.id === id)?.name)
    .filter((name): name is string => Boolean(name))));
  const missingValueNames = previewIssueNames(configurationPreview?.missingValues);
  const missingScoringLogicNames = previewIssueNames(configurationPreview?.missingScoringLogic);
  const notComputableNames = previewIssueNames(configurationPreview?.notComputableCriteria);
  const readinessBlockingCriterionNames = previewIssueNames(modelReadiness?.blockingCriterionIds);
  const promotionBlockingCriterionNames = previewIssueNames(modelReadiness?.promotionBlockingCriterionIds);
  const scoringTodoCriteria = editableScoringCriteria.filter((criterion) => !criterionScoringIsConfirmed(criterion) && criterion.scoringConfigurationStatus !== "draft");
  const scoringDraftCriteria = editableScoringCriteria.filter((criterion) => !criterionScoringIsConfirmed(criterion) && criterion.scoringConfigurationStatus === "draft");
  const scoringReadyCriteria = editableScoringCriteria.filter((criterion) => criterionScoringIsConfirmed(criterion));
  const previewNeedsReview = Boolean(modelReadiness?.previewReady && !configurationPreview && scoringConfiguration?.model.status !== "confirmed");
  const completionActions = [
    ...(!modelReadiness?.modelTypeConfigured ? [{
      key: "criteria",
      label: copy.completionStepCriteria,
      reason: copy.modelTypeMissingReason,
      cta: copy.completionActionCriteria,
      target: "customer-value-criteria-section"
    }] : []),
    ...(modelReadiness && !modelReadiness.weightsComplete ? [{
      key: "weighting",
      label: copy.completionStepWeighting,
      reason: copy.weightingIncompleteReason,
      cta: copy.completionActionWeighting,
      target: "customer-value-weighting-section"
    }] : []),
    ...(modelReadiness && !modelReadiness.scoringPromotionReady ? [{
      key: "scoring",
      label: copy.completionStepScoring,
      reason: promotionBlockingCriterionNames.length > 0
        ? `${copy.scoringIncompleteReason(modelReadiness.promotionBlockingCriterionIds.length)} ${promotionBlockingCriterionNames.join(", ")}.`
        : copy.scoringIncompleteReason(modelReadiness.promotionBlockingCriterionIds.length),
      cta: copy.completionActionScoring,
      target: modelReadiness.promotionBlockingCriterionIds[0] ? `customer-value-criterion-${modelReadiness.promotionBlockingCriterionIds[0]}` : "customer-value-scoring-section"
    }] : []),
    ...(modelReadiness && !modelReadiness.segmentsConfirmed ? [{
      key: "segments",
      label: copy.completionStepSegments,
      reason: copy.segmentIncompleteReason,
      cta: copy.completionActionSegments,
      target: "customer-value-segmentation-section"
    }] : []),
    ...(previewNeedsReview ? [{
      key: "preview",
      label: copy.completionStepPreview,
      reason: copy.previewIncompleteReason,
      cta: copy.completionActionPreview,
      target: "customer-value-preview-section"
    }] : [])
  ];
  const confirmationBlockerReasons = completionActions.map((action) => action.reason);
  const configurationConfirmationAvailable = Boolean(scoringConfiguration?.confirmAvailable && completionActions.length === 0);
  const scoringDraftSaveAvailable = isCustomerValueScoringDraftSaveAvailable(criterionDraft, isWorking);
  const uxCopy = CUSTOMER_VALUE_UX_COPY[language];
  const hasConfirmedModel = Boolean(modelReadiness?.status === "confirmed" && modelReadiness.scoringComplete && modelReadiness.blockingCriterionIds.length === 0);
  const showCompactConfirmedState = hasConfirmedModel && !isEditMode;
  const showConfigurationEditor = Boolean(displayCalculation) && !showCompactConfirmedState;
  const optionalNoticeCount = (recommendedPerspectiveTypes.length > 0 ? 1 : 0) + (translationIncomplete ? 1 : 0);
  const workflowSteps = buildCustomerValueWorkflowSteps({
    modelReadiness,
    hasActiveCriteria: editableScoringCriteria.length > 0,
    hasConfirmedModel
  });
  const activeScoringDraftSignature = buildCustomerValueScoringDraftSignature({
    modelType: modelTypeSelectValue,
    criteria: criterionDraft,
    bandsByCriterionId: Object.fromEntries(criterionDraft.map((criterion) => [criterion.id, bandsForCriterion(criterion)]))
  });
  const storedScoringDraftSignature = scoringConfiguration
    ? buildCustomerValueScoringDraftSignature({
      modelType: storedModelType,
      criteria: scoringConfiguration.criteria,
      bandsByCriterionId: scoringConfiguration.bandsByCriterionId
    })
    : "";
  const hasUnsavedScoringChanges = Boolean(scoringConfiguration) && activeScoringDraftSignature !== storedScoringDraftSignature;
  const primaryCompletionAction = hasConfirmedModel
    ? undefined
    : completionActions[0] ?? (configurationConfirmationAvailable
      ? {
        key: "confirm",
        label: copy.completionStepConfirm,
        reason: uxCopy.processDraftBody,
        cta: uxCopy.activateModel,
        target: "customer-value-confirm-section"
      }
      : modelReadiness?.previewReady
        ? {
          key: "preview",
          label: copy.completionStepPreview,
          reason: copy.previewIncompleteReason,
          cta: uxCopy.testModel,
          target: "customer-value-preview-section"
        }
        : undefined);
  const finalEvaluation = overview.calculation?.finalEvaluation;
  const finalEvaluationReady = finalEvaluation?.status === "ready_for_finalization";
  const finalEvaluationFinalized = finalEvaluation?.status === "finalized" || Boolean(finalEvaluationResult);
  const selectedFinalResult = finalEvaluationHistory.find((result) => result.id === selectedFinalResultId) ?? finalEvaluationResult ?? finalEvaluationHistory[0];
  const finalEvaluationButtonDisabled = isWorking || isFinalizingEvaluation || !finalEvaluationReady || Boolean(finalEvaluationResult);
  const formatFinalNumber = (value: number | null | undefined) => (
    typeof value === "number" && Number.isFinite(value) ? value.toLocaleString(language === "de" ? "de-DE" : "en-US", { maximumFractionDigits: 2 }) : copy.notStored
  );
  const formatFinalDate = (value: string | undefined) => value
    ? new Intl.DateTimeFormat(language === "de" ? "de-DE" : "en-US", {
      dateStyle: "medium",
      timeStyle: "short"
    }).format(new Date(value))
    : copy.notStored;
  const safeFinalValue = (value: unknown) => {
    if (value === null || value === undefined || value === "") return copy.notStored;
    if (typeof value === "number") return formatFinalNumber(value);
    if (typeof value === "boolean") return value ? copy.booleanTrue : copy.booleanFalse;
    return String(value);
  };

  const executiveMissingAcknowledged = Boolean(overview.perspectiveReview?.missingExecutive && overview.perspectiveReview.acknowledgedAt);
  const financeMissingAcknowledged = Boolean(overview.perspectiveReview?.missingFinanceOrCommercial && overview.perspectiveReview.acknowledgedAt);

  return (
    <Layout
      eyebrow={copy.eyebrow}
      title={copy.title}
      description={copy.description}
    >
      <div className="customer-value-editor">
      {isLoading ? (
        <Panel title={copy.loadingTitle}>
          <HauffeLoader label={copy.loadingLabel} />
        </Panel>
      ) : null}
      {error ? (
        <Panel title={copy.loadErrorTitle} variant="accent">
          <p className="form-error" role="alert">{error}</p>
        </Panel>
      ) : null}
      {notice ? (
        <Panel title={copy.saved} variant="accent">
          <p>{notice}</p>
        </Panel>
      ) : null}
      {!isLoading && !error && !overview.workspace ? (
        <Panel title={copy.noWorkspaceTitle} variant="accent">
          <p>{copy.noWorkspaceBody}</p>
        </Panel>
      ) : null}

      {overview.workspace ? (
        <>
          {!showCompactConfirmedState ? (
            <Panel title={copy.workingState} variant="accent">
              <p>{copy.workingBody}</p>
            </Panel>
          ) : null}

          {!showCompactConfirmedState ? (
            <div className="grid grid-4">
            <Stat label={copy.workspace} value={overview.workspace.name} />
            <Stat label={copy.process} value={customerValueProcessLabel(overview.process?.status, language)} tone={overview.process?.status === "calculation_confirmed" ? "success" : overview.process?.status ? "warning" : "danger"} />
            <Stat label={copy.valueRound} value={`${submittedValueInputs}/${visibleDepartments.length}`} tone={submittedValueInputs === visibleDepartments.length && visibleDepartments.length > 0 ? "success" : "warning"} />
            <Stat label={copy.calculation} value={displayCalculation?.status === "confirmed" ? copy.confirmed : displayCalculation ? copy.readyForReview : copy.open} tone={displayCalculation?.status === "confirmed" ? "success" : displayCalculation ? "warning" : "default"} />
            </div>
          ) : null}

          {!showCompactConfirmedState ? (
            <Panel title={copy.reviewPerspectives} variant="accent">
            <p>{copy.reviewPerspectivesBody}</p>
            <div className="data-list">
              <div>
                <span>{copy.submittedPerspectives}</span>
                <strong>{perspectiveList(perspectiveCoverage.submittedPerspectives)}</strong>
              </div>
              <div>
                <span>{copy.openPerspectives}</span>
                <strong>{perspectiveList(perspectiveCoverage.openPerspectives)}</strong>
              </div>
              <div>
                <span>{copy.recommendedPerspectives}</span>
                <strong>{recommendedPerspectiveTypes.map((type) => getPerspectiveTypeLabel(type, language)).join(" · ")}</strong>
              </div>
            </div>
            {perspectiveCoverage.missingExecutive && !executiveMissingAcknowledged ? (
              <p className="form-note">{copy.executiveMissing}</p>
            ) : null}
            {perspectiveCoverage.missingFinanceOrCommercial && !financeMissingAcknowledged ? (
              <p className="form-note">{copy.financeMissing}</p>
            ) : null}
            <div className="hero-actions">
              {ownerExecutivePerspective ? (
                <a className="button button-secondary" href={withLanguagePrefix(`/workspace/departments/${ownerExecutivePerspective.id}/input`, language)}>{copy.answerExecutive}</a>
              ) : canInviteExecutive ? (
                <a className="button button-secondary" href={perspectiveLink("executive")}>{copy.inviteExecutive}</a>
              ) : executivePerspective ? (
                <span className="form-note">{copy.executiveExists}</span>
              ) : null}
              <a className="button button-secondary" href={perspectiveLink("finance")}>{copy.inviteFinance}</a>
              <a className="button button-secondary" href={perspectiveLink("department")}>{copy.addPerspective}</a>
              <button
                className="button button-secondary"
                type="button"
                disabled={isWorking}
                onClick={() => void runAction(async () => {
                  await reopenRuntimeValueRound();
                  setNotice(copy.valueRoundReopened);
                }, copy.valueRoundReopenError)}
              >
                {copy.reopenValueRound}
              </button>
            </div>
            {((perspectiveCoverage.missingExecutive && !executiveMissingAcknowledged) || (perspectiveCoverage.missingFinanceOrCommercial && !financeMissingAcknowledged)) ? (
              <div className="form-actions">
                {perspectiveCoverage.missingExecutive && !executiveMissingAcknowledged ? (
                  <button
                    className="button button-secondary"
                    type="button"
                    disabled={isWorking}
                    onClick={() => void runAction(async () => {
                      await acknowledgeRuntimeMissingPerspectives({
                        missingExecutive: true,
                        missingFinanceOrCommercial: financeMissingAcknowledged
                      });
                      setNotice(copy.missingAcknowledged);
                    }, copy.acknowledgeError)}
                  >
                    {copy.continueWithoutExecutive}
                  </button>
                ) : null}
                {perspectiveCoverage.missingFinanceOrCommercial && !financeMissingAcknowledged ? (
                  <button
                    className="button button-secondary"
                    type="button"
                    disabled={isWorking}
                    onClick={() => void runAction(async () => {
                      await acknowledgeRuntimeMissingPerspectives({
                        missingExecutive: executiveMissingAcknowledged,
                        missingFinanceOrCommercial: true
                      });
                      setNotice(copy.missingAcknowledged);
                    }, copy.acknowledgeError)}
                  >
                    {copy.continueWithoutFinance}
                  </button>
                ) : null}
              </div>
            ) : null}
            <p className="form-note">
              {copy.updateEvaluation}: {copy.updateEvaluationNote}
            </p>
          </Panel>
          ) : null}

          {!showCompactConfirmedState && !overview.latestSummary ? (
            <Panel title={copy.firstEvaluationTitle} variant="accent">
              <p>{copy.firstEvaluationBody}</p>
              <a className="button button-primary" href={withLanguagePrefix("/workspace/hauffe-os-summary", language)}>
                {copy.goEvaluation}
              </a>
            </Panel>
          ) : null}

          {!showCompactConfirmedState && overview.latestSummary && overview.process?.status === "value_round_open" ? (
            <Panel title={copy.valueRoundRunning} variant="accent">
              <p>{copy.valueRoundBody}</p>
            </Panel>
          ) : null}

          {!showCompactConfirmedState && overview.latestSummary && clusters.length === 0 ? (
            <Panel title={copy.clusterTitle} variant="accent">
              <p>{copy.clusterBody}</p>
              <button
                className="button button-primary"
                type="button"
                disabled={isWorking || !canPrepareClusters(overview.process?.status)}
                onClick={() => void runAction(async () => {
                  await generateRuntimeCustomerValueClusters(language);
                  setNotice(copy.clustersPrepared);
                }, copy.clustersError)}
              >
                {copy.prepareCriteria}
              </button>
            </Panel>
          ) : null}

          {!showCompactConfirmedState && translationIncomplete ? (
            <Panel title={copy.criteriaLanguageTitle} variant="accent">
              <p>{copy.criteriaLanguageBody}</p>
              {isWorking && clusterLanguageMismatch ? (
                <div className="processing-state motion-fade">
                  <HauffeLoader label={copy.regenerateCriteria} />
                  <div>
                    <div className="weight-bar" aria-hidden="true"><span style={{ width: "72%" }} /></div>
                    <p className="form-note">{copy.localGeneratingHelp}</p>
                  </div>
                </div>
              ) : null}
              {clusterLanguageMismatch ? (
                <button
                  className="button button-secondary"
                  type="button"
                  disabled={isWorking || !canPrepareClusters(overview.process?.status)}
                  onClick={() => void runAction(async () => {
                    await generateRuntimeCustomerValueClusters(language, { regenerate: true });
                    setCriteriaRegenerated(true);
                    setNotice(copy.clustersRegenerated);
                  }, copy.clustersError)}
                >
                  {copy.regenerateCriteria}
                </button>
              ) : null}
            </Panel>
          ) : null}
          {!showCompactConfirmedState && criteriaRegenerated && !translationIncomplete && clusters.length > 0 ? (
            <Panel title={copy.reviewTitle} variant="accent">
              <p>{copy.clustersRegenerated}</p>
              <a className="button button-primary" href="#criteria-review">{copy.openRegeneratedCriteria}</a>
            </Panel>
          ) : null}

          {!showCompactConfirmedState && clusters.length > 0 && !translationIncomplete && !displayCalculation ? (
            <Panel title={copy.reviewTitle}>
              <span id="criteria-review" />
              <p className="form-note">{copy.reviewNote}</p>
              <div className="question-flow">
                {displayClusters.map((cluster) => {
                  const draft = resolveRuntimeClusterForLanguage(draftClusters[cluster.id] ?? cluster, language);
                  const sourceTerms = safeTextList(cluster.sourceTerms);
                  const sourceDepartments = safeTextList(cluster.sourceDepartments);
                  return (
                    <article className="question-card criteria-card" key={cluster.id}>
                      <div className="criteria-card__header">
                        <span className="question-number">{String(cluster.sortOrder).padStart(2, "0")}</span>
                        <strong>{draft.name || copy.criterion}</strong>
                      </div>
                      <label className="criteria-card__name editor-field">
                        {copy.criterion}
                        <input
                          value={draft.name}
                          onChange={(event) => updateClusterDraft(cluster.id, { name: event.target.value })}
                        />
                      </label>
                      <label className="criteria-card__description editor-field">
                        {copy.descriptionLabel}
                        <textarea
                          value={draft.description}
                          rows={4}
                          onChange={(event) => updateClusterDraft(cluster.id, { description: event.target.value })}
                        />
                      </label>
                      <div className="criteria-meta-grid">
                        <label className="criteria-meta-box editor-field-card">
                          {copy.criterionType}
                          <select
                            value={draft.criterionType}
                            onChange={(event) => updateClusterDraft(cluster.id, { criterionType: event.target.value as CriterionType })}
                          >
                            <option value="positive">{copy.positive}</option>
                            <option value="negative">{copy.negative}</option>
                            <option value="neutral">{copy.neutral}</option>
                          </select>
                        </label>
                        <label className="criteria-meta-box editor-field-card">
                          {copy.category}
                          <select
                            value={draft.criterionKind}
                            onChange={(event) => updateClusterDraft(cluster.id, { criterionKind: event.target.value as CustomerValueCriterionKind })}
                          >
                            <option value="perspective">{copy.perspective}</option>
                            <option value="system_metric">{copy.systemMetric}</option>
                            <option value="prospect_metric">{copy.prospectMetric}</option>
                          </select>
                        </label>
                        <label className="criteria-meta-box editor-field-card">
                          {copy.scoringType}
                          <select
                            value={draft.scoringType}
                            onChange={(event) => updateClusterDraft(cluster.id, { scoringType: event.target.value as CustomerValueScoringType })}
                          >
                            <option value="manual_1_10">{copy.manual}</option>
                            <option value="numeric_higher_better">{copy.higherBetter}</option>
                            <option value="numeric_lower_better">{copy.lowerBetter}</option>
                            <option value="negative_effort">{copy.effort}</option>
                          </select>
                        </label>
                        <label className="criteria-meta-box editor-field-card">
                          {copy.unit}
                          <input
                            value={draft.unit ?? ""}
                            placeholder={copy.unitPlaceholder}
                            onChange={(event) => updateClusterDraft(cluster.id, { unit: event.target.value })}
                          />
                        </label>
                      </div>
                      <p className="form-note editor-helper">
                        {copy.signals}: {sourceTerms.length ? sourceTerms.join(", ") : copy.derivedSignals}
                      </p>
                      <p className="form-note editor-helper">
                        {copy.departments}: {sourceDepartments.length ? sourceDepartments.join(", ") : copy.multiplePerspectives}
                      </p>
                      <div className="form-actions editor-actions">
                        <button
                          className="button button-secondary"
                          type="button"
                          disabled={isWorking}
                          onClick={() => void runAction(async () => {
                            await updateRuntimeCustomerValueCluster(cluster.id, {
                              name: draft.name,
                              description: draft.description,
                              criterionType: draft.criterionType,
                              criterionKind: draft.criterionKind,
                              scoringType: draft.scoringType,
                              unit: draft.unit,
                              ownerApproved: true,
                              language
                            });
                            setNotice(copy.criterionSaved);
                          }, copy.criterionSaveError)}
                        >
                          {copy.saveCriterion}
                        </button>
                        <button
                          className="button button-secondary"
                          type="button"
                          disabled={isWorking || clusters.length <= 2}
                          onClick={() => void runAction(async () => {
                            await deleteRuntimeCustomerValueCluster(cluster.id);
                            setNotice(copy.criterionRemoved);
                          }, copy.criterionRemoveError)}
                        >
                          {copy.remove}
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
              <div className="form-actions editor-actions">
                <button
                  className="button button-primary"
                  type="button"
                  disabled={isWorking || clusters.length === 0}
                  onClick={() => void runAction(async () => {
                    await approveRuntimeCustomerValueClusters();
                    setNotice(copy.criteriaApproved);
                  }, copy.criteriaApproveError)}
                >
                  {criteriaAlreadyConfirmed ? copy.editCriteria : copy.confirmCriteria}
                </button>
              </div>
            </Panel>
          ) : null}

          {!showCompactConfirmedState && canWeightClusters(overview.process?.status) && weightableClusters.length > 0 && !translationIncomplete ? (
            <Panel title={copy.weightingTitle} variant="accent">
              <h3>{copy.departmentWeightingTitle}</h3>
              <p>{copy.departmentWeightingBody}</p>
              <div className="data-list">
                <div>
                  <span>{copy.submittedPerspectives}</span>
                  <strong>{overview.departmentWeighting?.submittedDepartments ?? 0}/{overview.departmentWeighting?.totalDepartments ?? 0}</strong>
                </div>
                <div>
                  <span>{copy.missingWeightings}</span>
                  <strong>
                    {missingWeightingDepartments.length
                      ? missingWeightingDepartments.map((department) => getPerspectiveDisplayName({ name: department.name, perspectiveType: department.perspectiveType }, language)).join(", ")
                      : copy.allWeightingsSubmitted}
                  </strong>
                </div>
              </div>
              {missingWeightingDepartments.length ? (
                <div className="data-list">
                  {missingWeightingDepartments.map((department) => (
                    <div key={department.id}>
                      <span>{getPerspectiveDisplayName({ name: department.name, perspectiveType: department.perspectiveType }, language)}</span>
                      <a className="button button-secondary" href={withLanguagePrefix(`/workspace/departments/${department.id}/weighting`, language)}>
                        {copy.weightingTask}
                      </a>
                    </div>
                  ))}
                </div>
              ) : null}
              {missingWeightingDepartments.length ? (
                <div className="hero-actions">
                  <button
                    className="button button-secondary"
                    type="button"
                    disabled={isWorking}
                    onClick={() => void runAction(async () => {
                      await sendRuntimeDepartmentWeightingReminder();
                      setNotice(copy.reminderSent);
                    }, copy.weightingError)}
                  >
                    {copy.remindDepartments}
                  </button>
                </div>
              ) : null}
              <div className="weighting-review" role="table" aria-label={copy.departmentWeightingTitle}>
                <div className="weighting-review-row weighting-review-head" role="row">
                  <span role="columnheader">{copy.criterion}</span>
                  <span role="columnheader">{copy.suggestedAverage}</span>
                  <span role="columnheader">{copy.ownerFinal}</span>
                </div>
                {displayWeightableClusters.map((cluster) => (
                  <div className="weighting-review-row" role="row" key={cluster.id}>
                    <strong role="cell">{cluster.name}</strong>
                    <span role="cell">{overview.departmentWeighting?.averages[cluster.id] ?? 0}%</span>
                    <span role="cell">{weights[cluster.id] ?? 0}%</span>
                    <div className="weight-bar weighting-review-bar" aria-hidden="true">
                      <span style={{ width: `${Math.min(100, Math.max(0, overview.departmentWeighting?.averages[cluster.id] ?? 0))}%` }} />
                    </div>
                  </div>
                ))}
              </div>
              {missingWeightingDepartments.length ? <p className="form-note">{copy.continueWithMissing}</p> : null}
              {allDepartmentWeightingsSubmitted ? <p className="form-note">{copy.allWeightingsReady}</p> : null}
              <p>{copy.weightingBody}</p>
              {allDepartmentWeightingsSubmitted ? (
                <>
                  <div className="calculation-criteria-list">
                    {displayWeightableClusters.map((cluster) => (
                      <div className="calculation-criterion" key={cluster.id}>
                        <div className="calculation-criterion__top">
                          <strong>{cluster.name}</strong>
                          <input
                            aria-label={`${cluster.name} ${copy.weighting}`}
                            type="number"
                            min="0"
                            max="100"
                            step="1"
                            value={weights[cluster.id] ?? 0}
                            onChange={(event) =>
                              setWeights((current) => ({ ...current, [cluster.id]: Number(event.target.value) }))
                            }
                          />
                        </div>
                        {cluster.description ? <p className="calculation-criterion__description">{cluster.description}</p> : null}
                        <div className="weight-bar" aria-hidden="true">
                          <span style={{ width: `${Math.min(100, Math.max(0, weights[cluster.id] ?? 0))}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="segment-config-card">
                    <label className="segment-config-field editor-field">
                      {copy.segmentLogic}
                      <select className="segment-config-select" value={segmentCount} onChange={(event) => setSegmentCount(Number(event.target.value))}>
                        {Array.from({ length: MAX_SEGMENT_COUNT - MIN_SEGMENT_COUNT + 1 }, (_, index) => MIN_SEGMENT_COUNT + index).map((count) => (
                          <option key={count} value={count}>{copy.segmentOption(count)}</option>
                        ))}
                      </select>
                    </label>
                    <p className="form-note">{copy.segmentHelp}</p>
                    <p className={weightValidation.ok ? "form-note segment-config-status" : "form-error segment-config-status"}>
                      {copy.distributed}: {weightValidation.total}% · {weightValidation.message}
                    </p>
                  </div>
                  <div className="segment-config-actions">
                    <button
                      className="button button-primary"
                      type="button"
                      disabled={isWorking || !weightValidation.ok}
                      onClick={() => void runAction(async () => {
                        const relevantWeights = Object.fromEntries(weightableClusters.map((cluster) => [cluster.id, weights[cluster.id] ?? 0]));
                        await saveRuntimeCustomerValueWeights(relevantWeights, segmentCount, language);
                        setNotice(copy.calculationPrepared);
                      }, copy.weightingError)}
                    >
                      {isWorking ? copy.preparingCalculation : copy.prepareCalculation}
                    </button>
                  </div>
                  {isWorking ? (
                    <div className="processing-state motion-fade">
                      <HauffeLoader label={copy.preparingCalculation} />
                      <p className="form-note">{copy.processingData} {copy.processingStay}</p>
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="hero-actions">
                  <button
                    className="button button-primary"
                    type="button"
                    disabled
                  >
                    {copy.prepareCalculation}
                  </button>
                </div>
              )}
            </Panel>
          ) : null}

          {!showCompactConfirmedState && displayCalculation && !translationIncomplete ? (
            <Panel title="Customer Value Calculation" variant="accent">
              <div className="data-list">
                <div>
                  <span>{copy.status}</span>
                  <StatusBadge tone={displayCalculation.status === "confirmed" ? "success" : "warning"} withCheck={displayCalculation.status === "confirmed"}>
                    {displayCalculation.status === "confirmed" ? copy.confirmed : copy.readyForReview}
                  </StatusBadge>
                </div>
                <div>
                  <span>{copy.methodologyVersion}</span>
                  <strong>{displayCalculation.methodologyVersion ?? copy.notStored}</strong>
                </div>
              </div>
              {displayCalculation.methodNote ? <p className="form-note">{displayCalculation.methodNote}</p> : null}
              <a className="button button-primary" href="#customer-value-calculation-result">
                {copy.viewCalculation}
              </a>
              <button
                className="button button-secondary"
                type="button"
                disabled={isExporting}
                onClick={() => void downloadExport()}
              >
                {isExporting ? copy.downloading : copy.download}
              </button>
              {isExporting ? (
                <div className="processing-state motion-fade">
                  <HauffeLoader label={copy.downloading} />
                  <p className="form-note">{copy.processingData} {copy.processingStay}</p>
                </div>
              ) : null}
            </Panel>
          ) : null}

          {!showCompactConfirmedState && displayCalculation && !translationIncomplete ? (
            <Panel title={copy.finalEvaluationTitle} variant="accent">
              <p className="form-note">
                {copy.finalEvaluationBody} <FieldHelp content={copy.finalHelp.immutable} label={copy.immutableResult} />
              </p>
              <div className="data-list">
                <div>
                  <span>{copy.finalEvaluationCompany}</span>
                  <strong>{finalEvaluation?.companyName ?? copy.notStored}</strong>
                </div>
                <div>
                  <span>{copy.status}</span>
                  <StatusBadge tone={finalEvaluationFinalized ? "success" : finalEvaluationReady ? "warning" : "default"} withCheck={finalEvaluationFinalized}>
                    {finalEvaluationFinalized ? copy.finalEvaluationFinalized : finalEvaluationReady ? copy.finalEvaluationReady : copy.finalEvaluationUnavailable}
                  </StatusBadge>
                </div>
                <div>
                  <span>{copy.finalEvaluationScore} <FieldHelp content={copy.finalHelp.score} label={copy.finalEvaluationScore} /></span>
                  <strong>{formatFinalNumber(selectedFinalResult?.finalScore ?? finalEvaluation?.finalScore)}</strong>
                </div>
                <div>
                  <span>{copy.finalEvaluationSegment} <FieldHelp content={copy.finalHelp.segment} label={copy.finalEvaluationSegment} /></span>
                  <strong>{selectedFinalResult?.segmentName ?? finalEvaluation?.finalSegmentName ?? copy.notStored}</strong>
                </div>
                <div>
                  <span>{copy.finalEvaluationVersion} <FieldHelp content={copy.finalHelp.engine} label={copy.finalEvaluationVersion} /></span>
                  <strong>{selectedFinalResult?.engineVersion ?? finalEvaluation?.calculationVersion ?? copy.notStored}</strong>
                </div>
                <div>
                  <span>{copy.finalizedAt}</span>
                  <strong>{formatFinalDate(selectedFinalResult?.finalizedAt ?? finalEvaluation?.finalizedAt)}</strong>
                </div>
              </div>
              {!finalEvaluationReady && !finalEvaluationFinalized ? (
                <p className="form-note">{copy.finalEvaluationNotReady}</p>
              ) : null}
              {finalEvaluationReadbackLoading ? <p className="form-note">{copy.processingData}</p> : null}
              {selectedFinalResult ? (
                <div className="final-result-card" aria-label={copy.latestFinalResult}>
                  <div className="final-result-card__hero">
                    <div>
                      <span>{copy.finalEvaluationScore}</span>
                      <strong>{formatFinalNumber(selectedFinalResult.roundedScore)}</strong>
                      <small>{copy.scoreScale}: {selectedFinalResult.scoreScale.min}-{selectedFinalResult.scoreScale.max}</small>
                    </div>
                    <div>
                      <span>{copy.finalEvaluationSegment}</span>
                      <strong>{selectedFinalResult.segmentName}</strong>
                      <small>{copy.modelType}: {selectedFinalResult.modelName ?? selectedFinalResult.modelId} · {copy.segmentVersion} {selectedFinalResult.modelVersion}</small>
                    </div>
                  </div>

                  <details className="final-result-details" open>
                    <summary>{copy.criterionBreakdown}</summary>
                    <div className="final-criterion-list">
                      {selectedFinalResult.criterionResults.map((criterion) => (
                        <div className="final-criterion-card" key={criterion.criterionId}>
                          <strong>{criterion.criterionName ?? criterion.criterionId}</strong>
                          <div className="data-list">
                            <div>
                              <span>{copy.criterionRawValue}</span>
                              <strong>{safeFinalValue(criterion.rawValue)}</strong>
                            </div>
                            <div>
                              <span>{copy.criterionPoints} <FieldHelp content={copy.finalHelp.points} label={copy.criterionPoints} /></span>
                              <strong>{formatFinalNumber(criterion.points)}</strong>
                            </div>
                            <div>
                              <span>{copy.weighting} <FieldHelp content={copy.finalHelp.weight} label={copy.weighting} /></span>
                              <strong>{formatFinalNumber(criterion.weight)}%</strong>
                            </div>
                            <div>
                              <span>{copy.criterionWeightedContribution} <FieldHelp content={copy.finalHelp.contribution} label={copy.criterionWeightedContribution} /></span>
                              <strong>{formatFinalNumber(criterion.weightedContribution)}</strong>
                            </div>
                            <div>
                              <span>{copy.scoringBand}</span>
                              <strong>{criterion.scoringBandId ? `${criterion.scoringBandId} · v${criterion.scoringBandVersion ?? 1}` : copy.notStored}</strong>
                            </div>
                            <div>
                              <span>{copy.status}</span>
                              <strong>{copy.finalCriterionStatusLabel(criterion.status)}</strong>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </details>

                  <details className="final-result-details">
                    <summary>{copy.calculationDetails}</summary>
                    <div className="data-list">
                      <div>
                        <span>{copy.finalEvaluationVersion}</span>
                        <strong>{selectedFinalResult.engineVersion}</strong>
                      </div>
                      <div>
                        <span>{copy.rawScore}</span>
                        <strong>{formatFinalNumber(selectedFinalResult.rawScore)}</strong>
                      </div>
                      <div>
                        <span>{copy.roundedScore}</span>
                        <strong>{formatFinalNumber(selectedFinalResult.roundedScore)}</strong>
                      </div>
                      <div>
                        <span>{copy.weightingVersion}</span>
                        <strong>{selectedFinalResult.weightingVersion ?? copy.weightingVersionMissing}</strong>
                      </div>
                      <div>
                        <span>{copy.snapshotReference}</span>
                        <strong>{selectedFinalResult.id}</strong>
                      </div>
                      <div>
                        <span>{copy.calculatedAt}</span>
                        <strong>{formatFinalDate(selectedFinalResult.calculatedAt)}</strong>
                      </div>
                    </div>
                  </details>
                </div>
              ) : null}
              <details className="final-result-details">
                <summary>{copy.finalEvaluationHistoryTitle}</summary>
                {finalEvaluationHistory.length === 0 ? (
                  <p className="form-note">{copy.finalEvaluationHistoryEmpty}</p>
                ) : (
                  <div className="history-list">
                    {finalEvaluationHistory.map((result, index) => (
                      <button
                        key={result.id}
                        type="button"
                        className={result.id === selectedFinalResult?.id ? "history-list__item history-list__item--active" : "history-list__item"}
                        onClick={() => setSelectedFinalResultId(result.id)}
                      >
                        <strong>{index === 0 ? copy.latestFinalResult : copy.historicalFinalResult}</strong>
                        <span>{formatFinalDate(result.finalizedAt)} · {formatFinalNumber(result.finalScore)} · {result.segmentName}</span>
                        <small>{copy.finalEvaluationVersion}: {result.engineVersion}</small>
                      </button>
                    ))}
                  </div>
                )}
              </details>
              <div className="form-actions">
                <button
                  className="button button-primary"
                  type="button"
                  disabled={finalEvaluationButtonDisabled}
                  onClick={() => setFinalEvaluationDialogOpen(true)}
                >
                  {isFinalizingEvaluation ? copy.finalizingEvaluation : copy.finalizeEvaluation}
                </button>
              </div>
              {finalEvaluationDialogOpen ? (
                <div className="modal-backdrop" role="presentation" onMouseDown={() => !isFinalizingEvaluation && setFinalEvaluationDialogOpen(false)}>
                  <div
                    className="modal-dialog"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="final-evaluation-dialog-title"
                    onMouseDown={(event) => event.stopPropagation()}
                    onKeyDown={(event) => {
                      if (event.key === "Escape" && !isFinalizingEvaluation) {
                        setFinalEvaluationDialogOpen(false);
                      }
                    }}
                  >
                    <h2 id="final-evaluation-dialog-title">{copy.finalEvaluationDialogTitle}</h2>
                    <p>{copy.finalEvaluationDialogBody}</p>
                    <div className="form-actions">
                      <button
                        className="button button-secondary"
                        type="button"
                        disabled={isFinalizingEvaluation}
                        onClick={() => setFinalEvaluationDialogOpen(false)}
                      >
                        {copy.finalEvaluationDialogCancel}
                      </button>
                      <button
                        className="button button-primary"
                        type="button"
                        ref={finalEvaluationConfirmButtonRef}
                        disabled={isFinalizingEvaluation}
                        onClick={() => void confirmFinalizeCustomerValueEvaluation()}
                      >
                        {isFinalizingEvaluation ? copy.finalizingEvaluation : copy.finalEvaluationDialogConfirm}
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}
            </Panel>
          ) : null}

          {!showCompactConfirmedState && displayCalculation?.criteria?.length && !translationIncomplete ? (
            <Panel title={copy.criteriaWeights}>
              <div id="customer-value-calculation-result" className="calculation-criteria-list">
                {displayCalculation.criteria.map((criterion) => (
                  <div className="calculation-criterion" key={criterion.id}>
                    <div className="calculation-criterion__top">
                      <div>
                        <span>{influenceLabel(criterion.criterionType)}</span>
                        <strong>{criterion.name}</strong>
                      </div>
                      <StatusBadge tone="success">{copy.weighting} {criterion.weight}%</StatusBadge>
                    </div>
                    <p className="calculation-criterion__description">{criterion.description}</p>
                  </div>
                ))}
              </div>
              {displayCalculation.status !== "confirmed" ? (
                <div className="form-actions">
                  <button
                    className="button button-primary"
                    type="button"
                    disabled={isWorking}
                    onClick={() => void runAction(async () => {
                      await confirmRuntimeCustomerValueCalculation();
                      setNotice(copy.calculationConfirmed);
                    }, copy.confirmError)}
                  >
                    {copy.confirmCalculation}
                  </button>
                </div>
              ) : null}
            </Panel>
          ) : null}

          {!showCompactConfirmedState && displayCalculation?.segments?.length && !translationIncomplete ? (
            <Panel title={copy.finalSegmentLogic} variant="accent">
              <p className="form-note">{copy.dimensionLogicBody}</p>
              <div className="segment-list">
                {displayCalculation.segments.map((segment, index) => {
                  const relatedCriterion = displayCalculation.criteria?.find((criterion) => criterion.name === segment.name) ?? displayCalculation.criteria?.[index];
                  return (
                    <article className="segment-card" key={segment.name}>
                      <div className="segment-card__header">
                        <strong>{segment.name}</strong>
                      </div>
                      <p>{segment.description}</p>
                      <div className="segment-detail-grid">
                        <div className="segment-detail"><span>{copy.dimensionScore}</span><p>{copy.dimensionScoreRange}</p></div>
                        <div className="segment-detail"><span>{copy.dimensionWeight}</span><p>{relatedCriterion ? `${relatedCriterion.weight}%` : copy.notStored}</p></div>
                        <div className="segment-detail"><span>{copy.dimensionInfluence}</span><p>{influenceLabel(relatedCriterion?.criterionType)}</p></div>
                        {segment.meaning ? <div className="segment-detail"><span>{copy.segmentMeaning}</span><p>{segment.meaning}</p></div> : null}
                        {segment.typicalCharacteristics?.length ? (
                          <div className="segment-detail"><span>{copy.segmentCharacteristics}</span><p>{segment.typicalCharacteristics.join(", ")}</p></div>
                        ) : null}
                        {segment.risks?.length ? <div className="segment-detail"><span>{copy.segmentRisks}</span><p>{segment.risks.join(", ")}</p></div> : null}
                        {segment.recommendation ? <div className="segment-detail"><span>{copy.segmentRecommendation}</span><p>{segment.recommendation}</p></div> : null}
                        {segment.relevantDepartments?.length ? (
                          <div className="segment-detail"><span>{copy.segmentDepartments}</span><p>{segment.relevantDepartments.join(", ")}</p></div>
                        ) : null}
                        {segment.crmDataHints?.length ? (
                          <div className="segment-detail"><span>{copy.segmentDataHints}</span><p>{segment.crmDataHints.join(", ")}</p></div>
                        ) : null}
                      </div>
                    </article>
                  );
                })}
              </div>
            </Panel>
          ) : null}

          {displayCalculation && scoringConfiguration ? (
            <Panel title={uxCopy.processTitle} variant="accent">
              {showCompactConfirmedState ? (
                <div className="customer-value-overview customer-value-overview--compact">
                  <div className="customer-value-overview__hero customer-value-overview__hero--compact">
                    <div>
                      <p className="customer-value-overview__eyebrow">
                        {modelTypeSummary === "new_customer_value"
                          ? copy.newCustomerValue
                          : modelTypeSummary === "existing_customer_value"
                            ? copy.existingCustomerValue
                            : copy.unclassifiedModel}
                      </p>
                      <h3 className="customer-value-overview__title">{uxCopy.compactSummaryTitle}</h3>
                      <div className="customer-value-overview__headline">
                        <StatusBadge tone="success" withCheck>
                          {uxCopy.processConfirmedBadge}
                        </StatusBadge>
                        <span>{uxCopy.compactSummaryReady}</span>
                      </div>
                    </div>
                    <div className="customer-value-primary-action customer-value-primary-action--compact">
                      <button className="button button-primary" type="button" onClick={() => openModelEditor()}>
                        {uxCopy.editModel}
                      </button>
                      <button className="button button-secondary" type="button" onClick={() => openPreviewPanel()}>
                        {uxCopy.testModelCollapsed}
                      </button>
                    </div>
                  </div>
                  <div className="customer-value-kpi-grid">
                    <article className="customer-value-kpi-card">
                      <strong>{editableScoringCriteria.length}</strong>
                      <span>{uxCopy.compactSummaryCriteria}</span>
                    </article>
                    <article className="customer-value-kpi-card">
                      <strong>{criterionDraftWeightTotal}%</strong>
                      <span>{uxCopy.compactSummaryWeighting}</span>
                    </article>
                    <article className="customer-value-kpi-card">
                      <strong>{segmentDraftForDisplay.length}</strong>
                      <span>{uxCopy.compactSummarySegments}</span>
                    </article>
                    <article className="customer-value-kpi-card">
                      <strong>{uxCopy.processConfirmedBadge}</strong>
                      <span>{uxCopy.compactSummaryScoring}</span>
                    </article>
                  </div>
                  <div className="customer-value-progress customer-value-progress--compact" aria-label={uxCopy.processTitle}>
                    {workflowSteps.map((step, index) => (
                      <div className={`customer-value-progress__step customer-value-progress__step--${step.state}`} key={step.key}>
                        <span className="customer-value-progress__marker" aria-hidden="true">{step.state === "done" ? "✓" : step.state === "current" ? "•" : String(index + 1)}</span>
                        <div>
                          <strong>{step.key === "criteria"
                            ? uxCopy.stepCriteria
                            : step.key === "weighting"
                              ? uxCopy.stepWeighting
                              : step.key === "scoring"
                                ? uxCopy.stepScoring
                                : step.key === "segments"
                                  ? uxCopy.stepSegments
                                  : uxCopy.stepFinish}</strong>
                          {step.state !== "done" ? (
                            <small>{step.state === "current" ? uxCopy.stepCurrent : uxCopy.stepOpen}</small>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="customer-value-overview">
                  <div className="customer-value-overview__hero">
                    <div>
                      <p className="form-note">{uxCopy.processBody}</p>
                      <div className="data-list">
                        <div>
                          <span>{copy.modelType}</span>
                          <strong>
                            {modelTypeSummary === "new_customer_value"
                              ? copy.newCustomerValue
                              : modelTypeSummary === "existing_customer_value"
                                ? copy.existingCustomerValue
                                : copy.unclassifiedModel}
                            {modelTypeSummaryIsDraft ? ` (${copy.unsavedModelTypeDraft})` : ""}
                          </strong>
                        </div>
                        <div>
                          <span>{copy.modelReadinessStatus}</span>
                          <strong>{modelReadiness ? copy.modelReadinessLabel(modelReadiness.status) : copy.notStored}</strong>
                        </div>
                        <div>
                          <span>{copy.criteriaWeights}</span>
                          <strong>{copy.activeWeightTotal(criterionDraftWeightTotal)}</strong>
                        </div>
                        <div>
                          <span>{copy.status}</span>
                          <strong>{hasConfirmedModel ? uxCopy.processConfirmedBadge : hasUnsavedScoringChanges ? uxCopy.processDraftBadge : uxCopy.processNeedsAttentionBadge}</strong>
                        </div>
                      </div>
                    </div>
                    <StatusBadge tone={hasConfirmedModel ? "success" : hasUnsavedScoringChanges ? "warning" : "default"} withCheck={hasConfirmedModel}>
                      {hasConfirmedModel ? uxCopy.processConfirmedBadge : hasUnsavedScoringChanges ? uxCopy.processDraftBadge : uxCopy.processNeedsAttentionBadge}
                    </StatusBadge>
                  </div>
                  <div className="customer-value-progress" aria-label={uxCopy.processTitle}>
                    {workflowSteps.map((step, index) => (
                      <div className={`customer-value-progress__step customer-value-progress__step--${step.state}`} key={step.key}>
                        <span className="customer-value-progress__marker" aria-hidden="true">{step.state === "done" ? "✓" : step.state === "current" ? "•" : String(index + 1)}</span>
                        <div>
                          <strong>{step.key === "criteria"
                            ? uxCopy.stepCriteria
                            : step.key === "weighting"
                              ? uxCopy.stepWeighting
                              : step.key === "scoring"
                                ? uxCopy.stepScoring
                                : step.key === "segments"
                                  ? uxCopy.stepSegments
                                  : uxCopy.stepFinish}</strong>
                          {step.state !== "done" ? (
                            <small>{step.state === "current" ? uxCopy.stepCurrent : uxCopy.stepOpen}</small>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                  {hasConfirmedModel ? (
                    <div className="customer-value-success-note">
                      <strong>{uxCopy.processReadyTitle}</strong>
                      <p>{uxCopy.processReadyBody}</p>
                    </div>
                  ) : hasUnsavedScoringChanges ? (
                    <div className="customer-value-success-note customer-value-success-note--draft">
                      <strong>{uxCopy.savedChangesInactive}</strong>
                      <p>{uxCopy.processDraftBody}</p>
                    </div>
                  ) : null}
                  <div className="customer-value-primary-action">
                    <div>
                      <strong>{uxCopy.primaryActionTitle}</strong>
                      <p className="form-note">{primaryCompletionAction ? primaryCompletionAction.reason : uxCopy.primaryActionDone}</p>
                    </div>
                    {primaryCompletionAction ? (
                      <button className="button button-primary" type="button" onClick={() => runCompletionAction(primaryCompletionAction.target)}>
                        {primaryCompletionAction.cta}
                      </button>
                    ) : null}
                  </div>
                  {completionActions.length > 0 ? (
                    <div className="customer-value-blocker-list">
                      {completionActions.map((action) => (
                        <article className="calculation-criterion" key={action.key}>
                          <div className="calculation-criterion__top">
                            <strong>{action.label}</strong>
                            <StatusBadge tone="warning">{uxCopy.stepCurrent}</StatusBadge>
                          </div>
                          <p className="form-error">{action.reason}</p>
                          <button className="button button-secondary" type="button" onClick={() => runCompletionAction(action.target)}>
                            {action.cta}
                          </button>
                        </article>
                      ))}
                    </div>
                  ) : null}
                </div>
              )}
            </Panel>
          ) : null}

          {showCompactConfirmedState ? (
            <>
              <Panel title={uxCopy.criteriaSectionTitle} variant="accent">
                <div className="customer-value-section-header">
                  <div>
                    <p className="form-note">{uxCopy.criteriaCompactBody}</p>
                  </div>
                  <button className="button button-primary customer-value-section-cta" type="button" onClick={() => openModelEditor("customer-value-criteria-section")}>
                    {uxCopy.editCriteria}
                  </button>
                </div>
                <div className="customer-value-criteria-grid">
                  {editableScoringCriteria.map((criterion) => (
                    <article className="customer-value-criterion-summary" key={criterion.id}>
                      <div className="customer-value-criterion-summary__header">
                        <strong>{criterion.name}</strong>
                        <StatusBadge tone="success" withCheck>
                          {criterion.criterionType === "legacy" && criterion.sourceStrategy === "legacy_assessment"
                            ? uxCopy.activeStatusLegacy
                            : uxCopy.activeStatusReady}
                        </StatusBadge>
                      </div>
                      <div className="customer-value-criterion-summary__meta">
                        <span>{criterion.weight}%</span>
                        <span>{criterion.unitType === "percentage" ? "%" : criterion.unitType === "EUR" ? "EUR" : criterion.unitType === "none" ? copy.notStored : criterion.unitType} · {criterion.scoringDirection === "higher_is_better"
                          ? uxCopy.criterionInfluenceHigher
                          : criterion.scoringDirection === "lower_is_better"
                            ? uxCopy.criterionInfluenceLower
                            : criterion.scoringDirection === "manual_bands"
                              ? uxCopy.criterionInfluenceManual
                              : uxCopy.criterionInfluenceUnknown}</span>
                      </div>
                      <div className="action-row">
                        <button className="button button-secondary" type="button" onClick={() => openModelEditor(`customer-value-criterion-${criterion.id}`)}>
                          {uxCopy.criterionEdit}
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              </Panel>

              {segmentDraftForDisplay.length > 0 ? (
                <Panel title={uxCopy.segmentsCompactTitle}>
                  <div className="customer-value-section-header">
                    <div>
                      <p className="form-note">{uxCopy.segmentsCompactBody}</p>
                    </div>
                    <button className="button button-primary customer-value-section-cta" type="button" onClick={() => openModelEditor("customer-value-segmentation-section")}>
                      {uxCopy.editSegments}
                    </button>
                  </div>
                  <div className="customer-value-segment-grid">
                    {segmentDraftForDisplay.map((segment) => (
                      <article className="customer-value-segment-summary" key={segment.id}>
                        <strong>{segment.name || copy.segmentName}</strong>
                        <p>{copy.fromTo(segment.minScore, segment.maxScore)}</p>
                      </article>
                    ))}
                  </div>
                </Panel>
              ) : null}

              {optionalNoticeCount > 0 ? (
                <details className="customer-value-optional-notices">
                  <summary>{uxCopy.optionalNoticeTitle}</summary>
                  <p className="form-note">{uxCopy.optionalNoticeBody}</p>
                  <ul className="clean-list">
                    {recommendedPerspectiveTypes.length > 0 ? <li>{uxCopy.optionalPerspectives(recommendedPerspectiveTypes.length)}</li> : null}
                    {translationIncomplete ? <li>{uxCopy.optionalTranslations}</li> : null}
                  </ul>
                </details>
              ) : null}

              {historicalCriteria.length > 0 ? (
                <details className="advanced-organization">
                  <summary>{uxCopy.historicalCriteriaTitle(historicalCriteria.length)}</summary>
                  <p className="form-note">{uxCopy.historicalCriteriaBody}</p>
                  <div className="customer-value-history-grid">
                    {historicalCriteria.map((criterion) => (
                      <article className="customer-value-criterion-summary customer-value-criterion-summary--historical" key={criterion.id}>
                        <div className="customer-value-criterion-summary__header">
                          <strong>{criterion.name}</strong>
                          <StatusBadge tone="default">{uxCopy.activeStatusHistorical}</StatusBadge>
                        </div>
                        <div className="customer-value-criterion-summary__meta">
                          <span>{criterion.weight}%</span>
                          <span>{uxCopy.historicalState}</span>
                        </div>
                      </article>
                    ))}
                  </div>
                </details>
              ) : null}
            </>
          ) : null}

          <div id="customer-value-criteria-section" />
          {showConfigurationEditor ? (
            <Panel title={copy.reviewTitle} variant="accent">
              <p className="form-note">{copy.configurationBody}</p>
              {hasConfirmedModel ? (
                <div className="customer-value-edit-banner">
                  <div>
                    <strong>{uxCopy.editModeTitle}</strong>
                    <p className="form-note">{uxCopy.editModeBody}</p>
                  </div>
                  <div className="customer-value-edit-banner__actions">
                    <button className="button button-secondary" type="button" disabled={!scoringDraftSaveAvailable || !hasUnsavedScoringChanges} onClick={() => void saveScoringConfigurationDraft()}>
                      {uxCopy.saveChanges}
                    </button>
                    <button className="button button-secondary" type="button" onClick={() => closeModelEditor()}>
                      {isPreviewPanelOpen ? uxCopy.hideTestModel : uxCopy.stopEditing}
                    </button>
                  </div>
                </div>
              ) : null}
              {isConfigurationLoading ? <p className="form-note">{copy.configurationLoading}</p> : null}
              {scoringConfiguration ? (
                <>
	                  <div className="data-list">
	                    <div>
	                      <span>{copy.modelType}</span>
	                      <strong>
	                        {modelTypeSummary === "new_customer_value"
	                          ? copy.newCustomerValue
	                          : modelTypeSummary === "existing_customer_value"
	                            ? copy.existingCustomerValue
	                            : copy.unclassifiedModel}
	                        {modelTypeSummaryIsDraft ? ` (${copy.unsavedModelTypeDraft})` : ""}
	                      </strong>
	                    </div>
	                    <div>
	                      <span>{copy.modelReadinessStatus}</span>
	                      <strong>{modelReadiness ? copy.modelReadinessLabel(modelReadiness.status) : copy.notStored}</strong>
	                    </div>
	                    <div>
	                      <span>{copy.status}</span>
	                      <strong>{scoringConfiguration.model.status}</strong>
	                    </div>
	                    <div>
	                      <span>{copy.criteriaWeights}</span>
	                      <strong>{copy.activeWeightTotal(criterionDraftWeightTotal)}</strong>
                    </div>
                    <div>
                      <span>{copy.versionsTitle}</span>
                      <strong>{copy.versionSummary(scoringVersionSummaries.filter((summary) => summary.previewActive).length, scoringVersionSummaries.length)}</strong>
                    </div>
                  </div>
	                  <div className="criteria-meta-grid">
	                    <label className="criteria-meta-box editor-field-card">
	                      <span>{copy.modelType}<FieldHelp content={helpContent("modelType")} label={copy.modelType} /></span>
	                      <select
	                        disabled={scoringConfiguration.model.modelTypeLocked}
	                        value={modelTypeSelectValue}
	                        onChange={(event) => {
	                          if (event.target.value) updateConfigurationModelType(event.target.value as CustomerValueModelType);
	                        }}
	                      >
	                        <option value="" disabled>{copy.unclassifiedModel}</option>
	                        <option value="existing_customer_value">{copy.existingCustomerValue}</option>
	                        <option value="new_customer_value">{copy.newCustomerValue}</option>
	                      </select>
	                    </label>
	                  </div>
	                  <p className="form-note">{copy.modelReadinessHelp}</p>
                  {modelReadiness?.status === "scoring_incomplete" ? (
                    <p className="form-note">
                      {copy.scoringIncompleteExplanation}
                      {readinessBlockingCriterionNames.length > 0 ? ` ${copy.criteriaRequiringScoring}: ${readinessBlockingCriterionNames.join(", ")}.` : ""}
                    </p>
                  ) : null}
                  {!scoringConfiguration.validation.ok ? (
                    <p className="form-error">
                      {copy.configurationInvalid} {scoringConfiguration.validation.errors.join(", ")}
                    </p>
                  ) : null}
                  <div id="customer-value-weighting-section" />
                  <div className={`weighting-status weighting-status--${criterionWeightStatusTone}`}>
                    <strong>{copy.completionStepWeighting}</strong>
                    <p className="form-note">{copy.weightingRequiredExplanation}</p>
                    <div className="data-list">
                      <div>
                        <span>{copy.currentWeighting}</span>
                        <strong>{copy.activeWeightTotal(criterionDraftWeightTotal)}</strong>
                      </div>
                      <div>
                        <span>{copy.requiredWeighting}</span>
                        <strong>100%</strong>
                      </div>
                      <div>
                        <span>{copy.status}</span>
                        <strong>{copy.activeWeightDifference(criterionDraftWeightTotal)}</strong>
                      </div>
                    </div>
                    <p className={criterionDraftWeightTotal === 100 ? "form-note" : "form-error"}>
                      {criterionDraftWeightTotal === 100
                        ? copy.activeWeightComplete
                        : criterionDraftWeightTotal > 100
                          ? copy.activeWeightTooHigh(criterionDraftWeightDifference)
                          : copy.activeWeightTooLow(criterionDraftWeightDifference)}
                    </p>
                    {criterionDraftWeightTotal !== 100 ? <p className="form-note">{copy.activeWeightDraftIncomplete}</p> : null}
                  </div>
                  <p className="form-note">{copy.noAutomaticRedistribution}</p>

                  <details className="advanced-organization">
                    <summary>{copy.advancedOrganization}</summary>
                    <p className="form-note">
                      {copy.advancedOrganizationBody} <FieldHelp content={helpContent("category")} label={copy.category} />
                    </p>
                    <h3>{copy.manageCategories}</h3>
                    <div className="calculation-criteria-list">
                    {categoryDraft.slice().sort((left, right) => left.rank - right.rank).map((category) => (
                      <article className="calculation-criterion" key={category.id}>
                        <div className="calculation-criterion__top">
                          <strong>{category.name || copy.categoryName}</strong>
                          <StatusBadge tone={category.active ? "success" : "warning"}>
                            {category.active ? copy.activeField : copy.deactivateCategory}
                          </StatusBadge>
                        </div>
                        <div className="criteria-meta-grid">
                          <label className="criteria-meta-box editor-field-card">
                            <span>{copy.categoryName}<FieldHelp content={helpContent("category")} label={copy.categoryName} /></span>
                            <input value={category.name} onChange={(event) => updateCategoryDraft(category.id, { name: event.target.value })} />
                          </label>
                          <label className="criteria-meta-box editor-field-card">
                            {copy.descriptionLabel}
                            <input value={category.description ?? ""} onChange={(event) => updateCategoryDraft(category.id, { description: event.target.value })} />
                          </label>
                          <label className="criteria-meta-box editor-field-card">
                            {copy.rank}
                            <input type="number" min="1" step="1" value={category.rank} onChange={(event) => updateCategoryDraft(category.id, { rank: Number(event.target.value) })} />
                          </label>
                        </div>
                        <div className="action-row">
                          <button className="button button-secondary" type="button" disabled={isWorking} onClick={() => saveCategoryDraft(category)}>{copy.saveCategory}</button>
                          <button className="button button-secondary" type="button" disabled={isWorking} onClick={() => category.active ? deactivateCategoryDraft(category) : saveCategoryDraft({ ...category, active: true })}>
                            {category.active ? copy.deactivateCategory : copy.reactivateCategory}
                          </button>
                          <button className="button button-secondary" type="button" disabled={isWorking} onClick={() => deleteCategoryDraft(category.id)}>{copy.deleteCategory}</button>
                        </div>
                      </article>
                    ))}
                    </div>
                    <button className="button button-secondary" type="button" disabled={isWorking} onClick={addCategoryDraft}>{copy.addCategory}</button>
                  </details>

                  <div id="customer-value-scoring-section" />
                  <h3>{uxCopy.criteriaSectionTitle}</h3>
                  <p className="form-note">{uxCopy.criteriaSectionBody}</p>
                  <div className="data-list">
                    <div>
                      <span>{copy.activeWeightSummary}</span>
                      <strong>{copy.activeWeightTotal(criterionDraftWeightTotal)}</strong>
                    </div>
                    <div>
                      <span>{copy.status}</span>
                      <strong>{copy.activeWeightDifference(criterionDraftWeightTotal)}</strong>
                    </div>
                  </div>
                  <button className="button button-secondary" type="button" disabled={isWorking} onClick={startCriterionAddFlow}>{copy.addCriterion}</button>
                  {criterionAddDraft ? (
                    <article className="calculation-criterion">
                      <div className="calculation-criterion__top">
                        <strong>{copy.addCriterionFlowTitle}</strong>
                        <StatusBadge tone="warning">{copy.draftOnly}</StatusBadge>
                      </div>
                      <p className="form-note">{copy.addCriterionFlowBody}</p>
                      <div className="criteria-meta-grid">
                        <label className="criteria-meta-box editor-field-card">
                          <span>{copy.criterion}<FieldHelp content={helpContent("criterion")} label={copy.criterion} /></span>
                          <input value={criterionAddDraft.name} onChange={(event) => updateCriterionAddDraft({ name: event.target.value, suggestion: undefined })} />
                        </label>
                        <label className="criteria-meta-box editor-field-card">
                          <span>{copy.evaluationType}<FieldHelp content={helpContent("evaluationType")} label={copy.evaluationType} /></span>
                          <select value={criterionAddDraft.criterionType} onChange={(event) => updateCriterionAddDraft({ criterionType: event.target.value as CriterionAddDraft["criterionType"], suggestion: undefined })}>
                            {criterionTypeOptions.filter((option) => canSelectCriterionTypeForNewCriterion(option)).map((option) => <option key={option} value={option}>{criterionLabel(CRITERION_TYPE_LABELS, option, language)}</option>)}
                          </select>
                        </label>
                        <label className="criteria-meta-box editor-field-card">
                          <span>{copy.valueType}<FieldHelp content={helpContent("valueType")} label={copy.valueType} /></span>
                          <select value={criterionAddDraft.valueType} onChange={(event) => updateCriterionAddDraft({ valueType: event.target.value as CriterionAddDraft["valueType"], suggestion: undefined })}>
                            {valueTypeOptions.map((option) => <option key={option} value={option}>{criterionLabel(VALUE_TYPE_LABELS, option, language)}</option>)}
                          </select>
                        </label>
                        <label className="criteria-meta-box editor-field-card">
                          <span>{copy.unit}<FieldHelp content={helpContent("unitType")} label={copy.unit} /></span>
                          <select value={criterionAddDraft.unitType} onChange={(event) => updateCriterionAddDraft({ unitType: event.target.value as CriterionAddDraft["unitType"], suggestion: undefined })}>
                            {unitTypeOptions.map((option) => <option key={option} value={option}>{criterionLabel(UNIT_TYPE_LABELS, option, language)}</option>)}
                          </select>
                        </label>
                        <label className="criteria-meta-box editor-field-card">
                          <span>{copy.scoringDirection}<FieldHelp content={helpContent("scoringDirection")} label={copy.scoringDirection} /></span>
                          <select value={criterionAddDraft.scoringDirection} onChange={(event) => updateCriterionAddDraft({ scoringDirection: event.target.value as CriterionAddDraft["scoringDirection"], suggestion: undefined })}>
                            {scoringDirectionOptions.map((option) => <option key={option} value={option}>{criterionLabel(SCORING_DIRECTION_LABELS, option, language)}</option>)}
                          </select>
                        </label>
                        <label className="criteria-meta-box editor-field-card">
                          <span>{copy.weighting}<FieldHelp content={helpContent("weight")} label={copy.weighting} /></span>
                          <input type="number" min="0" max="100" step="0.01" value={criterionAddDraft.weight} onChange={(event) => updateCriterionAddDraft({ weight: Number(event.target.value) })} />
                        </label>
                        <label className="criteria-meta-box editor-field-card">
                          <span>{copy.descriptionLabel}<FieldHelp content={helpContent("descriptionSuggestion")} label={copy.descriptionSuggestion} /></span>
                          <textarea value={criterionAddDraft.description} onChange={(event) => updateCriterionAddDraft({ description: event.target.value })} />
                        </label>
                      </div>
                      <div className="action-row">
                        <button className="button button-secondary" type="button" disabled={isWorking || isCreatingCriterionDraft || !criterionAddDraft.name.trim()} onClick={suggestCriterionDescription}>{copy.requestDescriptionSuggestion}</button>
                        <button className="button button-primary" type="button" disabled={isWorking || isCreatingCriterionDraft || !criterionAddDraft.name.trim()} onClick={saveCriterionAddDraft}>
                          {isCreatingCriterionDraft ? copy.criterionCreating : copy.addCriterionDraft}
                        </button>
                        <button className="button button-secondary" type="button" disabled={isWorking} onClick={() => { setCriterionAddDraft(undefined); setCriterionAddError(undefined); }}>{copy.cancel}</button>
                      </div>
                      {criterionAddError ? <p className="form-error">{criterionAddError}</p> : null}
                      {criterionAddDraft.suggestion ? (
                        <div className="calculation-criterion">
                          <div className="calculation-criterion__top">
                            <strong>{copy.descriptionSuggestion}</strong>
                            <StatusBadge tone={criterionAddDraft.suggestion.confidence === "strong_context" ? "success" : "warning"}>
                              {criterionAddDraft.suggestion.confidence === "strong_context"
                                ? copy.confidenceStrongContext
                                : criterionAddDraft.suggestion.confidence === "partial_context"
                                  ? copy.confidencePartialContext
                                  : copy.confidenceGenericFallback}
                            </StatusBadge>
                          </div>
                          <p>{criterionAddDraft.suggestion.suggestedDescription}</p>
                          <p className="form-note"><strong>{copy.suggestionSourceSummary}:</strong> {criterionAddDraft.suggestion.sourceSummary}</p>
                          {criterionAddDraft.suggestion.warnings.includes("existing_description_present") ? <p className="form-note">{copy.existingDescriptionKept}</p> : null}
                          <button className="button button-secondary" type="button" onClick={() => updateCriterionAddDraft({ description: criterionAddDraft.suggestion?.suggestedDescription ?? criterionAddDraft.description })}>{copy.applyDescriptionSuggestion}</button>
                        </div>
                      ) : null}
                    </article>
                  ) : null}
                  <div className="calculation-criteria-list">
                    {[
                      { title: copy.scoringStatusTodo, items: scoringTodoCriteria, tone: "warning" as const },
                      { title: copy.scoringStatusDraft, items: scoringDraftCriteria, tone: "warning" as const },
                      { title: copy.scoringStatusReady, items: scoringReadyCriteria, tone: "success" as const }
                    ].map((group) => group.items.length > 0 ? (
                      <article className="calculation-criterion" key={group.title}>
                        <div className="calculation-criterion__top">
                          <strong>{group.title}</strong>
                          <StatusBadge tone={group.tone}>{group.items.length}</StatusBadge>
                        </div>
                        <div className="action-row">
                          {group.items.map((criterion) => (
                            <button className="button button-secondary" type="button" key={criterion.id} onClick={() => focusCriterionScoring(criterion.id)}>
                              {criterion.name} · {copy.configureCriterionEvaluation}
                            </button>
                          ))}
                        </div>
                      </article>
                    ) : null)}
                    {editableScoringCriteria.map((criterion) => (
                      <article id={`customer-value-criterion-${criterion.id}`} className={`calculation-criterion${highlightedCriterionId === criterion.id ? " calculation-criterion--highlighted" : ""}`} key={criterion.id}>
                        <div className="calculation-criterion__top">
                          <div>
                            <span>{uxCopy.criterionBusinessSummary}</span>
                            <strong>{criterion.name}</strong>
                          </div>
                          <StatusBadge tone={criterionScoringIsConfirmed(criterion) ? "success" : criterionScoringHasCompleteDraftBands(criterion) ? "warning" : "default"}>
                            {criterionScoringIsConfirmed(criterion)
                              ? criterion.criterionType === "legacy" && criterion.sourceStrategy === "legacy_assessment"
                                ? uxCopy.activeStatusLegacy
                                : uxCopy.activeStatusReady
                              : criterionScoringHasCompleteDraftBands(criterion)
                                ? uxCopy.activeStatusDraft
                                : uxCopy.activeStatusTodo}
                          </StatusBadge>
                        </div>
                        <p className="form-note">{criterion.description}</p>
                        <div className="data-list">
                          <div><span>{copy.weighting}</span><strong>{criterion.weight}%</strong></div>
                          <div><span>{copy.evaluationType}</span><strong>{localizedCriterionTypeLabel(criterion.criterionType)}</strong></div>
                          <div><span>{copy.unit}</span><strong>{criterion.unitType === "percentage" ? "%" : criterion.unitType === "EUR" ? "EUR" : criterion.unitType === "none" ? copy.notStored : criterion.unitType}</strong></div>
                          <div><span>{copy.scoringDirection}</span><strong>{criterion.scoringDirection === "higher_is_better"
                            ? uxCopy.criterionInfluenceHigher
                            : criterion.scoringDirection === "lower_is_better"
                              ? uxCopy.criterionInfluenceLower
                              : criterion.scoringDirection === "manual_bands"
                                ? uxCopy.criterionInfluenceManual
                                : uxCopy.criterionInfluenceUnknown}</strong></div>
                        </div>
                        <div className="action-row">
                          <button className="button button-secondary" type="button" onClick={() => toggleCriterionDetails(criterion.id)}>
                            {expandedCriterionIds.has(criterion.id) ? copy.closeCriterion : uxCopy.criterionEdit}
                          </button>
                          <button className="button button-secondary" type="button" disabled={isWorking} onClick={() => duplicateCriterionDraft(criterion)}>{copy.duplicateCriterion}</button>
                          <button className="button button-secondary" type="button" disabled={isWorking} onClick={() => setCriterionActiveDraft(criterion.id, !criterion.active)}>
                            {criterion.active ? copy.deactivateCriterion : copy.reactivateCriterion}
                          </button>
                          {criterion.scoringConfigurationStatus === "draft" ? (
                            <button className="button button-secondary" type="button" disabled={isWorking} onClick={() => deleteCriterionDraft(criterion.id)}>{copy.deleteCriterion}</button>
                          ) : null}
                        </div>
                        {criterion.criterionType === "legacy" ? (
                          <p className="form-note" title={CRITERION_TOOLTIPS.legacyReplacement[language]}>
                            {criterionScoringIsConfirmed(criterion) ? uxCopy.activeStatusLegacy : copy.replaceLegacyHint}
                          </p>
                        ) : null}
                        {expandedCriterionIds.has(criterion.id) ? (
                        <>
                        <p className="form-note">{copy.atomicSaveNote}</p>
                        <div className="criteria-meta-grid">
                          <label className="criteria-meta-box editor-field-card">
                            {copy.criterion}
                            <input
                              value={criterion.name}
                              onChange={(event) => updateCriterionDraft(criterion.id, { name: event.target.value })}
                            />
                          </label>
                          <div className="criteria-meta-box editor-field-card">
                            <details>
                              <summary>{copy.advancedOrganization}</summary>
                              <p className="form-note">{copy.advancedOrganizationBody}</p>
                              <label>
                                <span>{copy.category}<FieldHelp content={helpContent("category")} label={copy.category} /></span>
                                <select
                                  value={criterion.categoryId ?? ""}
                                  onChange={(event) => updateCriterionDraft(criterion.id, { categoryId: event.target.value || null })}
                                >
                                  <option value="">{copy.uncategorized}</option>
                                  {categoryDraft.filter((category) => category.active).map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
                                </select>
                              </label>
                              <button
                                className="button button-secondary"
                                type="button"
                                disabled={isWorking}
                                onClick={() => {
                                  setInlineCategoryForCriterionId(criterion.id);
                                  setInlineCategoryDraft({ name: "", description: "", rank: categoryDraft.length + 1 });
                                }}
                              >
                                {copy.addCategoryInline}
                              </button>
                              {inlineCategoryForCriterionId === criterion.id ? (
                                <div className="criteria-meta-box editor-field-card">
                                  {copy.addCategoryInline}
                                  <label>
                                    {copy.categoryName}
                                    <input
                                      value={inlineCategoryDraft.name}
                                      placeholder={copy.categoryName}
                                      onChange={(event) => setInlineCategoryDraft((current) => ({ ...current, name: event.target.value }))}
                                    />
                                  </label>
                                  <label>
                                    {copy.categoryDescription}
                                    <input
                                      value={inlineCategoryDraft.description}
                                      placeholder={copy.categoryDescription}
                                      onChange={(event) => setInlineCategoryDraft((current) => ({ ...current, description: event.target.value }))}
                                    />
                                  </label>
                                  <label>
                                    {copy.rank}
                                    <input
                                      type="number"
                                      min="1"
                                      step="1"
                                      value={inlineCategoryDraft.rank}
                                      onChange={(event) => setInlineCategoryDraft((current) => ({ ...current, rank: Number(event.target.value) }))}
                                    />
                                  </label>
                                  <div className="action-row">
                                    <button className="button button-secondary" type="button" disabled={isWorking || !inlineCategoryDraft.name.trim()} onClick={() => void createInlineCategoryForCriterion(criterion.id)}>
                                      {copy.createCategoryAndSelect}
                                    </button>
                                    <button className="button button-secondary" type="button" disabled={isWorking} onClick={() => setInlineCategoryForCriterionId(undefined)}>
                                      {copy.cancel}
                                    </button>
                                  </div>
                                </div>
                              ) : null}
                            </details>
                          </div>
                          <label className="criteria-meta-box editor-field-card">
                            {copy.descriptionLabel}
                            <textarea
                              value={criterion.description ?? ""}
                              onChange={(event) => updateCriterionDraft(criterion.id, { description: event.target.value })}
                            />
                          </label>
                          <label className="criteria-meta-box editor-field-card">
                            <span>{copy.weighting}<FieldHelp content={helpContent("weight")} label={copy.weighting} /></span>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              value={criterion.weight}
                              onChange={(event) => updateCriterionDraft(criterion.id, { weight: Number(event.target.value) })}
                            />
                          </label>
                          <label className="criteria-meta-box editor-field-card">
                            {copy.rank}
                            <input
                              type="number"
                              min="1"
                              step="1"
                              value={criterion.rank}
                              onChange={(event) => updateCriterionDraft(criterion.id, { rank: Number(event.target.value) })}
                            />
                          </label>
                          <label className="criteria-meta-box editor-field-card">
                            <span>{copy.evaluationType}<FieldHelp content={helpContent("evaluationType")} label={copy.evaluationType} /></span>
                            <select
                              value={criterion.criterionType}
                              onChange={(event) => updateCriterionDraft(criterion.id, { criterionType: event.target.value as RuntimeCustomerValueCriterionConfiguration["criterionType"] })}
                            >
                              {criterionTypeOptions.filter((option) => option === criterion.criterionType || canSelectCriterionTypeForNewCriterion(option)).map((option) => <option key={option} value={option}>{criterionLabel(CRITERION_TYPE_LABELS, option, language)}</option>)}
                            </select>
                          </label>
                          {shouldShowQuantitativeFields(criterion.criterionType) ? (
                          <>
                          <label className="criteria-meta-box editor-field-card">
                            <span>{copy.valueType}<FieldHelp content={helpContent("valueType")} label={copy.valueType} /></span>
                            <select
                              value={criterion.valueType}
                              onChange={(event) => updateCriterionDraft(criterion.id, { valueType: event.target.value as RuntimeCustomerValueCriterionConfiguration["valueType"] })}
                            >
                              {valueTypeOptions.map((option) => <option key={option} value={option}>{criterionLabel(VALUE_TYPE_LABELS, option, language)}</option>)}
                            </select>
                          </label>
                          <label className="criteria-meta-box editor-field-card">
                            <span>{copy.unit}<FieldHelp content={helpContent("unitType")} label={copy.unit} /></span>
                            <select
                              value={criterion.unitType}
                              onChange={(event) => updateCriterionDraft(criterion.id, { unitType: event.target.value as RuntimeCustomerValueCriterionConfiguration["unitType"] })}
                            >
                              {unitTypeOptions.map((option) => <option key={option} value={option}>{criterionLabel(UNIT_TYPE_LABELS, option, language)}</option>)}
                            </select>
                          </label>
                          <label className="criteria-meta-box editor-field-card">
                            <span>{copy.scoringDirection}<FieldHelp content={helpContent("scoringDirection")} label={copy.scoringDirection} /></span>
                            <select
                              value={criterion.scoringDirection}
                              onChange={(event) => updateCriterionDraft(criterion.id, { scoringDirection: event.target.value as RuntimeCustomerValueCriterionConfiguration["scoringDirection"] })}
                            >
                              {scoringDirectionOptions.map((option) => <option key={option} value={option}>{criterionLabel(SCORING_DIRECTION_LABELS, option, language)}</option>)}
                            </select>
                          </label>
                          </>
                          ) : <p className="form-note">{copy.quantitativeFieldsOnly}</p>}
                          <label className="criteria-meta-box editor-field-card">
                            <span>{copy.sourceStrategy}<FieldHelp content={helpContent("sourceStrategy")} label={copy.sourceStrategy} /></span>
                            <select
                              value={criterion.sourceStrategy}
                              onChange={(event) => updateCriterionDraft(criterion.id, { sourceStrategy: event.target.value as RuntimeCustomerValueCriterionConfiguration["sourceStrategy"] })}
                            >
                              {sourceStrategyOptions.map((option) => <option key={option} value={option}>{criterionLabel(SOURCE_STRATEGY_LABELS, option, language)}</option>)}
                            </select>
                          </label>
                          <label className="criteria-meta-box editor-field-card">
                            <span>{copy.configurationStatus}<FieldHelp content={helpContent("configurationStatus")} label={copy.configurationStatus} /></span>
                            <select
                              value={criterion.scoringConfigurationStatus}
                              onChange={(event) => updateCriterionDraft(criterion.id, { scoringConfigurationStatus: event.target.value as RuntimeCustomerValueCriterionConfiguration["scoringConfigurationStatus"] })}
                            >
                              {configurationStatusOptions.map((option) => <option key={option} value={option}>{criterionLabel(SCORING_STATUS_LABELS, option, language)}</option>)}
                            </select>
                          </label>
                          <label className="criteria-meta-box editor-field-card">
                            <span>{copy.requiredField}<FieldHelp content={helpContent("requiredField")} label={copy.requiredField} /></span>
                            <input
                              type="checkbox"
                              checked={criterion.required}
                              onChange={(event) => updateCriterionDraft(criterion.id, { required: event.target.checked })}
                            />
                          </label>
                          <label className="criteria-meta-box editor-field-card">
                            <span>{copy.activeField}<FieldHelp content={helpContent("activeField")} label={copy.activeField} /></span>
                            <input
                              type="checkbox"
                              checked={criterion.active}
                              onChange={(event) => updateCriterionDraft(criterion.id, { active: event.target.checked })}
                            />
                          </label>
                        </div>
                        <div className="calculation-criterion">
	                          <div className="calculation-criterion__top">
	                            <strong>{copy.scoringConfigurationTitle}</strong>
                              {criterionScoringIsConfirmed(criterion) ? (
                                <button className="button button-secondary" type="button" disabled={isWorking} onClick={() => startNewScoringDraftVersion(criterion)}>
                                  {copy.editNewScoringDraft}
                                </button>
                              ) : null}
		                            <StatusBadge tone={criterionScoringIsConfirmed(criterion) ? "success" : criterionScoringHasCompleteDraftBands(criterion) ? "warning" : "default"}>
	                              {criterionScoringIsConfirmed(criterion)
	                                ? copy.confirmed
	                                : criterionScoringHasCompleteDraftBands(criterion)
	                                  ? criterionLabel(SCORING_STATUS_LABELS, criterion.scoringConfigurationStatus, language)
	                                  : copy.missingBandConfiguration}
	                            </StatusBadge>
                          </div>
                          {criterion.criterionType === "legacy" ? (
                            <>
                              <p className="form-note">
	                                {copy.legacyNotice} <FieldHelp content={helpContent("legacyReplacement")} label={copy.configureScoringLogic} />
                              </p>
                              {!criterionUsesNumericBands(criterion) && !criterionUsesQualitativeBands(criterion) && !criterionUsesBooleanBands(criterion) ? (
                                <div className="action-row">
                                  <button className="button button-secondary" type="button" disabled={isWorking} onClick={() => configureLegacyDirectRatingDraft(criterion)}>
                                    {copy.directRatingSuggestion}
                                  </button>
                                  <span className="form-note">{copy.directRatingHelp}</span>
                                </div>
                              ) : null}
                              {legacyReplacementDraft?.sourceCriterionId === criterion.id ? (
                                <div className="replacement-flow">
                                  <div className="calculation-criterion__top">
                                    <strong>{copy.replacementFlowTitle}</strong>
                                    <StatusBadge tone="warning">{copy.draftOnly}</StatusBadge>
                                  </div>
                                  <p className="form-note">{copy.replacementIntro}</p>
                                  <div className="replacement-flow__steps" aria-label={copy.replacementFlowTitle}>
                                    {([
                                      ["explain", copy.replacementStepExplain],
                                      ["criteria", copy.replacementStepCriteria],
                                      ["weights", copy.replacementStepWeights],
                                      ["review", copy.replacementStepReview]
                                    ] as const).map(([step, label]) => (
                                      <button
                                        key={step}
                                        className={`replacement-flow__step ${legacyReplacementDraft.step === step ? "is-active" : ""}`}
                                        type="button"
                                        onClick={() => updateLegacyReplacementDraft({ step })}
                                      >
                                        {label}
                                      </button>
                                    ))}
                                  </div>
                                  <h4>{copy.replacementCriteriaSuggestion}</h4>
                                  <div className="calculation-criteria-list">
                                    {legacyReplacementDraft.criteria.map((replacementCriterion) => (
                                      <article className="calculation-criterion" key={replacementCriterion.localId}>
                                        <div className="criteria-meta-grid">
                                          <label className="criteria-meta-box editor-field-card">
                                            {copy.criterion}
                                            <input value={replacementCriterion.name} onChange={(event) => updateLegacyReplacementCriterion(replacementCriterion.localId, { name: event.target.value })} />
                                          </label>
                                          <label className="criteria-meta-box editor-field-card">
                                            {copy.descriptionLabel}
                                            <textarea value={replacementCriterion.description} onChange={(event) => updateLegacyReplacementCriterion(replacementCriterion.localId, { description: event.target.value })} />
                                          </label>
                                          <label className="criteria-meta-box editor-field-card">
                                            <span>{copy.valueType}<FieldHelp content={helpContent("valueType")} label={copy.valueType} /></span>
                                            <select value={replacementCriterion.valueType} onChange={(event) => updateLegacyReplacementCriterion(replacementCriterion.localId, { valueType: event.target.value as LegacyReplacementDraft["criteria"][number]["valueType"] })}>
                                              {(["currency", "percentage", "decimal", "integer", "count"] as const).map((option) => <option key={option} value={option}>{criterionLabel(VALUE_TYPE_LABELS, option, language)}</option>)}
                                            </select>
                                          </label>
                                          <label className="criteria-meta-box editor-field-card">
                                            <span>{copy.weighting}<FieldHelp content={helpContent("weight")} label={copy.weighting} /></span>
                                            <input type="number" min="0" max="100" step="0.01" value={replacementCriterion.weight} onChange={(event) => updateLegacyReplacementCriterion(replacementCriterion.localId, { weight: Number(event.target.value) })} />
                                          </label>
                                        </div>
                                      </article>
                                    ))}
                                  </div>
                                  <div className="data-list">
                                    <div>
                                      <span>{copy.activeWeightSummary}</span>
                                      <strong>{copy.replacementWeightSummary(
                                        legacyReplacementDraft.criteria.reduce((sum, replacementCriterion) => sum + Number(replacementCriterion.weight || 0), 0),
                                        Number(criterion.weight || 0)
                                      )}</strong>
                                    </div>
                                    <div>
                                      <span>{copy.status}</span>
                                      <strong>{legacyReplacementDraft.splitConfirmed ? copy.confirmed : copy.replacementDraftBlocked}</strong>
                                    </div>
                                  </div>
                                  <div className="action-row">
                                    <button className="button button-secondary" type="button" onClick={applySuggestedReplacementSplit}>{copy.replacementApplyWeights}</button>
                                    <label className="form-note">
                                      <input
                                        type="checkbox"
                                        checked={legacyReplacementDraft.splitConfirmed}
                                        onChange={(event) => updateLegacyReplacementDraft({ splitConfirmed: event.target.checked })}
                                      />{" "}
                                      {copy.replacementSplitConfirmed}
                                    </label>
                                  </div>
                                  <div className="action-row">
                                    <button className="button button-primary" type="button" disabled={isWorking || !legacyReplacementDraft.splitConfirmed} onClick={applyLegacyReplacementDraft}>
                                      {copy.replacementDraftApply}
                                    </button>
                                    <button className="button button-secondary" type="button" disabled={isWorking} onClick={() => setLegacyReplacementDraft(undefined)}>
                                      {copy.replacementDraftCancel}
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <button className="button button-secondary" type="button" disabled={isWorking} onClick={() => startLegacyReplacementDraft(criterion)}>
                                  {copy.replaceLegacyCriterion}
                                </button>
                              )}
                            </>
                          ) : null}
                          {criterion.criterionType === "calculated" ? (
                            <p className="form-note">{copy.notAvailableYet}</p>
                          ) : null}
                          {criterionUsesNumericBands(criterion) ? (
                            <>
                              <p className="form-note" title={CRITERION_TOOLTIPS.scoringDirection[language]}>{CRITERION_TOOLTIPS.scoringDirection[language]}</p>
                              <button className="button button-secondary" type="button" onClick={() => setBandDraft((current) => ({ ...current, [criterion.id]: createTenPointBandSuggestion(criterion.id, criterion.scoringDirection) }))}>
                                {copy.quantitativeSuggestion}
                              </button>
                              <div className="score-template-list">
                                {bandsForCriterion(criterion).map((band) => (
                                  <article className="score-template-card" key={band.id}>
                                    <div className="criteria-meta-grid">
                                      <label className="criteria-meta-box editor-field-card">
                                        <span>{copy.points}<FieldHelp content={helpContent("points")} label={copy.points} /></span>
                                        <input
                                          type="number"
                                          min="1"
                                          max="10"
                                          step="1"
                                          value={band.points ?? ""}
                                          onChange={(event) => updateBandDraft(criterion.id, band.id, { points: parseOptionalBandNumber(event.target.value) })}
                                        />
                                      </label>
                                      <label className="criteria-meta-box editor-field-card">
                                        <span>{copy.fromValue}<FieldHelp content={helpContent("valueRange")} label={copy.fromValue} /></span>
                                        <input
                                          type="number"
                                          step={criterion.valueType === "percentage" || criterion.unitType === "percentage" ? "0.01" : "1"}
                                          value={band.minValue ?? ""}
                                          onChange={(event) => updateBandDraft(criterion.id, band.id, { minValue: parseOptionalBandNumber(event.target.value) })}
                                        />
                                      </label>
                                      <label className="criteria-meta-box editor-field-card">
                                        <span>{copy.toValue}<FieldHelp content={helpContent("valueRange")} label={copy.toValue} /></span>
                                        <input
                                          type="number"
                                          step={criterion.valueType === "percentage" || criterion.unitType === "percentage" ? "0.01" : "1"}
                                          value={band.maxValue ?? ""}
                                          onChange={(event) => updateBandDraft(criterion.id, band.id, { maxValue: parseOptionalBandNumber(event.target.value) })}
                                        />
                                      </label>
                                      <div className="criteria-meta-box editor-field-card">
                                        <span>{copy.bandPreview}<FieldHelp content={helpContent("valueRange")} label={copy.bandPreview} /></span>
                                        <strong>{bandPreview(criterion, band)}</strong>
                                      </div>
                                    </div>
                                    <details>
                                      <summary>{uxCopy.advancedSettings}</summary>
                                      <div className="criteria-meta-grid">
                                        <label className="criteria-meta-box editor-field-card">
                                          <span>{copy.openLower}<FieldHelp content={helpContent("rangeBoundaries")} label={copy.openLower} /></span>
                                          <input type="checkbox" checked={band.minValue === null || band.minValue === undefined} onChange={(event) => updateBandDraft(criterion.id, band.id, { minValue: event.target.checked ? null : 0 })} />
                                        </label>
                                        <label className="criteria-meta-box editor-field-card">
                                          <span>{copy.openUpper}<FieldHelp content={helpContent("rangeBoundaries")} label={copy.openUpper} /></span>
                                          <input type="checkbox" checked={band.maxValue === null || band.maxValue === undefined} onChange={(event) => updateBandDraft(criterion.id, band.id, { maxValue: event.target.checked ? null : 0 })} />
                                        </label>
                                        <label className="criteria-meta-box editor-field-card">
                                          <span>{copy.minInclusive}<FieldHelp content={helpContent("rangeBoundaries")} label={copy.minInclusive} /></span>
                                          <input type="checkbox" checked={band.minInclusive} onChange={(event) => updateBandDraft(criterion.id, band.id, { minInclusive: event.target.checked })} />
                                        </label>
                                        <label className="criteria-meta-box editor-field-card">
                                          <span>{copy.maxInclusive}<FieldHelp content={helpContent("rangeBoundaries")} label={copy.maxInclusive} /></span>
                                          <input type="checkbox" checked={band.maxInclusive} onChange={(event) => updateBandDraft(criterion.id, band.id, { maxInclusive: event.target.checked })} />
                                        </label>
                                      </div>
                                    </details>
                                  </article>
                                ))}
                              </div>
                            </>
                          ) : null}
                          {describeCustomerValueScoringIssues(criterion, bandsForCriterion(criterion), language).length > 0 ? (
                            <div className="customer-value-inline-issues" role="status" aria-live="polite">
                              <strong>{uxCopy.criterionValidationTitle}</strong>
                              <ul className="clean-list">
                                {describeCustomerValueScoringIssues(criterion, bandsForCriterion(criterion), language).map((issue) => (
                                  <li key={issue}>{issue}</li>
                                ))}
                              </ul>
                            </div>
                          ) : null}
                          {criterionUsesQualitativeBands(criterion) ? (
                            <>
                              <div className="score-template-list">
                                {bandsForCriterion(criterion).map((band) => (
                                  <div className="criteria-meta-grid" key={band.id}>
                                    <label className="criteria-meta-box editor-field-card">
                                      {copy.optionValue}
                                      <input
                                        value={band.qualitativeOptionId ?? band.qualitativeValue ?? ""}
                                        onChange={(event) => updateBandDraft(criterion.id, band.id, {
                                          qualitativeOptionId: event.target.value,
                                          qualitativeValue: event.target.value
                                        })}
                                      />
                                    </label>
                                    <label className="criteria-meta-box editor-field-card">
                                      {copy.optionLabel}
                                      <input
                                        value={band.qualitativeLabelDe ?? ""}
                                        onChange={(event) => updateBandDraft(criterion.id, band.id, { qualitativeLabelDe: event.target.value })}
                                      />
                                    </label>
                                    <label className="criteria-meta-box editor-field-card">
                                      {copy.optionLabelEn}
                                      <input
                                        value={band.qualitativeLabelEn ?? ""}
                                        onChange={(event) => updateBandDraft(criterion.id, band.id, { qualitativeLabelEn: event.target.value })}
                                      />
                                    </label>
                                    <label className="criteria-meta-box editor-field-card">
                                      {copy.rank}
                                      <input type="number" min="1" step="1" value={band.rank} onChange={(event) => updateBandDraft(criterion.id, band.id, { rank: Number(event.target.value) })} />
                                    </label>
                                    <label className="criteria-meta-box editor-field-card">
                                      {copy.points}
                                      <input type="number" min="1" max="10" step="1" value={band.points ?? ""} onChange={(event) => updateBandDraft(criterion.id, band.id, { points: parseOptionalBandNumber(event.target.value) })} />
                                    </label>
                                  </div>
                                ))}
                              </div>
                              <button className="button button-secondary" type="button" disabled={isWorking} onClick={() => addQualitativeOption(criterion)}>
                                {copy.addOption}
                              </button>
                            </>
                          ) : null}
                          {criterionUsesBooleanBands(criterion) ? (
                            <div className="criteria-meta-grid">
                              {bandsForCriterion(criterion).map((band) => (
                                <label className="criteria-meta-box editor-field-card" key={band.id}>
                                  {band.booleanValue ? copy.booleanTrue : copy.booleanFalse}
                                  <input
                                    type="number"
                                    min="1"
                                    max="10"
                                    step="1"
                                    value={band.points ?? ""}
                                    onChange={(event) => updateBandDraft(criterion.id, band.id, { points: parseOptionalBandNumber(event.target.value) })}
                                  />
                                </label>
                              ))}
                            </div>
                          ) : null}
                        </div>
                        </>
                        ) : null}
                      </article>
                    ))}
                  </div>

                  <div id="customer-value-preview-section" />
                  <h3>{copy.previewTitle}</h3>
                  <div className="calculation-criterion">
                    <div className="calculation-criterion__top">
                      <strong>{copy.crmTransparencyTitle}</strong>
                      <FieldHelp content={helpContent("crmTransparency")} label={copy.crmTransparencyTitle} />
                    </div>
                    <p className="form-note">{copy.crmTransparencyBody}</p>
                    <div className="data-list">
                      {CUSTOMER_VALUE_CRM_TRANSPARENCY_FIELDS.map((field) => (
                        <div key={field.key}>
                          <span>{field.label[language]}</span>
                          <strong>{field.purpose[language]}</strong>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="criteria-meta-grid">
                    {editableScoringCriteria.map((criterion) => {
                      const previewKind = criterionPreviewInputKind(criterion);
                      const qualitativeOptions = bandsForCriterion(criterion).filter((band) => band.active && (band.qualitativeOptionId ?? band.qualitativeValue));
                      return (
                        <label className="criteria-meta-box editor-field-card" key={`${criterion.id}-preview`}>
                          {criterion.name} · {previewInputLabel(criterion)}
                          {previewKind === "boolean" ? (
                            <select
                              value={previewValues[criterion.id] ?? ""}
                              onChange={(event) => setPreviewValues((current) => ({ ...current, [criterion.id]: event.target.value }))}
                            >
                              <option value="">{copy.notStored}</option>
                              <option value="true">{copy.booleanTrue}</option>
                              <option value="false">{copy.booleanFalse}</option>
                            </select>
                          ) : previewKind === "choice" ? (
                            <select
                              value={previewValues[criterion.id] ?? ""}
                              onChange={(event) => setPreviewValues((current) => ({ ...current, [criterion.id]: event.target.value }))}
                            >
                              <option value="">{copy.notStored}</option>
                              {qualitativeOptions.map((band) => {
                                const value = band.qualitativeOptionId ?? band.qualitativeValue ?? "";
                                return <option key={band.id} value={value}>{bandPreview(criterion, band)}</option>;
                              })}
                            </select>
                          ) : previewKind === "number" ? (
                            <input
                              type="number"
                              min={criterion.criterionType === "legacy" ? 1 : undefined}
                              max={criterion.criterionType === "legacy" ? 10 : undefined}
                              step={criterion.criterionType === "legacy" || criterion.valueType === "integer" ? "1" : "0.01"}
                              value={previewValues[criterion.id] ?? ""}
                              onChange={(event) => setPreviewValues((current) => ({ ...current, [criterion.id]: event.target.value }))}
                            />
                          ) : (
                            <input disabled value="" placeholder={copy.previewConfigureFirst} />
                          )}
                        </label>
                      );
                    })}
                  </div>
                  {configurationPreview ? (
                    <>
	                      <div className="data-list">
	                        <div>
	                          <span>{copy.previewScore}</span>
	                          <strong>{configurationPreview.score ?? copy.notStored}</strong>
                        </div>
                        <div>
                          <span>{copy.previewSegment}</span>
                          <strong>{configurationPreview.segment?.name ?? copy.notStored}</strong>
                        </div>
                        <div>
	                          <span>{copy.previewMissing}</span>
	                          <strong>{configurationPreview.missingValues.length}</strong>
	                        </div>
	                        {missingValueNames.length > 0 ? (
	                          <div>
	                            <span>{copy.previewAffectedCriteria}</span>
	                            <strong>{missingValueNames.join(", ")}</strong>
	                          </div>
	                        ) : null}
	                        <div>
	                          <span>{copy.previewMissingScoringLogic}</span>
	                          <strong>{configurationPreview.missingScoringLogic?.length ?? 0}</strong>
	                        </div>
	                        {missingScoringLogicNames.length > 0 ? (
	                          <div>
	                            <span>{copy.previewAffectedCriteria}</span>
	                            <strong>{missingScoringLogicNames.join(", ")}</strong>
	                          </div>
	                        ) : null}
	                        {(configurationPreview.notComputableCriteria?.length ?? 0) > 0 ? (
	                          <div>
	                            <span>{copy.previewNotComputable}</span>
	                            <strong>{configurationPreview.notComputableCriteria?.length ?? 0}</strong>
	                          </div>
	                        ) : null}
	                        {notComputableNames.length > 0 ? (
	                          <div>
	                            <span>{copy.previewAffectedCriteria}</span>
	                            <strong>{notComputableNames.join(", ")}</strong>
	                          </div>
	                        ) : null}
	                        <div>
	                          <span>{copy.previewErrors}</span>
	                          <strong>{configurationPreview.errors.length ? configurationPreview.errors.map(copy.previewIssueLabel).join(" ") : copy.confirmed}</strong>
	                        </div>
	                      </div>
	                      <p className="form-note">{copy.previewCounterHelp}</p>
                      {configurationPreview.missingValues.length === 0 && (configurationPreview.missingScoringLogic?.length ?? 0) > 0 ? (
                        <p className="form-note">{copy.previewAllInputsPresentConfigurationMissing}</p>
                      ) : null}
                    </>
                  ) : null}

                  <div id="customer-value-confirm-section" />
                  <div className="form-actions editor-actions">
                    {hasUnsavedScoringChanges ? (
                      <button className="button button-secondary" type="button" disabled={!scoringDraftSaveAvailable} onClick={() => void saveScoringConfigurationDraft()}>
                        {uxCopy.saveChanges}
                      </button>
                    ) : (
                      <span className="form-note">{uxCopy.noUnsavedChanges}</span>
                    )}
                    <button className="button button-secondary" type="button" disabled={isWorking} onClick={() => void runScoringPreview()}>
                      {uxCopy.testModel}
                    </button>
                    {!hasConfirmedModel ? (
                      <button
                        className="button button-primary"
                        type="button"
                        disabled={isWorking || !configurationConfirmationAvailable}
                        title={configurationConfirmationAvailable ? uxCopy.activateModel : copy.confirmationBlockedTitle}
                        onClick={() => void confirmScoringConfiguration()}
                      >
                        {configurationConfirmationAvailable ? uxCopy.activateModel : copy.confirmConfigurationDisabled}
                      </button>
                    ) : null}
                  </div>
                  {hasConfirmedModel ? (
                    <p className="form-note">{uxCopy.confirmAlreadyActive}</p>
                  ) : !configurationConfirmationAvailable ? (
                    <div className="calculation-criterion">
                      <strong>{copy.confirmationBlockedTitle}</strong>
                      <ul>
                        {(confirmationBlockerReasons.length > 0 ? confirmationBlockerReasons : [copy.confirmationUnavailableReason]).map((reason) => (
                          <li key={reason}>{reason}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {historicalCriteria.length > 0 ? (
                    <details className="advanced-organization">
                      <summary>{uxCopy.historicalCriteriaTitle(historicalCriteria.length)}</summary>
                      <p className="form-note">{uxCopy.historicalCriteriaBody}</p>
                      <div className="calculation-criteria-list">
                        {historicalCriteria.map((criterion) => (
                          <article className="calculation-criterion" key={criterion.id}>
                            <div className="calculation-criterion__top">
                              <div>
                                <span>{uxCopy.historicalState}</span>
                                <strong>{criterion.name}</strong>
                              </div>
                              <StatusBadge tone="default">{uxCopy.activeStatusHistorical}</StatusBadge>
                            </div>
                            <p className="form-note">{criterion.description}</p>
                            <div className="data-list">
                              <div><span>{copy.weighting}</span><strong>{criterion.weight}%</strong></div>
                              <div><span>{copy.evaluationType}</span><strong>{localizedCriterionTypeLabel(criterion.criterionType)}</strong></div>
                              <div><span>{copy.status}</span><strong>{criterion.scoringConfigurationStatus === "superseded" ? uxCopy.activeStatusHistorical : uxCopy.historicalState}</strong></div>
                            </div>
                            <div className="action-row">
                              <button className="button button-secondary" type="button" disabled={isWorking} onClick={() => setCriterionActiveDraft(criterion.id, true)}>{copy.reactivateCriterion}</button>
                            </div>
                          </article>
                        ))}
                      </div>
                    </details>
                  ) : null}
                </>
              ) : null}
            </Panel>
          ) : null}

          <div id="customer-value-segmentation-section" />
          {showConfigurationEditor && segmentDefinition ? (
            <Panel title={copy.segmentDefinitionTitle} variant="accent">
              <p className="form-note">{copy.segmentDefinitionBody}</p>
              <div className="data-list">
                <div>
                  <span>{copy.status}</span>
                  <strong>{segmentDefinition.status}</strong>
                </div>
                <div>
                  <span>{copy.segmentVersion}</span>
                  <strong>{segmentDefinition.segmentVersion}</strong>
                </div>
              </div>
              {!segmentDefinition.validation.ok ? (
                <p className="form-error">
                  {copy.segmentInvalid} {segmentDefinition.validation.errors.join(", ")}
                </p>
              ) : null}
              <div className="calculation-criteria-list">
                {segmentDraftForDisplay.map((segment, index) => (
                  <article className="calculation-criterion" key={segment.id}>
                    <div className="calculation-criterion__top">
                      <strong>{segment.name || copy.segmentName}</strong>
                      <StatusBadge tone={index === 0 ? "success" : index === segmentDraftForDisplay.length - 1 ? "warning" : "default"}>
                        {copy.segmentPosition(index + 1, segmentDraftForDisplay.length)}
                      </StatusBadge>
                    </div>
                    <p className="form-note">{copy.segmentAssignmentExample(segment.name || copy.segmentName, segment.minScore, segment.maxScore)}</p>
                    <div className="criteria-meta-grid">
                      <label className="criteria-meta-box editor-field-card">
                        {copy.segmentName}
                        <input
                          disabled={!segmentDefinition.editable}
                          value={segment.name}
                          onChange={(event) => updateSegmentDraft(segment.id, { name: event.target.value })}
                        />
                      </label>
                      <label className="criteria-meta-box editor-field-card">
                        {copy.segmentDescriptionField}
                        <textarea
                          disabled={!segmentDefinition.editable}
                          value={segment.description}
                          onChange={(event) => updateSegmentDraft(segment.id, { description: event.target.value })}
                        />
                      </label>
                      <label className="criteria-meta-box editor-field-card">
                        {copy.segmentMinScore}
                        <input
                          disabled={!segmentDefinition.editable}
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={segment.minScore}
                          onChange={(event) => updateSegmentDraft(segment.id, { minScore: Number(event.target.value) })}
                        />
                      </label>
                      <label className="criteria-meta-box editor-field-card">
                        {copy.segmentMaxScore}
                        <input
                          disabled={!segmentDefinition.editable}
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={segment.maxScore}
                          onChange={(event) => updateSegmentDraft(segment.id, { maxScore: Number(event.target.value) })}
                        />
                      </label>
                    </div>
                  </article>
                ))}
              </div>
              <div className="form-actions editor-actions">
                {!segmentDefinition.editable ? (
                  <button className="button button-secondary" type="button" disabled={isWorking} onClick={() => void prepareSegmentDraft()}>
                    {copy.createSegmentDraft}
                  </button>
                ) : (
                  <>
                    <button className="button button-secondary" type="button" disabled={isWorking} onClick={() => void saveSegmentDraft()}>
                      {copy.saveSegmentDraft}
                    </button>
                    <button className="button button-primary" type="button" disabled={isWorking || !segmentDefinition.validation.ok} onClick={() => void confirmSegmentDraft()}>
                      {copy.confirmSegmentDraft}
                    </button>
                  </>
                )}
              </div>
            </Panel>
          ) : null}

          {displayCalculation?.scoreTemplates?.length && !translationIncomplete ? (
            <Panel title={copy.scoreLogic} variant="accent">
              <p className="form-note">{copy.scoreLogicBody}</p>
              <div className="score-template-list">
                {displayCalculation.scoreTemplates.map((template) => (
                  <article className="score-template-card" key={template.id}>
                    <div className="score-template-card__top">
                      <StatusBadge tone="default">{template.minScore}-{template.maxScore}</StatusBadge>
                      <strong>{template.segmentName}</strong>
                    </div>
                    <p>{template.recommendation}</p>
                  </article>
                ))}
              </div>
            </Panel>
          ) : null}
        </>
      ) : null}
      </div>
    </Layout>
  );
}
